        const { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } = React;

        // WebSocket URL - bruker samme host som siden
        const WS_URL = window.location.origin;

        // Annonser til skjermlesere
        function announce(message) {
            const el = document.getElementById('sr-announcements');
            if (el) {
                el.textContent = message;
                setTimeout(() => { el.textContent = ''; }, 1000);
            }
        }

        // Kryssfading ved bytte av bilde-src: det nye bildet lastes usynlig
        // i samme grid-celle og tones inn over det gamle først når det er
        // ferdig lastet — gammelt bilde står til det nye er klart, ingen
        // hard swap. Forelderen MÅ være `grid place-items-center` (lagene
        // stables med gridArea 1/1 så object-contain-geometrien og rammen
        // som klemmer bildet bevares).
        function KryssfadeBilde({ src, varighet = 250, className, ...rest }) {
            const [lag, setLag] = useState({ naa: src, forrige: null, inne: true });
            useEffect(() => {
                setLag(l => (src === l.naa ? l : { naa: src, forrige: l.naa, inne: false }));
            }, [src]);
            // Rydd bort underlaget når innfadingen er ferdig
            useEffect(() => {
                if (!lag.inne || !lag.forrige) return;
                const t = setTimeout(() => setLag(l => ({ ...l, forrige: null })), varighet + 80);
                return () => clearTimeout(t);
            }, [lag, varighet]);
            // Dobbel rAF: opacity 0 må rekke å bli malt før transition starter
            const tonInn = () => requestAnimationFrame(() => requestAnimationFrame(
                () => setLag(l => ({ ...l, inne: true }))));
            return (
                <>
                    {lag.forrige && (
                        <img src={lag.forrige} alt="" aria-hidden="true"
                             className={`${className} pointer-events-none`}
                             style={{ gridArea: '1 / 1' }} />
                    )}
                    <img {...rest} src={lag.naa}
                         onLoad={lag.inne ? undefined : tonInn}
                         onError={lag.inne ? undefined : tonInn}
                         className={className}
                         style={{ gridArea: '1 / 1', opacity: lag.inne ? 1 : 0, transition: `opacity ${varighet}ms ease` }} />
                </>
            );
        }

        function App() {
            const [socket, setSocket] = useState(null);

            // Skjermjustering: størrelse (zoom) og plassering (dx/dy i vmin) av
            // sangteksten på storskjermen. Sendes som set_style; serveren husker
            // den per rom så displays som kobler til senere får samme stil, og
            // valget huskes lokalt mellom økter. Styres med knapper i panelet —
            // ikke tastatur: piltastene er opptatt av navigasjonen.
            const SKJERM_STIL_STANDARD = { zoom: 1, dx: 0, dy: 0 };
            const [skjermStil, setSkjermStil] = useState(() => {
                try {
                    const l = JSON.parse(localStorage.getItem('visSkjermStil') || 'null');
                    if (l && typeof l === 'object') return { ...SKJERM_STIL_STANDARD, ...l };
                } catch (e) {}
                return SKJERM_STIL_STANDARD;
            });
            const skjermStilRef = useRef(skjermStil);
            const [showSkjermStil, setShowSkjermStil] = useState(false);
            const skjermStilAvviker = skjermStil.zoom !== 1 || skjermStil.dx !== 0 || skjermStil.dy !== 0;
            const sendSkjermStil = (stil) => {
                skjermStilRef.current = stil;
                setSkjermStil(stil);
                try { localStorage.setItem('visSkjermStil', JSON.stringify(stil)); } catch (e) {}
                socket?.emit('set_style', stil);
            };
            const justerSkjermStil = (endring) => {
                const n = { ...skjermStilRef.current };
                if (endring.zoom) n.zoom = Math.min(2.5, Math.max(0.4, Math.round((n.zoom + endring.zoom) * 20) / 20));
                if (endring.dx) n.dx = Math.min(40, Math.max(-40, n.dx + endring.dx));
                if (endring.dy) n.dy = Math.min(40, Math.max(-40, n.dy + endring.dy));
                sendSkjermStil(n);
            };
            // Lukk justeringsboksen med Escape
            useEffect(() => {
                if (!showSkjermStil) return;
                const paaTast = (e) => { if (e.key === 'Escape') setShowSkjermStil(false); };
                window.addEventListener('keydown', paaTast);
                return () => window.removeEventListener('keydown', paaTast);
            }, [showSkjermStil]);
            const [connected, setConnected] = useState(false);
            const [displaysConnected, setDisplaysConnected] = useState(0);   // alle display-klienter (skjermer + følgere)
            const [folgereConnected, setFolgereConnected] = useState(0);     // de som kom via følgekoden
            const displaysConnectedRef = useRef(0);
            const [roomCode, setRoomCode] = useState(null);
            const [connectionError, setConnectionError] = useState(null);
            const [showJoinRoomDialog, setShowJoinRoomDialog] = useState(false);
            const [joinRoomCode, setJoinRoomCode] = useState('');
            const [showControllerQR, setShowControllerQR] = useState(false);
            const [songs, setSongs] = useState([]);
            const [loading, setLoading] = useState(true);
            const [searchQuery, setSearchQuery] = useState('');
            const [activeTab, setActiveTab] = useState('nummer');
            const [sortAsc, setSortAsc] = useState(true);
            const [selectedSong, setSelectedSong] = useState(null);
            const [currentVerse, setCurrentVerse] = useState(0);
            const [verses, setVerses] = useState([]);
            const [editMode, setEditMode] = useState(false);
            const [editSong, setEditSong] = useState(null);
            const [showGotoDialog, setShowGotoDialog] = useState(false);
            const [gotoNumber, setGotoNumber] = useState('');
            const [isLoggedIn, setIsLoggedIn] = useState(false);
            const [showLoginDialog, setShowLoginDialog] = useState(false);
            const [loginPassword, setLoginPassword] = useState('');
            const [loginError, setLoginError] = useState('');
            const [loginLoading, setLoginLoading] = useState(false);
            const [showCodeInput, setShowCodeInput] = useState(false);
            const [resetCode, setResetCode] = useState('');
            const [codeSent, setCodeSent] = useState(false);
            // Konto-innlogging (sangleder-kontoen) i login-dialogen: primærvalg
            // for «Ny sang» — delt redigeringspassord ligger bak en lenke.
            const [loginMode, setLoginMode] = useState('konto');
            const [kontoBruker, setKontoBruker] = useState('');
            const [kontoPassord, setKontoPassord] = useState('');
            const [kontoSteg, setKontoSteg] = useState('logg');        // 'logg' | 'glemt' | 'nytt' (glemt passord, konto)
            const [glemtKode, setGlemtKode] = useState('');
            const [glemtNyttPassord, setGlemtNyttPassord] = useState('');
            const [glemtInfo, setGlemtInfo] = useState('');
            // Nullstill glemt-passord-stegene hver gang dialogen lukkes
            useEffect(() => {
                if (!showLoginDialog) { setKontoSteg('logg'); setGlemtKode(''); setGlemtNyttPassord(''); setGlemtInfo(''); }
            }, [showLoginDialog]);
            const [showHelp, setShowHelp] = useState(false);
            const [qrVisible, setQrVisible] = useState(false);
            // Admin-panel (⚙): kanalrom (superadmin) + brukere/roller (admin+).
            // Flyttet hit fra sangbok-editoren — administrasjon har én dør inn.
            const [showAdminDialog, setShowAdminDialog] = useState(false);
            const [showVerktoyMeny, setShowVerktoyMeny] = useState(false);
            const [adminTab, setAdminTab] = useState('kanalrom');
            const [adminRolle, setAdminRolle] = useState('');
            const [innloggetBruker, setInnloggetBruker] = useState(null);   // kontobruker (null = ikke innlogget)
            const [pendingLoggInn, setPendingLoggInn] = useState(false);    // innlogging startet fra ⋮-menyen (ingen handling etterpå)
            const [pendingAdmin, setPendingAdmin] = useState(false);
            const [adminKanalrom, setAdminKanalrom] = useState([]);
            const [adminBrukere, setAdminBrukere] = useState([]);
            const [adminStatus, setAdminStatus] = useState('');
            const [nyttHus, setNyttHus] = useState('');
            const [kopiertUrlId, setKopiertUrlId] = useState(null);
            const [nyBrukernavn, setNyBrukernavn] = useState('');
            const [nyEpost, setNyEpost] = useState('');
            const [nyBrukerRolle, setNyBrukerRolle] = useState('');
            // Kanal-klikk som strandet på innlogging: slås på igjen automatisk
            // når socket-økta har fått innloggingen (billett_status).
            const [pendingKanal, setPendingKanal] = useState(null);

            // Sangplan import state
            const [showImportDialog, setShowImportDialog] = useState(false);
            const [importCode, setImportCode] = useState('');
            const [importedPlan, setImportedPlan] = useState(null);
            const [importLoading, setImportLoading] = useState(false);
            const [importError, setImportError] = useState('');
            const [romPlaner, setRomPlaner] = useState([]); // planer delt til fast rom (innboks)
            // FØLG (alltid på) + utgangskanal (toggle) — to uavhengige utganger.
            const [husAlias, setHusAlias] = useState(null);          // AKTIVT hus (møtekode + default følgekode)
            const [alleHus, setAlleHus] = useState([]);              // alle hus — velger vises ved flere enn ett
            const [husKanalKart, setHusKanalKart] = useState({});    // {hus: [kanaler]} fra /sangplan/moterom
            const [romOpprettet, setRomOpprettet] = useState(false); // rommet ble skapt av oss (ikke alias-join)
            const [folgKode, setFolgKode] = useState(null);          // aktiv følgekode (følgere bruker denne)
            const [folgInput, setFolgInput] = useState('');          // redigerbart følgerom-felt
            const [folgFeil, setFolgFeil] = useState('');            // feil ved setting av følgekode
            // Utgangskanal: 'websocket' (vMix browser-input). NDI/OBS-kanalen
            // ble avviklet 2026-08-08; maskineriet tar fortsatt flere kanaler.
            // Uavhengige toggles mot husets faste kanalrom — koder settes av superadmin.
            const KANAL_NAVN = { websocket: 'vMix' };
            const [kanaler, setKanaler] = useState({});              // {kanal: {paa, aktiv}}
            const [husKanaler, setHusKanaler] = useState(['websocket']);   // kanaler huset har konfigurert
            const [kanalParing, setKanalParing] = useState(null);    // kanal som venter paringskode
            const [kanalParingInput, setKanalParingInput] = useState('');
            const autoInitRef = useRef(false);                       // har vi kjørt auto-oppsett (første gang)
            const autoInitRoomRef = useRef(null);                    // rommet auto-oppsettet sist kjørte for
            const folgKodeRef = useRef(null);                        // gjeldende følgekode (for re-registrering)
            const kanalPaaRef = useRef({});                          // {kanal: paa} for re-registrering
            const kanalAutoRef = useRef(false);                      // siste kanal-forsøk kom fra auto-oppsettet
            const oktKanalerRef = useRef(null);                      // kanal-tilstand lagret i økta (lest før overskriving)
            const pendingKanalRef = useRef(null);                    // speiler pendingKanal for socket-handlere
            const husAliasRef = useRef(null);                        // speiler husAlias for socket-handlere
            const husKanalerRef = useRef([]);                        // speiler husKanaler for socket-handlere
            if (oktKanalerRef.current === null) {
                let lagret = null;
                try { lagret = JSON.parse(sessionStorage.getItem('visOktKanaler') || 'null'); } catch (e) {}
                oktKanalerRef.current = (lagret && typeof lagret === 'object') ? lagret
                    : {};   // eldre økter uten kanalvalg starter blankt
            }
            const SANGPLAN_API = ''; // samme origin — nginx proxyer /sangplan/

            // Mode: 'songs' or 'bible' — aktiv fane huskes over refresh
            const [appMode, setAppMode] = useState(
                () => localStorage.getItem('visAppMode') || 'songs');
            useEffect(() => {
                try { localStorage.setItem('visAppMode', appMode); } catch (e) {}
            }, [appMode]);

            // Bible state — ingen gjenopptak: fanen starter alltid med blanke ark
            const [bibleVersion, setBibleVersion] = useState('no');
            const [bibleData, setBibleData] = useState(null);
            // Parallellvisning: begge språk under hverandre på VEGGEN. Kanalen
            // (?key=1) beholder ett språk — skjerm.js viser andrespråket kun i
            // kun-stor-modus. Valget huskes mellom økter.
            const [parallell, setParallell] = useState(() => {
                try { return localStorage.getItem('visParallellBibel') === '1'; } catch (e) { return false; }
            });
            // { lang, data } — språket MÅ følge med: primær og andrespråk lastes
            // i hver sin fetch, og lander de i «feil» rekkefølge ved
            // oversettelsesbytte, ville samme språk stått i begge blokkene.
            const [altBibleData, setAltBibleData] = useState(null);
            const [bibleLoading, setBibleLoading] = useState(false);
            const [selectedBook, setSelectedBook] = useState(null);
            const [selectedChapter, setSelectedChapter] = useState(null);
            const [bibleVerses, setBibleVerses] = useState([]);
            const [currentBibleVerse, setCurrentBibleVerse] = useState(0);
            const [bibleNavLevel, setBibleNavLevel] = useState(0);
            const bibleNavLevelRef = useRef(0);
            const bibleNumberInputRef = useRef('');
            const bibleNumberTimeoutRef = useRef(null);
            const [bibleSearchQuery, setBibleSearchQuery] = useState('');
            const bibleSearchInputRef = useRef(null);
            const lastFocusedBookRef = useRef(null);

            // Presentasjon (PowerPoint) state
            const [presListe, setPresListe] = useState([]);
            const [presSoker, setPresSoker] = useState(false);
            const [presFeil, setPresFeil] = useState('');
            const [valgtPres, setValgtPres] = useState(null);
            const [presKonverterer, setPresKonverterer] = useState(null);
            const [presSlide, setPresSlide] = useState(1);
            const [presVist, setPresVist] = useState(false); // grønn modus: blaingen styrer skjermen
            const [presLiveSlide, setPresLiveSlide] = useState(null); // lysbildet skjermen faktisk viser
            // Presenter-fjernkontroll (/presenter): egen 6-sifret kode per økt
            const [presenterKode, setPresenterKode] = useState(null);
            const [presenterEpost, setPresenterEpost] = useState(null); // {ok, tekst}
            const [presStatus, setPresStatus] = useState(null); // pres_status fra serveren (talerens kontekst)
            const [presenterAntall, setPresenterAntall] = useState(0); // tilkoblede fjernkontroller

            // Bibelmanifest fra serveren. Fallbacken er de to innebygde
            // versjonene, så bibelfanen virker selv om API-et er nede.
            // «parallellPar» styrer hvilke versjoner som kan stå side om side:
            // det krever at begge filene deler KJV-nummerering (tysk er f.eks.
            // nativt nummerert og kan derfor ikke pares med noen ennå).
            const [bibler, setBibler] = useState([
                { id: 'no', navn: 'Norsk 1930', kort: '1930', url: '/bibel1930.json' },
                { id: 'en', navn: 'King James (KJV)', kort: 'KJV', url: '/kjv.json' },
            ]);
            const [parallellPar, setParallellPar] = useState([['no', 'en']]);
            useEffect(() => {
                fetch('/api/bibler')
                    .then(res => res.json())
                    .then(data => {
                        if (data.success && data.bibler?.length) {
                            setBibler(data.bibler);
                            setParallellPar(data.parallell || []);
                        }
                    })
                    .catch(() => {});
            }, []);
            const bibelUrl = (id) => (bibler.find(b => b.id === id) || bibler[0]).url;

            // Per-hus config: hvilke bibelversjoner huset har aktivert (null =
            // alle). Leses åpent; endring krever husets config-passord (⚙).
            const [husBibler, setHusBibler] = useState(null);
            useEffect(() => {
                if (!husAlias) return;
                fetch(`/api/hus/${husAlias}/config`)
                    .then(res => res.json())
                    .then(data => { if (data.success) setHusBibler(data.bibler); })
                    .catch(() => {});
            }, [husAlias]);
            const aktiveBibler = bibler.filter(b => !husBibler || husBibler.includes(b.id));
            // Aktiv versjon kan bli slått av i config eller ved husbytte —
            // hopp da til husets første aktive versjon.
            useEffect(() => {
                if (aktiveBibler.length && !aktiveBibler.some(b => b.id === bibleVersion)) {
                    setBibleVersion(aktiveBibler[0].id);
                }
            }, [husBibler, bibler]);

            // Versjonen aktiv bibel kan pares med i parallellvisning (null =
            // ingen). Partneren må selv være aktiv i huset.
            const partner = (() => {
                const par = parallellPar.find(p => p.includes(bibleVersion));
                const kandidat = par ? par.find(v => v !== bibleVersion) : null;
                return kandidat && aktiveBibler.some(b => b.id === kandidat) ? kandidat : null;
            })();

            // Hus-innstillinger (⚙): dialog med husets config-innlogging.
            // Innloggingen er ATSKILT fra sangleder-kontoene — husets eget
            // config-passord (satt av superadmin) låser opp lagring.
            const [showHusConfig, setShowHusConfig] = useState(false);
            const [husCfgInnlogget, setHusCfgInnlogget] = useState(false);
            const [husCfgPassord, setHusCfgPassord] = useState('');
            const [husCfgValg, setHusCfgValg] = useState([]);
            const [husCfgFeil, setHusCfgFeil] = useState('');
            const [husCfgLagrer, setHusCfgLagrer] = useState(false);
            const aapneHusConfig = async () => {
                setHusCfgFeil('');
                setHusCfgPassord('');
                try {
                    const res = await fetch(`/api/hus/${husAlias}/config`, { credentials: 'include' });
                    const data = await res.json();
                    if (data.success) {
                        setHusCfgInnlogget(data.innlogget);
                        setHusCfgValg(data.bibler || bibler.map(b => b.id));
                    }
                } catch (e) {}
                setShowHusConfig(true);
            };
            const husCfgLoggInn = async () => {
                setHusCfgFeil('');
                try {
                    const res = await fetch(`/api/hus/${husAlias}/config/login`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ passord: husCfgPassord })
                    });
                    const data = await res.json();
                    if (data.success) {
                        setHusCfgInnlogget(true);
                        setHusCfgPassord('');
                    } else {
                        setHusCfgFeil(data.error || 'Innlogging feilet');
                    }
                } catch (e) {
                    setHusCfgFeil('Nettverksfeil');
                }
            };
            const husCfgLagre = async () => {
                if (!husCfgValg.length) { setHusCfgFeil('Velg minst én bibel'); return; }
                setHusCfgLagrer(true);
                setHusCfgFeil('');
                try {
                    const res = await fetch(`/api/hus/${husAlias}/config`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ bibler: husCfgValg })
                    });
                    const data = await res.json();
                    if (data.success) {
                        setHusBibler(data.bibler);
                        setShowHusConfig(false);
                    } else {
                        setHusCfgFeil(data.error || 'Lagring feilet');
                    }
                } catch (e) {
                    setHusCfgFeil('Nettverksfeil');
                }
                setHusCfgLagrer(false);
            };

            // Check if already authenticated on load
            useEffect(() => {
                fetch('/api/auth/check', { credentials: 'include' })
                    .then(res => res.json())
                    .then(data => setIsLoggedIn(data.authenticated))
                    .catch(() => {});
            }, []);

            // Motsatt oversettelse lastes kun når parallellvisning er på — 4,5 MB
            // skal ikke hentes for de som viser ett språk.
            const altLoadedRef = useRef(null);
            useEffect(() => {
                try { localStorage.setItem('visParallellBibel', parallell ? '1' : '0'); } catch (e) {}
                if (!parallell || appMode !== 'bible') return;
                const altVersjon = partner;
                if (!altVersjon || altLoadedRef.current === altVersjon) return;
                altLoadedRef.current = altVersjon;
                fetch(bibelUrl(altVersjon))
                    .then(res => res.json())
                    .then(data => setAltBibleData({ lang: altVersjon, data }))
                    .catch(() => { altLoadedRef.current = null; });
            }, [parallell, appMode, bibleVersion, partner]);

            // Load Bible data when entering Bible mode the first time, and reload
            // (with selection reset) when the version changes afterwards.
            const loadedVersionRef = useRef(null);
            useEffect(() => {
                if (appMode !== 'bible' || loadedVersionRef.current === bibleVersion) return;
                const isVersionChange = loadedVersionRef.current !== null;
                loadedVersionRef.current = bibleVersion;
                setBibleLoading(true);
                const url = bibelUrl(bibleVersion);
                fetch(url)
                    .then(res => res.json())
                    .then(data => {
                        setBibleData(data);
                        setBibleLoading(false);
                        if (isVersionChange) {
                            // Behold posisjonen ved versjonsbytte: bokkodene
                            // ('01O' osv.) er identiske i begge filene, så samme
                            // bok/kapittel/vers slås opp i den nye teksten.
                            // (Versnummerering kan avvike marginalt enkelte
                            // steder — indeksen klemmes til kapittelets lengde.)
                            const bok = selectedBookRef.current;
                            const kap = selectedChapterRef.current;
                            const verseData = bok && kap && data.bible[bok]
                                ? data.bible[bok][kap] : null;
                            if (verseData) {
                                const verseArray = Object.keys(verseData).map(v => ({
                                    num: parseInt(v),
                                    text: verseData[v]
                                })).sort((a, b) => a.num - b.num);
                                setBibleVerses(verseArray);
                                setCurrentBibleVerse(prev =>
                                    Math.min(prev, verseArray.length - 1));
                                // Står verset aktivt på skjermen (grønt nivå):
                                // re-send det i den nye oversettelsen, så
                                // display/kanal bytter språk sammen med panelet.
                                if (bibleNavLevelRef.current === 2 && socketRef.current) {
                                    const idx = Math.min(currentBibleVerseRef.current,
                                        verseArray.length - 1);
                                    const nyLast = byggBibelLastRef.current(
                                        bok, data.names[bok], kap, verseArray[idx].num,
                                        cleanVerseText(verseArray[idx].text), data);
                                    socketRef.current.emit('show_bible', nyLast);
                                    sisteVisning.current.bible = nyLast;
                                }
                                // Gjenopprett tastaturkonteksten: versnavigasjon
                                // er tilstanden UTEN venstrepanel-fokus, så var
                                // brukeren i versene (gult/grønt) blurres
                                // versjonsknappen; ellers tilbake til kapittelet.
                                setTimeout(() => {
                                    if (bibleNavLevelRef.current >= 1) {
                                        document.activeElement?.blur();
                                    } else {
                                        const btn = document.querySelector(
                                            `[data-bible-chapter="${kap}"]`);
                                        if (btn) btn.focus();
                                    }
                                }, 100);
                            } else {
                                // Fantes ikke i den nye versjonen — fall tilbake
                                setSelectedBook(null);
                                setSelectedChapter(null);
                                setBibleVerses([]);
                            }
                        }
                    })
                    .catch(err => {
                        console.error('Failed to load Bible:', err);
                        setBibleLoading(false);
                        loadedVersionRef.current = null; // tillat nytt forsøk
                    });
            }, [appMode, bibleVersion]);

            // Keep bibleNavLevelRef in sync
            useEffect(() => {
                bibleNavLevelRef.current = bibleNavLevel;
            }, [bibleNavLevel]);

            // Refs for navigation
            const searchInputRef = useRef(null);
            const gotoInputRef = useRef(null);
            const [displayWindows, setDisplayWindows] = useState([]);

            // Normalisering og word-splitting for søk.
            //
            //   _normalize: lowercase + diakritisk folding (1-til-1 lengde-bevarende, så
            //   posisjoner i normalisert streng er gyldige også i original).
            //     "Skjønne" → "skjonne", "Lære" → "lare", "Café" → "cafe"
            //
            //   _splitWords: deler både tekst og søk på samme separatorer
            //     (whitespace + vanlige skilletegn + slash) så søk på
            //     "I'll fly" matcher "I'll Fly Away".
            const _normalize = (s) => (s || '').toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/ø/g, 'o').replace(/æ/g, 'a').replace(/å/g, 'a');
            const _splitWords = (s) => s.split(/[\s\n\r,.!?;:"()\-/]+/).filter(w => w.length > 0);

            // Find the byte position of a consecutive phrase-prefix match within a string.
            // qWords must be already normalized. Positions are valid in the ORIGINAL str
            // because _normalize is length-preserving.
            const findPhrasePosition = (str, qWords) => {
                if (!str || !qWords || qWords.length === 0) return null;
                const norm = _normalize(str);
                const wordRe = /[^\s\n\r,.!?;:"()\-/]+/g;
                const wordPositions = [];
                let m;
                while ((m = wordRe.exec(norm)) !== null) {
                    wordPositions.push({ start: m.index, end: m.index + m[0].length, word: m[0] });
                }
                if (wordPositions.length < qWords.length) return null;
                for (let i = 0; i <= wordPositions.length - qWords.length; i++) {
                    let ok = true;
                    for (let j = 0; j < qWords.length; j++) {
                        if (!wordPositions[i + j].word.startsWith(qWords[j])) { ok = false; break; }
                    }
                    if (ok) {
                        return {
                            phraseStart: wordPositions[i].start,
                            phraseEnd: wordPositions[i + qWords.length - 1].end,
                        };
                    }
                }
                return null;
            };

            // Get search context - finds the consecutive phrase-prefix match and shows
            // surrounding context with the entire matched phrase highlighted.
            const getSearchContext = (text, query) => {
                if (!text || !query || query.length < 2) return null;

                const qWords = _splitWords(_normalize(query));
                if (qWords.length === 0) return null;

                const verses = parseVerses(text);
                const findPhrase = (str) => findPhrasePosition(str, qWords);

                let verseLabel = '';
                let verseText = '';
                let phrasePos = null;
                for (let i = 0; i < verses.length; i++) {
                    const pos = findPhrase(verses[i].text);
                    if (pos) {
                        verseText = verses[i].text;
                        phrasePos = pos;
                        if (verses[i].isChorus) {
                            verseLabel = 'Kor';
                        } else {
                            const verseNum = verses.slice(0, i + 1).filter(v => !v.isChorus).length;
                            verseLabel = `Vers ${verseNum}`;
                        }
                        break;
                    }
                }

                if (!phrasePos) return null;

                // Normalise newlines to spaces. We need to map phrase offsets — newlines are 1:1
                // replaced with spaces so byte positions stay aligned.
                const normalizedText = verseText.replace(/\n/g, ' ');
                const phraseStart = phrasePos.phraseStart;
                const phraseEnd = phrasePos.phraseEnd;

                // ~40 chars context on each side of the phrase
                const contextRadius = 40;
                let start = Math.max(0, phraseStart - contextRadius);
                let end = Math.min(normalizedText.length, phraseEnd + contextRadius);

                // Snap to word boundaries (don't cut mid-word)
                if (start > 0) {
                    const spaceIndex = normalizedText.indexOf(' ', start);
                    if (spaceIndex !== -1 && spaceIndex < phraseStart) {
                        start = spaceIndex + 1;
                    }
                }
                if (end < normalizedText.length) {
                    const spaceIndex = normalizedText.lastIndexOf(' ', end);
                    if (spaceIndex > phraseEnd) {
                        end = spaceIndex;
                    }
                }

                const before = normalizedText.slice(start, phraseStart);
                const matched = normalizedText.slice(phraseStart, phraseEnd);
                const after = normalizedText.slice(phraseEnd, end);

                return {
                    verseLabel,
                    before: (start > 0 ? '...' : '') + before,
                    match: matched,
                    after: after + (end < normalizedText.length ? '...' : '')
                };
            };

            // Offentlige møtekoder (alias for faste rom med storskjerm).
            // Aktivt hus huskes per fane; ellers første hus (alfabetisk).
            useEffect(() => {
                fetch(`${SANGPLAN_API}/sangplan/moterom`)
                    .then(res => res.json())
                    .then(data => {
                        const rom = data.rom || [];
                        setAlleHus(rom);
                        setHusKanalKart(data.kanaler || {});
                        let lagret = null;
                        try { lagret = sessionStorage.getItem('visValgtHus'); } catch (e) {}
                        const def = (lagret && rom.includes(lagret)) ? lagret : (rom[0] || null);
                        setHusAlias(def);
                        setFolgInput(prev => prev || def || '');
                        // Hvilke utgangskanaler (vMix) huset har — styrer knappene
                        if (def && data.kanaler && data.kanaler[def]) setHusKanaler(data.kanaler[def]);
                    })
                    .catch(() => {});
            }, []);
            useEffect(() => { husAliasRef.current = husAlias; }, [husAlias]);
            useEffect(() => { husKanalerRef.current = husKanaler; }, [husKanaler]);

            // Bytt aktivt hus: slå av kanalene mot det gamle huset først
            // (broene peker dit), flytt følgekoden, og vis det nye husets knapper.
            const velgHus = (nytt) => {
                if (!nytt || nytt === husAlias) return;
                for (const [k, v] of Object.entries(kanaler)) {
                    if (v.paa && socket) socket.emit('kanal_toggle', { kanal: k, paa: false, hus: husAlias });
                }
                setKanaler({});
                setHusAlias(nytt);
                setHusKanaler(husKanalKart[nytt] || ['websocket']);
                try { sessionStorage.setItem('visValgtHus', nytt); } catch (e) {}
                setFolgInput(nytt);
                settFolgerom(nytt);
            };

            const settFolgerom = (kode) => {
                setFolgFeil('');
                const k = (kode || '').trim().toUpperCase();
                if (socket && k) socket.emit('sett_folgerom', { kode: k });
            };
            const toggleKanal = async (kanal, paa) => {
                kanalAutoRef.current = false; // manuelt valg — feil skal vises
                if (!paa && kanalParing === kanal) { setKanalParing(null); setKanalParingInput(''); }
                if (!socket) return;
                if (paa) {
                    // Socket-økta ser innloggingen slik den var ved tilkobling.
                    // Er fanen logget ut siden — også fra en annen fane eller app
                    // som deler sesjonen (sangleder, editor) — må serveren få vite
                    // det FØR den vurderer kanalen, ellers slår en utlogget fane
                    // på vMix med gammel rett. Begge innloggingsformer sjekkes
                    // live over HTTP (konto og delt passord).
                    const [konto, delt] = await Promise.all([
                        sjekkSangplanTilgang(),
                        fetch('/api/auth/check', { credentials: 'include' })
                            .then(r => r.json()).catch(() => ({})),
                    ]);
                    if (!konto.authenticated && !delt.authenticated) socket.emit('logg_ut');
                }
                socket.emit('kanal_toggle', { kanal, paa, hus: husAlias });
            };
            const sendKanalParing = () => {
                const k = kanalParingInput.trim();
                if (socket && k && kanalParing) socket.emit('kanal_paring', { kanal: kanalParing, kode: k });
            };

            // Speil gjeldende følgekode/kanal-tilstand i refs for re-registrering,
            // og husk kanalvalgene i økta (overlever refresh, jf. room_joined)
            useEffect(() => { folgKodeRef.current = folgKode; }, [folgKode]);
            useEffect(() => {
                if (!Object.keys(kanaler).length) return; // ikke overskriv lagret økt før første status
                const paa = {};
                for (const [k, v] of Object.entries(kanaler)) paa[k] = !!v.paa;
                kanalPaaRef.current = paa;
                try { sessionStorage.setItem('visOktKanaler', JSON.stringify(paa)); } catch (e) {}
            }, [kanaler]);

            // Auto-oppsett når rom er opprettet: FØLG alltid på (default OPSTAD)
            // og kanalene på som standard. Kjøres på nytt for hvert nye rom — ved
            // reconnect (f.eks. server-restart) opprettes et nytt rom på
            // serveren, og følgekode/kanal må re-registreres der, ellers viser
            // UI-et en fantomkode som serveren ikke lenger kjenner.
            useEffect(() => {
                if (romOpprettet && husAlias && connected && socket && roomCode
                        && autoInitRoomRef.current !== roomCode) {
                    autoInitRoomRef.current = roomCode;
                    const forsteGang = !autoInitRef.current;
                    autoInitRef.current = true;
                    socket.emit('sett_folgerom', { kode: (forsteGang ? husAlias : (folgKodeRef.current || husAlias)) });
                    // Første gang slås ALLE kanaler huset har konfigurert på.
                    // Tidligere sto 'ndi' hardkodet her. NDI er avviklet, og
                    // et hus uten den kanalen ville startet møtet helt uten
                    // utgang. Ved re-registrering gjenopprettes det som var på.
                    kanalAutoRef.current = true;
                    const paa = forsteGang
                        ? Object.fromEntries(husKanaler.map(k => [k, true]))
                        : kanalPaaRef.current;
                    for (const [k, v] of Object.entries(paa)) {
                        if (v) socket.emit('kanal_toggle', { kanal: k, paa: true, hus: husAlias });
                    }
                }
            }, [romOpprettet, husAlias, connected, socket, roomCode]);

            // Toppfelt: «Følgerom» (alltid på, redigerbart felt) + kanal-toggle.
            const visFolgKode = folgKode || husAlias || '';
            const sendFolgInput = () => {
                const k = folgInput.trim().toUpperCase();
                if (k && k !== visFolgKode) settFolgerom(k);
                else setFolgInput(visFolgKode);
            };
            const storskjermUI = (husAlias && romOpprettet && connected) ? (
                <span className="relative flex items-center gap-2">
                    {alleHus.length > 1 && (
                        <span className="flex items-center gap-1 text-xs" title="Aktivt hus: styrer hvilke vMix-utganger knappene kobler mot, og default følgekode.">
                            <span className="text-gray-500">Hus:</span>
                            <select
                                value={husAlias}
                                onChange={(e) => velgHus(e.target.value)}
                                aria-label="Velg aktivt hus"
                                className="px-1.5 py-0.5 bg-gray-800 border border-gray-600 rounded font-mono text-gull-400 text-xs focus:outline-none focus:border-gull-500"
                            >
                                {alleHus.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                        </span>
                    )}
                    <span className="flex items-center gap-1 text-xs" title="Følgekoden følgere/skjerm2 bruker. Sender alltid. Kan endres.">
                        <span className="text-gray-500">Følg:</span>
                        <input
                            id="folg-kode-input"
                            value={folgInput}
                            onChange={(e) => { setFolgInput(e.target.value.toUpperCase()); setFolgFeil(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                            onBlur={sendFolgInput}
                            aria-label="Følgerom-kode"
                            spellCheck={false}
                            className={`w-20 px-1.5 py-0.5 bg-gray-800 border rounded font-mono text-green-400 focus:outline-none focus:border-gull-500 ${folgFeil ? 'border-gull-500' : 'border-gray-600'}`}
                        />
                    </span>
                    {husKanaler.map((k) => {
                        const st = kanaler[k] || {};
                        const navn = KANAL_NAVN[k] || k;
                        const kilde = 'vMix-inputen';
                        return (
                            <button
                                key={k}
                                onClick={() => toggleKanal(k, !st.paa)}
                                className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition ${st.paa ? (st.aktiv ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-gull-500 hover:bg-gull-400 text-gull-950') : 'bg-red-700 hover:bg-red-600 text-white'}`}
                                title={st.paa
                                    ? (st.aktiv ? `${navn} på — kilde tilkoblet, sendes til storskjerm/stream. Klikk for å slå av.`
                                                : `${navn} på — venter på ${kilde}. Klikk for å slå av.`)
                                    : `${navn} av — klikk for å sende til storskjerm/stream.`}
                                aria-pressed={!!st.paa}
                            >
                                <span className={`w-2 h-2 rounded-full ${st.paa ? (st.aktiv ? 'bg-green-200' : 'bg-gull-200') : 'bg-red-300'}`} aria-hidden="true"></span>
                                {navn}
                            </button>
                        );
                    })}
                    {kanalParing && (
                        <span className="flex items-center gap-1 text-xs">
                            <input
                                id="kanal-paring-input"
                                value={kanalParingInput}
                                onChange={(e) => setKanalParingInput(e.target.value.replace(/\D/g, ''))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendKanalParing(); } }}
                                placeholder="Kode"
                                maxLength={4}
                                inputMode="numeric"
                                autoFocus
                                aria-label={`${KANAL_NAVN[kanalParing] || kanalParing}-paringskode fra storskjermen`}
                                className="w-14 px-1.5 py-0.5 bg-gray-800 border border-gull-500 rounded font-mono text-gull-300 focus:outline-none focus:border-gull-500"
                            />
                            <button onClick={sendKanalParing} className="px-2 py-0.5 bg-gull-500 hover:bg-gull-400 rounded text-gull-950 transition">OK</button>
                        </span>
                    )}
                    {folgFeil && (
                        <span
                            className="absolute top-full right-0 mt-1.5 px-2 py-1 text-xs text-gull-300 bg-gray-900 border border-gull-600 rounded shadow-lg whitespace-nowrap z-50"
                            role="alert"
                        >
                            {folgFeil}
                        </span>
                    )}
                </span>
            ) : null;

            // Delt statuslinje øverst i høyre panel (over sangen): rom/utganger
            // + globale dialogknapper. Én kilde for alle moduser — erstatter de
            // tidligere kopiene i venstre panel-headerne, som var overfylte.
            // Lukk verktøymenyen (⋮) med Escape
            useEffect(() => {
                if (!showVerktoyMeny) return;
                const paaTast = (e) => { if (e.key === 'Escape') setShowVerktoyMeny(false); };
                window.addEventListener('keydown', paaTast);
                return () => window.removeEventListener('keydown', paaTast);
            }, [showVerktoyMeny]);

            // Rad i verktøymenyen: lukker menyen og kjører handlingen
            const menyValg = (fn) => () => { setShowVerktoyMeny(false); fn(); };

            const statusLinjeUI = (
                <div className="flex flex-wrap md:flex-nowrap items-center justify-end gap-2 px-3 py-1.5 md:py-0 md:h-12 border-b border-gray-700 bg-gray-800 flex-shrink-0" role="toolbar" aria-label="Rom og utganger">
                    <span className="flex items-center gap-2 flex-wrap md:flex-nowrap toolbar-buttons mr-auto">
                        <button
                            id="new-display-btn"
                            onClick={(e) => { openDisplayWindow(e.currentTarget); }}
                            className="text-gull-400 hover:text-gull-300 hover:bg-gull-900/30 px-2 py-1 rounded transition flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-gull-500"
                            title="Åpne display (på skjerm 2 hvis tilgjengelig)"
                            aria-label="Åpne nytt display-vindu"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v4m2-2h-4" />
                            </svg>
                            <span className="text-xs">Ny</span>
                        </button>
                        {displaysConnected > 0 && (
                            <button
                                onClick={() => {
                                    if (qrVisible) {
                                        socket?.emit('hide_qr');
                                        setQrVisible(false);
                                    } else {
                                        socket?.emit('show_qr');
                                        setQrVisible(true);
                                    }
                                }}
                                className={`${qrVisible ? 'text-green-200 bg-green-700' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'} px-2 py-1 rounded transition flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-gull-500`}
                                title={qrVisible ? "Skjul QR-kode fra storskjerm" : "Vis QR-kode på storskjerm"}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h4v4H4zM16 4h4v4h-4zM4 16h4v4H4zM16 17v3h3M16 16h.01M20 16v4M12 4v4M12 12h.01M12 16v4" />
                                </svg>
                                <span className="text-xs">{qrVisible ? 'Skjul' : 'Skjerm'}</span>
                            </button>
                        )}
                        {displayWindows.filter(d => !d.win.closed).map((d, i) => (
                            <button
                                key={d.id}
                                onClick={() => {
                                    d.win.close();
                                    setDisplayWindows(prev => prev.filter(w => w.id !== d.id));
                                }}
                                className="text-green-500 hover:text-red-400 focus:text-red-400 p-1 rounded transition group focus:outline-none focus:ring-2 focus:ring-gull-500"
                                title={`Lukk skjerm ${i + 1}`}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    <path className="opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                                </svg>
                            </button>
                        ))}
                        <div className={`connection-dot ${connected && displaysConnected > 0 ? 'connected' : 'disconnected'}`} aria-hidden="true"></div>
                        <span className="text-xs text-gray-400" role="status" aria-live="polite">
                            {connected ? (() => {
                                // Følgere (følgekode) er teknisk skjermer, men vises for seg
                                const skjermer = Math.max(0, displaysConnected - folgereConnected);
                                const s1 = `${skjermer} skjerm${skjermer !== 1 ? 'er' : ''}`;
                                return folgereConnected > 0
                                    ? `${s1} · ${folgereConnected} følger${folgereConnected !== 1 ? 'e' : ''}`
                                    : s1;
                            })() : 'Frakoblet'}
                        </span>
                        {connectionError && (
                            <span className="text-xs text-gull-400 ml-2" role="alert">
                                {connectionError}
                            </span>
                        )}
                    </span>
                    {roomCode && (
                        <>
                            <span className="text-xs text-gray-500">Rom:</span>
                            <span
                                className="text-sm font-mono text-gull-400 cursor-pointer hover:text-gull-300"
                                onClick={() => {
                                    navigator.clipboard.writeText(roomCode);
                                }}
                                title="Klikk for å kopiere romkode"
                            >
                                {roomCode}
                            </span>
                            {storskjermUI}
                        </>
                    )}
                    <div className="relative">
                        <button
                            onClick={() => { setShowVerktoyMeny(v => !v); oppdaterInnlogget(); }}
                            className={`p-1 rounded transition ${showVerktoyMeny ? 'text-white bg-gray-700' : 'text-gray-400 hover:text-white'}`}
                            title="Meny"
                            aria-label="Åpne meny"
                            aria-haspopup="menu"
                            aria-expanded={showVerktoyMeny}
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="12" cy="19" r="1.7" />
                            </svg>
                        </button>
                        {showVerktoyMeny && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setShowVerktoyMeny(false)} aria-hidden="true"></div>
                                <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1" role="menu" aria-label="Verktøymeny">
                                    <a
                                        href="https://sangleder.bibeltroen.no"
                                        target="_blank"
                                        rel="noopener"
                                        onClick={() => setShowVerktoyMeny(false)}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition whitespace-nowrap"
                                        role="menuitem"
                                    >
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                                        </svg>
                                        Åpne Sangleder
                                        <span className="ml-auto text-gray-500 text-xs" aria-hidden="true">↗</span>
                                    </a>
                                    <button
                                        onClick={menyValg(() => setShowControllerQR(true))}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition whitespace-nowrap text-left"
                                        role="menuitem"
                                    >
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        iPad-kontroller (QR)
                                    </button>
                                    <button
                                        onClick={menyValg(() => setShowJoinRoomDialog(true))}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition whitespace-nowrap text-left"
                                        role="menuitem"
                                    >
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                        Koble til rom
                                    </button>
                                    {husAlias && (
                                        <button
                                            onClick={menyValg(aapneHusConfig)}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition whitespace-nowrap text-left"
                                            role="menuitem"
                                        >
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            Hus-innstillinger ({husAlias})
                                        </button>
                                    )}
                                    <button
                                        onClick={menyValg(() => (innloggetBruker !== null || isLoggedIn) ? loggUtAdmin() : apneInnlogging())}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition whitespace-nowrap"
                                        role="menuitem"
                                    >
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            {(innloggetBruker !== null || isLoggedIn)
                                                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />}
                                        </svg>
                                        {innloggetBruker ? `Logg ut (${innloggetBruker})`
                                            : (innloggetBruker !== null || isLoggedIn) ? 'Logg ut' : 'Logg inn'}
                                    </button>
                                    <button
                                        onClick={menyValg(() => startAdmin())}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition whitespace-nowrap text-left"
                                        role="menuitem"
                                    >
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        Administrasjon
                                    </button>
                                    <button
                                        onClick={menyValg(() => setShowHelp(true))}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition whitespace-nowrap text-left"
                                        role="menuitem"
                                    >
                                        <span className="w-5 h-5 bg-gray-600 text-gray-300 rounded-full flex items-center justify-center text-xs font-bold" aria-hidden="true">?</span>
                                        Tastaturhjelp
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            );

            // Check if we should join an existing room (iPad remote control)
            const getUrlRoomCode = () => {
                const params = new URLSearchParams(window.location.search);
                return params.get('rom') || params.get('room');
            };

            // Connect to WebSocket with auto-reconnect
            useEffect(() => {
                const urlRoomCode = getUrlRoomCode();
                // Romkoden denne FANEN eier. Lagres i sessionStorage (ikke
                // localStorage — nye faner skal være nye økter) og holdes i
                // live her gjennom hele økta, så BÅDE refresh og en ren
                // gjenoppkobling (nettbrudd midt i møtet) tar opp igjen samme
                // rom med samme koder. Nulles den, lager `connect` et nytt rom
                // og skjermen blir stående igjen på den gamle koden.
                // Rommet overlever tomt i en grace-periode på serveren; feiler
                // joinet (utløpt/omstart), opprettes nytt rom som før.
                let oktKode = urlRoomCode ? null : (sessionStorage.getItem('visOktRom') || null);
                // Bare den FØRSTE innmeldingen skal seede refs fra lagrede
                // valg — ved reconnect står React-tilstanden allerede riktig.
                let seedFraLagring = !!oktKode;
                // Er det VÅRT gjenopptaks-join vi venter svar på? Skiller
                // «rommet mitt er borte» fra en join_error på et rom
                // operatøren selv taster inn (da skal økta stå urørt).
                let venterGjenopptak = false;

                const s = io(WS_URL, {
                    reconnection: true,
                    reconnectionAttempts: Infinity,  // aldri gi opp — møtet varer lenger enn 10 forsøk
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    timeout: 20000
                });

                s.on('connect', () => {
                    setConnected(true);
                    setConnectionError(null);
                    if (urlRoomCode) {
                        // Join existing room (iPad mode)
                        s.emit('join_room', { room_code: urlRoomCode.toUpperCase() });
                    } else if (oktKode) {
                        // Gjenoppta egen økt (etter refresh ELLER nettbrudd)
                        venterGjenopptak = true;
                        s.emit('join_room', { room_code: oktKode });
                    } else {
                        // Create a room when connecting (PC mode)
                        s.emit('create_room');
                    }
                });

                s.on('disconnect', (reason) => {
                    setConnected(false);
                    if (reason === 'io server disconnect') {
                        // Server disconnected us, try to reconnect
                        s.connect();
                    }
                });

                s.on('connect_error', (error) => {
                    console.error('Connection error:', error);
                    setConnectionError('Kan ikke koble til server. Prøver på nytt...');
                });

                // NB: gjenoppkoblings-hendelsene ligger på MANAGEREN (s.io),
                // ikke på socketen — `s.on('reconnect', ...)` fyrer aldri i
                // Socket.IO v4 og etterlot operatøren med «Prøver på nytt...»
                // uten videre beskjed.
                s.io.on('reconnect', () => {
                    setConnectionError(null);
                });

                s.io.on('reconnect_attempt', (attemptNumber) => {
                    setConnectionError(`Kobler til på nytt... (forsøk ${attemptNumber})`);
                });

                s.io.on('reconnect_failed', () => {
                    console.error('Failed to reconnect');
                    setConnectionError('Kunne ikke koble til server. Last siden på nytt.');
                });

                s.on('room_created', (data) => {
                    setRoomCode(data.room_code);
                    setConnectionError(null);
                    {   // skjermjusteringen følger styreren inn i rommet
                        const st = skjermStilRef.current;
                        if (st.zoom !== 1 || st.dx || st.dy) s.emit('set_style', st);
                    }
                    setRomOpprettet(true);
                    setPresStatus(null); // nytt rom starter uten talerkontekst
                    setPresenterAntall(0);
                    oktKode = data.room_code;  // en senere reconnect skal JOINE dette, ikke lage et nytt rom
                    seedFraLagring = false;
                    sessionStorage.setItem('visOktRom', data.room_code);
                    // FØLG (alltid på) + kanalene settes automatisk via
                    // egen effekt når husAlias er lastet — se useEffect under.
                });

                s.on('room_joined', (data) => {
                    setRoomCode(data.room_code);
                    setConnectionError(null);
                    {   // skjermjusteringen følger styreren inn i rommet
                        const st = skjermStilRef.current;
                        if (st.zoom !== 1 || st.dx || st.dy) s.emit('set_style', st);
                    }
                    venterGjenopptak = false;
                    if (oktKode && data.room_code === oktKode) {
                        // Egen økt gjenopptatt (refresh eller nettbrudd): full
                        // operatørmodus. `oktKode` beholdes — også neste
                        // gjenoppkobling skal treffe dette rommet.
                        if (seedFraLagring) {
                            // Første innmelding etter en refresh: server-tilstanden
                            // (følgekode/kanal/presenter-kode) lever alt — seed refs
                            // fra lagrede valg så auto-oppsettet re-registrerer det
                            // samme i stedet for defaults.
                            seedFraLagring = false;
                            autoInitRef.current = true; // ikke førstegang
                            folgKodeRef.current = sessionStorage.getItem('visOktFolg') || null;
                            kanalPaaRef.current = oktKanalerRef.current || {};
                        }
                        setRomOpprettet(true);
                    } else {
                        setRomOpprettet(false);
                    }
                });

                // FØLG: følgekoden ble satt (alltid på)
                s.on('folgerom_satt', (data) => {
                    setFolgKode(data.kode || null);
                    setFolgInput(data.kode || '');
                    setFolgFeil('');
                    if (data.kode) sessionStorage.setItem('visOktFolg', data.kode);
                    announce('Følgerom: ' + (data.kode || ''));
                });
                // Følgekoden var opptatt av en annen økt → serveren har laget
                // en tilfeldig kode i stedet (romkoden skal aldri eksponeres)
                s.on('folgerom_opptatt', (data) => {
                    setFolgKode(data.folgkode || null);
                    setFolgInput(data.folgkode || '');
                    setFolgFeil('Koden var opptatt — ny kode valgt');
                    if (data.folgkode) sessionStorage.setItem('visOktFolg', data.folgkode);
                    announce('Følgekoden var opptatt — følgere bruker ' + (data.folgkode || ''));
                });
                s.on('folgerom_feil', (data) => {
                    setFolgFeil(data.error || 'Kunne ikke sette følgerom');
                });
                // Presenter-fjernkontroll: kode for /presenter (egen, adskilt fra følg)
                s.on('presenterkode_satt', (data) => {
                    setPresenterKode(data.kode || null);
                });
                s.on('presenter_epost_sendt', (data) => {
                    setPresenterEpost({ ok: true, tekst: `Lenke sendt til ${data.til}` });
                    announce(`Fjernkontroll-lenke sendt til ${data.til}`);
                });
                s.on('presenter_epost_feil', (data) => {
                    setPresenterEpost({ ok: false, tekst: data.error || 'E-posten kunne ikke sendes' });
                });
                // Talerens kontekst (posisjon + live) — settes av fjernkontrollen,
                // klargjøring og operatørens egne visninger. Holder markørene i sync.
                s.on('pres_status', (data) => setPresStatus(data));
                // Antall tilkoblede fjernkontroller («Koblet til fjernkontroll»)
                s.on('presenter_status', (data) => setPresenterAntall(data.antall || 0));
                // Avslag fra serveren (f.eks. vis-klikk mens sangen eier skjermen)
                s.on('presenter_feil', (data) => announce(data.error || ''));
                // Utgangskanal-status (vMix) — én hendelse per kanal
                s.on('kanal_status', (data) => {
                    const kanal = data.kanal || 'websocket';
                    setKanaler(prev => ({ ...prev, [kanal]: { paa: !!data.paa, aktiv: !!data.aktiv } }));
                    if (data.paa) {
                        setKanalParing(p => (p === kanal ? null : p));
                        setKanalParingInput('');
                        setFolgFeil('');
                    }
                });
                s.on('kanal_opptatt', (data) => {
                    const kanal = data.kanal || 'websocket';
                    setKanaler(prev => ({ ...prev, [kanal]: { paa: false, aktiv: false } }));
                    setKanalParing(p => (p === kanal ? null : p));
                    setFolgFeil(`${KANAL_NAVN[kanal] || kanal}-utgangen er i bruk av en annen økt`);
                });
                s.on('kanal_feil', (data) => {
                    const kanal = data.kanal || 'websocket';
                    setKanaler(prev => ({ ...prev, [kanal]: { paa: false, aktiv: false } }));
                    if (data.krever_innlogging) {
                        // Auto-oppsettet prøver kanalene for alle — for uinnloggede
                        // skal det feile stille. Manuelt klikk åpner innlogging;
                        // kanalen huskes og slås på igjen etter billett-innløsing.
                        if (!kanalAutoRef.current) {
                            pendingKanalRef.current = kanal;
                            setPendingKanal(kanal);
                            setShowLoginDialog(true);
                            setFolgFeil(data.error || 'Krever innlogging');
                        }
                        return;
                    }
                    setFolgFeil(data.error || `${KANAL_NAVN[kanal] || kanal}-feil`);
                });
                // Socket-økta har fått innloggingen (engangsbillett løst inn):
                // fullfør kanal-klikket som strandet på innloggingskravet.
                s.on('billett_status', (data) => {
                    if (!data.success) return;
                    kanalAutoRef.current = false; // ekte feil skal vises nå
                    const kanal = pendingKanalRef.current;
                    if (kanal) {
                        pendingKanalRef.current = null;
                        setPendingKanal(null);
                        s.emit('kanal_toggle', { kanal, paa: true, hus: husAliasRef.current });
                        return;
                    }
                    // Ingen strandet klikk: auto-oppsettet kjørte før innloggingen
                    // og fikk 'krever_innlogging' i stillhet. Nå som økta er
                    // innlogget, slå på husets kanaler — ellers ble utgangen
                    // aldri koblet med mindre operatøren husket å klikke selv.
                    for (const k of (husKanalerRef.current || [])) {
                        s.emit('kanal_toggle', { kanal: k, paa: true, hus: husAliasRef.current });
                    }
                });
                // Feil nett → serveren viser en paringskode på storskjermen;
                // operatøren taster den her for å bevise nærvær
                s.on('kanal_paring_krav', (data) => {
                    const kanal = data.kanal || 'websocket';
                    setKanaler(prev => ({ ...prev, [kanal]: { paa: false, aktiv: false } }));
                    setKanalParing(kanal);
                    setKanalParingInput('');
                    setFolgFeil('Tast koden som vises på storskjermen');
                });

                // Innboks: sangplaner delt til det faste rommet (leveres ved
                // innmelding som kontroller, og live når noen deler underveis)
                s.on('rom_planer', (data) => {
                    setRomPlaner(data.planer || []);
                });

                s.on('sangplan_delt', (data) => {
                    setRomPlaner(prev => [data, ...prev.filter(p =>
                        !(p.delt_av === data.delt_av && p.navn === data.navn))]);
                    announce(`Sangplan «${data.navn}» mottatt fra ${data.delt_av}`);
                });

                // Rommets sangplan er romtilstand på serveren (plan_sett/
                // plan_fremdrift → plan_status): den overlever refresh og
                // synkes til alle styrere og til sangleder-appens vise-modus.
                // Serveren er eneste kilde — lokale endringer emittes og
                // kommer tilbake hit.
                s.on('plan_status', (data) => {
                    const p = data.plan;
                    setImportedPlan(p ? {
                        bruker: p.delt_av || 'Sangleder', navn: p.navn,
                        sanger: p.elementer || [], aktiv: p.aktiv,
                        sunget: p.sunget || []
                    } : null);
                });

                s.on('join_error', (data) => {
                    if (venterGjenopptak) {
                        // Gjenopptaket feilet (fristen utløpt / server-omstart):
                        // start en frisk økt i stedet — som før denne funksjonen.
                        // En join_error på et rom operatøren selv taster inn
                        // faller IKKE hit, og lar økta stå urørt.
                        venterGjenopptak = false;
                        oktKode = null;
                        seedFraLagring = false;
                        sessionStorage.removeItem('visOktRom');
                        s.emit('create_room');
                        return;
                    }
                    console.error('Join error:', data.error);
                    setConnectionError(data.error || 'Kunne ikke bli med i rommet');
                    setRoomCode(null);
                });

                s.on('room_closed', (data) => {
                    setConnectionError(data.message || 'Rommet ble stengt');
                    setRoomCode(null);
                });

                s.on('display_count', (data) => {
                    const prevCount = displaysConnectedRef.current || 0;
                    setDisplaysConnected(data.count);
                    setFolgereConnected(data.folgere || 0);
                    displaysConnectedRef.current = data.count;
                    // Reset QR status when a new display connects (it won't have QR visible)
                    if (data.count > prevCount) {
                        setQrVisible(false);
                    }
                });

                s.on('qr_status', (data) => {
                    setQrVisible(data.visible);
                });

                setSocket(s);

                return () => s.disconnect();
            }, []);

            // Join existing room
            const handleJoinRoom = () => {
                if (socket && joinRoomCode.trim()) {
                    socket.emit('join_room', { room_code: joinRoomCode.trim().toUpperCase() });
                    setShowJoinRoomDialog(false);
                    setJoinRoomCode('');
                }
            };

            // Generate controller QR code when dialog opens
            useEffect(() => {
                if (showControllerQR && roomCode) {
                    // Small delay to ensure canvas is rendered
                    setTimeout(() => {
                        const canvas = document.getElementById('controller-qr-canvas');
                        if (canvas) {
                            const controlUrl = `${window.location.origin}/?rom=${roomCode}`;
                            QRCode.toCanvas(canvas, controlUrl, {
                                width: 200,
                                margin: 1,
                                color: { dark: '#000000', light: '#ffffff' }
                            }, (err) => {
                                if (err) console.error('QR code error:', err);
                            });
                        }
                    }, 50);
                }
            }, [showControllerQR, roomCode]);

            // Sangbok-register: flere bøker side om side. Hovedboka er den
            // redigerbare; importerte bøker (f.eks. en ryddet variant) er
            // skrivebeskyttede. Valgt bok huskes per enhet.
            const [bokId, setBokId] = useState(() => localStorage.getItem('sangbokValgtBok') || 'hovedbok');
            const [boker, setBoker] = useState([]);
            useEffect(() => {
                fetch('/api/boker')
                    .then(res => res.json())
                    .then(data => {
                        if (!data.success) return;
                        setBoker(data.boker);
                        // valgt bok kan være slettet siden sist
                        if (!data.boker.some(b => b.id === bokId)) setBokId('hovedbok');
                    })
                    .catch(() => {});
            }, []);
            const valgtBok = boker.find(b => b.id === bokId);
            const iHovedbok = bokId === 'hovedbok';
            // Redigerbar = hovedboka eller bok opprettet via /boker/ny (flagget
            // følger med fra /api/boker). Importerte bøker er skrivebeskyttede.
            const bokRedigerbar = iHovedbok || !!(valgtBok && valgtBok.redigerbar);

            // Bøker hører til ETT hus og deles aldri på tvers: velgeren viser
            // kun aktivt hus' bøker. Sist brukte bok huskes per hus; ellers
            // husets første bok (hovedboka som siste nødutvei).
            const husBoker = boker.filter(b => (b.hus || '').toUpperCase() === (husAlias || '').toUpperCase());
            useEffect(() => {
                if (!husAlias || !boker.length) return;
                const egne = boker.filter(b => (b.hus || '').toUpperCase() === husAlias.toUpperCase());
                let onsket = null;
                try { onsket = localStorage.getItem(`sangbokValgtBok:${husAlias}`); } catch (e) {}
                setBokId((onsket && egne.some(b => b.id === onsket)) ? onsket
                    : (egne[0] ? egne[0].id : 'hovedbok'));
            }, [husAlias, boker]);

            const SANGBOK_CSV_URL = iHovedbok ? 'sangbok_komplett.csv' : `/boker/${bokId}.csv`;
            const CACHE_KEY = `sangbok_songs_${bokId}`;
            const CACHE_TIME_KEY = `sangbok_songs_time_${bokId}`;
            // Kort cache: CSV-en er nå kilden til sannhet (redigeringer skrives
            // rett i den), så andre enheter bør se endringer raskt. Selve
            // nedlastingen er billig (304 hvis uendret, ~420 KB gzippet ellers).
            const CACHE_MAX_AGE = 1000 * 60 * 5; // 5 min

            const loadSongs = async (forceRefresh = false) => {
                // Try to load from cache first
                if (!forceRefresh) {
                    const cached = localStorage.getItem(CACHE_KEY);
                    const cacheTime = localStorage.getItem(CACHE_TIME_KEY);
                    if (cached && cacheTime) {
                        const age = Date.now() - parseInt(cacheTime);
                        if (age < CACHE_MAX_AGE) {
                            setSongs(JSON.parse(cached));
                            setLoading(false);
                            return;
                        }
                    }
                }

                setLoading(true);
                try {
                    const response = await fetch(SANGBOK_CSV_URL);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const csvText = await response.text();

                    Papa.parse(csvText, {
                        header: true,
                        delimiter: ';',
                        complete: (results) => {
                            const parsed = results.data
                                .filter(row => row.Nummer && row.Tittel && row.Tittel !== '<tom>')
                                .map(row => ({
                                    nummer: row.Nummer,
                                    tittel: row.Tittel,
                                    toneart: row.Toneart || '',
                                    tekst: row.Tekst || '',
                                    tekstforfatter: row.Tekstforfatter || '',
                                    copyright: row.Copyright || ''
                                }));
                            setSongs(parsed);
                            setLoading(false);
                            localStorage.setItem(CACHE_KEY, JSON.stringify(parsed));
                            localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());
                        },
                        error: (err) => {
                            console.error('CSV parse error:', err);
                            setLoading(false);
                        }
                    });
                } catch (error) {
                    console.error('Failed to load songs:', error);
                    setLoading(false);
                }
            };

            // Load songs on mount og ved bokbytte
            useEffect(() => {
                loadSongs();
                setSelectedSong(null);
                setVerses([]);
                // husk valget per hus (bøker er hus-private)
                const hus = husAliasRef.current;
                if (hus) {
                    try { localStorage.setItem(`sangbokValgtBok:${hus}`, bokId); } catch (e) {}
                }
            }, [bokId]);

            // Sømløs overføring fra Sangleder: hele planen ligger i ?plan=
            // (base64url av kompakt JSON: sangnummer som streng, notat som
            // {n: tekst}) — ingen serverlagring eller innlogging involvert.
            // Venter på at sangboka er lastet så titlene kan slås opp.
            useEffect(() => {
                if (songs.length === 0) return;
                if (!roomCode) return;   // plan_sett krever at økta (rommet) er oppe
                const params = new URLSearchParams(window.location.search);
                const encoded = params.get('plan');
                if (!encoded) return;
                try {
                    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
                    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
                    const compact = JSON.parse(new TextDecoder().decode(bytes));
                    const sanger = compact.map(item =>
                        (item && typeof item === 'object')
                            ? { kind: 'note', text: String(item.n || '') }
                            : { nummer: String(item), tittel: songs.find(s => s.nummer === String(item))?.tittel || '' }
                    );
                    planSett('Sangplan', sanger);
                    window.history.replaceState({}, '', window.location.pathname);
                } catch (err) {
                    console.error('Kunne ikke lese sangplan fra URL:', err);
                }
            }, [songs, roomCode]);

            // Hent sangplan med 4-bokstavs kortkode fra Sangleder
            // («virtuell minnepinne» — lagres uten innlogging, 24 t TTL).
            // Planene er ellers private; koden er eneste vei inn.
            const handleImportPlan = async () => {
                const code = importCode.trim().toUpperCase();
                if (!code) return;
                if (!/^[A-Z]{4}$/.test(code)) {
                    setImportError('Koden er 4 bokstaver (f.eks. KYNG)');
                    return;
                }
                setImportLoading(true);
                setImportError('');
                try {
                    const res = await fetch(`${SANGPLAN_API}/sangplan/kode/${code}`);
                    const data = await res.json();
                    if (data.success) {
                        planSett(data.navn || 'Sangplan', data.sangplan || []);
                        setShowImportDialog(false);
                        setImportCode('');
                    } else {
                        setImportError(data.error || 'Ukjent kode');
                    }
                } catch (err) {
                    setImportError('Nettverksfeil');
                }
                setImportLoading(false);
            };

            // === Planen som romtilstand ===
            // Alle planendringer går via serveren og kommer tilbake som
            // plan_status (se socket-lytteren) — det holder planen i synk
            // mellom styrere, over refresh, og ut til sangleder-appens
            // vise-modus. setImportedPlan kalles aldri direkte lenger.
            const planSett = (navn, elementer, deltAv) => {
                socketRef.current?.emit('plan_sett',
                    { plan: { navn, delt_av: deltAv || null, elementer } });
            };
            const planFjern = () => socketRef.current?.emit('plan_sett', { plan: null });

            // Velg planelement: vis sangen i styreren og flytt fremdriften.
            // Regelen er enkel: forlater du en sang, regnes den som sunget.
            const velgPlanElement = (idx) => {
                const p = importedPlan;
                if (!p) return;
                const item = p.sanger[idx];
                if (!item || item.kind === 'note') return;
                // Sangen slås opp i boka; planelementer fra Sangleder bærer
                // full tekst og kan vises selv om boka mangler nummeret.
                const fullSong = songs.find(s => s.nummer === item.nummer)
                    || (item.tekst ? item : null);
                if (fullSong) selectSong(fullSong);
                const sunget = (p.aktiv != null && p.aktiv !== idx && p.sanger[p.aktiv]?.kind !== 'note')
                    ? [...new Set([...(p.sunget || []), p.aktiv])]
                    : (p.sunget || []);
                socketRef.current?.emit('plan_fremdrift', { aktiv: idx, sunget });
            };
            const planNeste = () => {
                const p = importedPlan;
                if (!p) return;
                let idx = (p.aktiv == null ? -1 : p.aktiv) + 1;
                while (idx < p.sanger.length && p.sanger[idx].kind === 'note') idx++;
                if (idx < p.sanger.length) velgPlanElement(idx);
            };
            const planForrige = () => {
                const p = importedPlan;
                if (!p) return;
                let idx = (p.aktiv == null ? p.sanger.length : p.aktiv) - 1;
                while (idx >= 0 && p.sanger[idx].kind === 'note') idx--;
                if (idx >= 0) velgPlanElement(idx);
            };

            // Set initial focus when songs are loaded
            useEffect(() => {
                if (!loading && songs.length > 0) {
                    setTimeout(() => {
                        document.getElementById('songs-tab')?.focus();
                    }, 50);
                }
            }, [loading, songs.length]);

            // Select song
            const selectSong = (song) => {
                setSelectedSong(song);
                const parsedVerses = parseVerses(song.tekst);
                setVerses(parsedVerses);
                setCurrentVerse(0);
                navLevelRef.current = 0;
                setNavLevel(0);
                // Clear display when selecting a new song
                socket?.emit('clear');
                setQrVisible(false);
                announce(`Sang ${song.nummer}: ${song.tittel} valgt. ${parsedVerses.length} vers.`);
            };

            // Når noe annet tar skjermen er bibelverset ikke lenger vist:
            // demoter bibelnivået grønt → gult, ellers «lyver» grønt og
            // versjonsbytte re-sender bibelen oppå det som faktisk vises.
            const bibelIkkePaaSkjerm = () => {
                if (bibleNavLevelRef.current === 2) {
                    bibleNavLevelRef.current = 1;
                    setBibleNavLevel(1);
                }
            };

            // Show specific verse - use ref to avoid stale closures
            const socketRef = useRef(socket);
            const selectedSongRef = useRef(selectedSong);
            const navLevelRef = useRef(0);
            // Navigation levels: 0 = song list, 1 = song preview (no display), 2 = display active
            // (deklarert her — brukes av effekten under; var tidligere deklarert
            // lenger ned, som ga use-before-init i deps-arrayen)
            const [navLevel, setNavLevel] = useState(0);

            useEffect(() => {
                socketRef.current = socket;
            }, [socket]);

            useEffect(() => {
                selectedSongRef.current = selectedSong;
            }, [selectedSong]);

            useEffect(() => {
                navLevelRef.current = navLevel;
            }, [navLevel]);

            const showVerse = (index) => {
                setCurrentVerse(index);
                if (socketRef.current && selectedSongRef.current) {
                    // Send both song and verse to ensure display is synced
                    socketRef.current.emit('show_song', { song: selectedSongRef.current, verse: index });
                    sisteVisning.current.songs = { song: selectedSongRef.current, verse: index };
                    bibelIkkePaaSkjerm();
                }
                // Annonser vers for skjermleser
                const verseLabel = verses[index]?.isChorus ? 'Kor' : `Vers ${verses.slice(0, index + 1).filter(v => !v.isChorus).length}`;
                announce(`${verseLabel} vises på skjerm`);
            };

            // «Fanen trumfer visningen»: hver fane husker sitt sist viste
            // innhold OG nivået den ble forlatt på. Kun GRØNT (på skjerm da
            // du forlot fanen) re-sendes ved retur; gult forblir gult med
            // blank skjerm. (Eksplisitt «tøm skjerm» nullstiller minnet.)
            const sisteVisning = useRef({ songs: null, bible: null, presentasjon: null });
            const fanenivaa = useRef({ songs: 0, bible: 0, presentasjon: 0 });
            const forrigeFane = useRef(appMode);
            useEffect(() => {
                if (forrigeFane.current === appMode) return;
                forrigeFane.current = appMode;
                const s = socketRef.current;
                if (!s) return;
                const husket = sisteVisning.current[appMode];
                if (husket && fanenivaa.current[appMode] === 2) {
                    if (appMode === 'songs') {
                        s.emit('show_song', husket);
                        navLevelRef.current = 2;
                        setNavLevel(2);
                    } else if (appMode === 'bible') {
                        s.emit('show_bible', husket);
                        bibleNavLevelRef.current = 2;
                        setBibleNavLevel(2);
                    } else if (appMode === 'presentasjon') {
                        s.emit('show_image', husket);
                        setPresVist(true);
                    }
                } else {
                    // Var gul (eller tom) da fanen ble forlatt → blank skjerm
                    s.emit('clear');
                    if (appMode === 'presentasjon') setPresVist(false);
                }
                setQrVisible(false);
                // Klikkfokus blir stående på faneknappen, og dens piltast-
                // handlere (stopPropagation) fanger alt — slipp fokuset så
                // innholds-navigasjonen tar over piltastene. Men KUN når
                // fokuset står på selve faneknappen: ↓-fra-fanen har allerede
                // flyttet fokus inn i innholdet (f.eks. 1. Mosebok), og det
                // fokuset skal ikke røres.
                setTimeout(() => {
                    const aktiv = document.activeElement;
                    if (aktiv?.getAttribute?.('role') === 'tab') aktiv.blur();
                }, 100);
            }, [appMode]);

            // Nivåene speiles fortløpende MENS fanen er aktiv — fanebytte-
            // handlerne nullstiller nivå-refene synkront, så en fangst i
            // bytteøyeblikket leser alltid 0/1 og «glemmer» grønt. Guarden
            // på appMode gjør at nullstillingen ved bytte (samme render som
            // appMode endres) ikke overskriver avgangsnivået. MÅ stå ETTER
            // trumf-effekten: den skal lese forrige verdi før speilingen
            // skriver denne renderens.
            useEffect(() => {
                if (appMode === 'songs') fanenivaa.current.songs = navLevel;
            }, [navLevel, appMode]);
            useEffect(() => {
                if (appMode === 'bible') fanenivaa.current.bible = bibleNavLevel;
            }, [bibleNavLevel, appMode]);
            useEffect(() => {
                if (appMode === 'presentasjon') fanenivaa.current.presentasjon = presVist ? 2 : 0;
            }, [presVist, appMode]);

            // Sangbok Skjerm Chrome extension ID. When the user has installed it, we ask
            // it to open the display directly in real fullscreen on the secondary screen
            // (no click required). The extension uses chrome.windows.create(state:fullscreen)
            // which web pages can't trigger themselves due to the user-gesture requirement.
            // Two IDs are accepted. The Chrome Web Store assigns its own ID on first
            // upload — the manifest "key" field does NOT carry the dev ID across, so the
            // store build and the sideloaded dev build (whose manifest pins the old ID
            // via "key") will never match. Both must keep working while a dev copy is
            // loaded unpacked.
            const SANGBOK_EXTENSION_IDS = [
                "oceojeffhbpbbhpfalnjjagkhcgiahhn", // Chrome Web Store
                "clhlffnohoppjllbimjnelhedmjajlff", // sideloaded dev (manifest "key")
            ];

            // Ping both at once and keep whichever answers. Probing them one after the
            // other would double the wait when nothing is installed; pinging in parallel
            // and only then sending open-fullscreen also avoids opening two windows when
            // both builds happen to be installed at the same time.
            const detectExtension = () => new Promise(resolve => {
                let settled = false, pending = SANGBOK_EXTENSION_IDS.length;
                const done = (result) => { if (!settled) { settled = true; resolve(result); } };
                const miss = () => { if (--pending === 0) done(null); };
                setTimeout(() => done(null), 1500); // nothing installed → bail
                for (const extId of SANGBOK_EXTENSION_IDS) {
                    try {
                        chrome.runtime.sendMessage(extId, { action: "ping" }, (response) => {
                            if (chrome.runtime.lastError) return miss();
                            if (response?.ok) done(extId); else miss();
                        });
                    } catch (e) { miss(); }
                }
            });

            const tryExtensionFullscreen = async (url) => {
                if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) return null;
                const extId = await detectExtension();
                if (!extId) return null;
                return new Promise(resolve => {
                    let settled = false;
                    const done = (result) => { if (!settled) { settled = true; resolve(result); } };
                    setTimeout(() => done(null), 1500);
                    try {
                        chrome.runtime.sendMessage(
                            extId,
                            { action: "open-fullscreen", url },
                            (response) => {
                                if (chrome.runtime.lastError) return done(null);
                                done(response?.ok ? { ...response, extId } : null);
                            }
                        );
                    } catch (e) {
                        done(null);
                    }
                });
            };

            // Open the display. Three paths in order of preference:
            //   1. Sangbok Skjerm extension installed → real fullscreen on 2nd screen, zero clicks
            //   2. Window Management API → popup positioned on 2nd screen, one click to fullscreen
            //   3. Plain popup → user moves it manually, one click to fullscreen
            // macOS Chrome requires a user gesture in the popup's own document to enter real
            // fullscreen, which is why path 2 needs a click. Extensions bypass that.
            const openDisplayWindow = async (btnEl) => {
                const url = roomCode ? `/skjerm?rom=${roomCode}` : '/skjerm';
                const winName = `vis_skjerm_${Date.now()}`;
                let win = null;
                let placedOnSecondScreen = false;

                // Path 1: extension. Returns {ok, windowId} if installed.
                // fs=1 tells skjerm.html the popup is already in real fullscreen at the
                // window level (extension's chrome.windows.create state:"fullscreen"),
                // so it should suppress the click-to-fullscreen overlay.
                const extUrl = `${window.location.origin}${url}${url.includes('?') ? '&' : '?'}fs=1`;
                const ext = await tryExtensionFullscreen(extUrl);
                if (ext?.ok && ext.windowId) {
                    const id = Date.now();
                    // Adapter: emulate the {win} shape the close-button UI expects.
                    const entry = {
                        closed: false,
                        close() {
                            this.closed = true;
                            try {
                                chrome.runtime.sendMessage(
                                    ext.extId,
                                    { action: "close-window", windowId: ext.windowId },
                                    () => { /* ignore lastError */ }
                                );
                            } catch (e) { /* extension uninstalled mid-session */ }
                        },
                    };
                    setDisplayWindows(prev => [...prev, { id, win: entry }]);
                    announce('Display åpnet i fullskjerm på sekundær skjerm');
                    if (btnEl) btnEl.focus();
                    return entry;
                }

                if ('getScreenDetails' in window) {
                    try {
                        const details = await window.getScreenDetails();
                        const current = details.currentScreen;
                        const target = details.screens.find(s =>
                            s.left !== current.left || s.top !== current.top
                        );
                        if (target) {
                            win = window.open(
                                url,
                                winName,
                                `popup=yes,` +
                                `left=${target.availLeft},top=${target.availTop},` +
                                `width=${target.availWidth},height=${target.availHeight}`
                            );
                            if (win) {
                                placedOnSecondScreen = true;
                                announce('Display åpnet på sekundær skjerm — klikk i skjerm-vinduet for fullskjerm');
                            }
                        }
                    } catch (e) {
                        // Permission denied or unsupported. Fall through.
                    }
                }

                if (!win) {
                    win = window.open(
                        url,
                        winName,
                        'width=1280,height=720,menubar=no,toolbar=no,location=no,status=no'
                    );
                }

                if (win) {
                    const id = Date.now();
                    setDisplayWindows(prev => [...prev, { id, win }]);
                    if (!placedOnSecondScreen) {
                        win.blur();
                        window.focus();
                    }
                    if (btnEl) btnEl.focus();
                }
                return win;
            };

            // Phrase prefix match. Query words must appear consecutively in text, in order,
            // each as a prefix of the corresponding text word. Uses _normalize and
            // _splitWords defined near findPhrasePosition above.
            const fuzzyMatch = (text, query) => {
                if (!query) return true;
                const tWords = _splitWords(_normalize(text));
                const qWords = _splitWords(_normalize(query));
                if (qWords.length === 0) return true;
                if (tWords.length < qWords.length) return false;

                for (let i = 0; i <= tWords.length - qWords.length; i++) {
                    let ok = true;
                    for (let j = 0; j < qWords.length; j++) {
                        if (!tWords[i + j].startsWith(qWords[j])) { ok = false; break; }
                    }
                    if (ok) return true;
                }
                return false;
            };

            // Filter songs
            const filteredSongs = useMemo(() => {
                let filtered = songs;

                if (searchQuery) {
                    const q = searchQuery.toLowerCase();
                    filtered = songs.filter(s =>
                        s.nummer.includes(q) ||
                        fuzzyMatch(s.tittel, searchQuery) ||
                        fuzzyMatch(s.tekst, searchQuery)
                    );
                }

                // Sort based on active tab and direction
                const dir = sortAsc ? 1 : -1;
                const sortFn = (a, b) => {
                    if (activeTab === 'nummer') {
                        return dir * (parseInt(a.nummer) - parseInt(b.nummer));
                    } else if (activeTab === 'alfa') {
                        return dir * a.tittel.localeCompare(b.tittel, 'no');
                    } else {
                        const ta = a.toneart || 'zzz';
                        const tb = b.toneart || 'zzz';
                        return dir * (ta.localeCompare(tb) || parseInt(a.nummer) - parseInt(b.nummer));
                    }
                };

                // When searching, partition: nummer/title-matches first, then text-only matches.
                // Each group is sorted by the user's chosen sort order.
                if (searchQuery) {
                    const q = searchQuery.toLowerCase();
                    const titleHits = [];
                    const textHits = [];
                    for (const s of filtered) {
                        const isTitleHit = s.nummer.includes(q) || fuzzyMatch(s.tittel, searchQuery);
                        (isTitleHit ? titleHits : textHits).push(s);
                    }
                    return [...titleHits.sort(sortFn), ...textHits.sort(sortFn)];
                }

                return [...filtered].sort(sortFn);
            }, [songs, searchQuery, activeTab, sortAsc]);

            // Login handler - authenticates via server
            const handleLogin = async () => {
                setLoginLoading(true);
                setLoginError('');

                try {
                    const response = await fetch('/api/auth/login', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ password: loginPassword })
                    });

                    const data = await response.json();

                    if (data.success) {
                        setIsLoggedIn(true);
                        setShowLoginDialog(false);
                        setLoginPassword('');
                        oppdaterSocketOkt();
                        // Fortsett dit brukeren var på vei: kanal, redigering eller ny sang
                        if (pendingLoggInn) {
                            setPendingLoggInn(false);   // innlogging fra menyen: ingen handling etterpå
                        } else if (pendingKanal) {
                            // kanalen slås på igjen når billetten er løst inn (billett_status)
                        } else if (pendingEditSong) {
                            setEditMode(true);
                            setEditSong({ ...pendingEditSong });
                            setPendingEditSong(null);
                        } else {
                            setNewSong({ nummer: '', tittel: '', toneart: '', tekst: '', tekstforfatter: '', copyright: '' });
                            setAddError('');
                            setAddMode(true);
                        }
                    } else {
                        setLoginError(data.error || 'Feil passord');
                    }
                } catch (err) {
                    setLoginError('Kunne ikke koble til server');
                } finally {
                    setLoginLoading(false);
                }
            };

            // Konto-innlogging: e-post/brukernavn + passord mot sangleder-kontoen.
            // Rollen avgjør hva som åpnes: add krever sangleder+, edit redaktør+.
            const handleKontoLogin = async (passordOverstyr) => {
                // onClick sender et event-objekt — bare en streng teller som overstyring
                const passord = typeof passordOverstyr === 'string' ? passordOverstyr : kontoPassord;
                setLoginLoading(true);
                setLoginError('');
                try {
                    const response = await fetch('/api/sangplan/auth', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ bruker_id: kontoBruker.trim(), password: passord })
                    });
                    const data = await response.json();
                    if (!data.success) {
                        setLoginError(data.error || 'Feil e-post eller passord');
                        return;
                    }
                    oppdaterSocketOkt();
                    const roller = (data.rolle || '').split(',');
                    if (pendingLoggInn) {
                        setShowLoginDialog(false);
                        setKontoPassord('');
                        setPendingLoggInn(false);
                    } else if (pendingAdmin) {
                        if (roller.includes('admin') || roller.includes('superadmin')) {
                            setShowLoginDialog(false);
                            setKontoPassord('');
                            setPendingAdmin(false);
                            apnAdminPanel(data.rolle);
                        } else {
                            setLoginError('Administrasjon krever admin-rolle — kontoen din har ikke det');
                        }
                    } else if (pendingKanal) {
                        setShowLoginDialog(false);
                        setKontoPassord('');
                        // kanalen slås på igjen når billetten er løst inn (billett_status)
                    } else if (pendingEditSong) {
                        if (harRedigeringsRolle(roller)) {
                            setShowLoginDialog(false);
                            setKontoPassord('');
                            setEditMode(true);
                            setEditSong({ ...pendingEditSong });
                            setPendingEditSong(null);
                        } else {
                            setLoginError('Redigering krever redaktør-rolle — kontoen din har ikke det');
                        }
                    } else {
                        if (data.kan_legge_til_sang) {
                            setShowLoginDialog(false);
                            setKontoPassord('');
                            setNewSong({ nummer: '', tittel: '', toneart: '', tekst: '', tekstforfatter: '', copyright: '' });
                            setAddError('');
                            setAddMode(true);
                        } else {
                            setLoginError('Kontoen mangler rolle — be administratoren om sangleder- eller operatør-rolle');
                        }
                    }
                } catch (err) {
                    setLoginError('Kunne ikke koble til server');
                } finally {
                    setLoginLoading(false);
                }
            };

            // Glemt passord (konto): 6-sifret kode på e-post → nytt passord →
            // vanlig innlogging med det nye passordet, så fortsettelsen
            // (admin/kanal/redigering/ny sang/meny) blir som ved ordinær innlogging.
            const handleGlemtKonto = async () => {
                setLoginLoading(true);
                setLoginError('');
                try {
                    const r = await fetch('/api/sangplan/glemt-passord', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                        body: JSON.stringify({ brukernavn: kontoBruker.trim() })
                    });
                    const d = await r.json();
                    if (!d.success) { setLoginError(d.error || 'Kunne ikke sende kode'); return; }
                    setGlemtInfo(d.message || 'Hvis kontoen finnes og har e-post, er en kode sendt.');
                    setKontoSteg('nytt');
                } catch (e) {
                    setLoginError('Kunne ikke koble til server');
                } finally {
                    setLoginLoading(false);
                }
            };
            const handleTilbakestillKonto = async () => {
                if (glemtNyttPassord.length < 8) { setLoginError('Passordet må ha minst 8 tegn'); return; }
                setLoginLoading(true);
                setLoginError('');
                try {
                    const r = await fetch('/api/sangplan/tilbakestill', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                        body: JSON.stringify({ brukernavn: kontoBruker.trim(), kode: glemtKode.trim(), nytt_passord: glemtNyttPassord })
                    });
                    const d = await r.json();
                    if (!d.success) { setLoginError(d.error || 'Feil eller utløpt kode'); return; }
                    const nytt = glemtNyttPassord;
                    setKontoSteg('logg'); setGlemtKode(''); setGlemtNyttPassord(''); setGlemtInfo('');
                    setLoginLoading(false);
                    await handleKontoLogin(nytt);
                    return;
                } catch (e) {
                    setLoginError('Kunne ikke koble til server');
                } finally {
                    setLoginLoading(false);
                }
            };

            // Send reset code via email
            const handleForgotPassword = async () => {
                setLoginLoading(true);
                setLoginError('');

                try {
                    const response = await fetch('/api/auth/forgot', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include'
                    });

                    const data = await response.json();

                    if (data.success) {
                        setShowCodeInput(true);
                        setCodeSent(true);
                    } else {
                        setLoginError(data.error || 'Kunne ikke sende kode');
                    }
                } catch (err) {
                    setLoginError('Kunne ikke koble til server');
                } finally {
                    setLoginLoading(false);
                }
            };

            // Verify reset code
            const handleVerifyCode = async () => {
                setLoginLoading(true);
                setLoginError('');

                try {
                    const response = await fetch('/api/auth/verify-code', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ code: resetCode })
                    });

                    const data = await response.json();

                    if (data.success) {
                        setIsLoggedIn(true);
                        setShowLoginDialog(false);
                        setResetCode('');
                        setShowCodeInput(false);
                        setCodeSent(false);
                        oppdaterSocketOkt();
                        // Fortsett dit brukeren var på vei: kanal, redigering eller ny sang
                        if (pendingLoggInn) {
                            setPendingLoggInn(false);   // innlogging fra menyen: ingen handling etterpå
                        } else if (pendingKanal) {
                            // kanalen slås på igjen når billetten er løst inn (billett_status)
                        } else if (pendingEditSong) {
                            setEditMode(true);
                            setEditSong({ ...pendingEditSong });
                            setPendingEditSong(null);
                        } else {
                            setNewSong({ nummer: '', tittel: '', toneart: '', tekst: '', tekstforfatter: '', copyright: '' });
                            setAddError('');
                            setAddMode(true);
                        }
                    } else {
                        setLoginError(data.error || 'Ugyldig kode');
                    }
                } catch (err) {
                    setLoginError('Kunne ikke koble til server');
                } finally {
                    setLoginLoading(false);
                }
            };

            const [pendingEditSong, setPendingEditSong] = useState(null);
            const [addMode, setAddMode] = useState(false);

            // Esc lukker øverste åpne dialog — ett sted for alle dialogene.
            // (Ligger her, etter addMode-deklarasjonen: hooks som leser en
            // useState før den er deklarert gir ReferenceError → sort side.)
            // Fanges i capture-fasen, så Esc ikke også treffer den globale
            // tastaturhandleren (som ellers tømmer sangsøket bak dialogen)
            // eller feltet som har fokus. Rediger-dialogen har egen Esc
            // (cancelEdit) i den globale handleren og er ikke med her.
            // Lukkingen speiler dialogens egen Lukk/Avbryt-knapp.
            useEffect(() => {
                const dialoger = [
                    [showLoginDialog, () => {
                        setShowLoginDialog(false); setLoginError(''); setShowCodeInput(false);
                        setPendingAdmin(false); setPendingKanal(null); pendingKanalRef.current = null;
                        setPendingLoggInn(false);
                    }],
                    [showControllerQR, () => setShowControllerQR(false)],
                    [showHusConfig, () => setShowHusConfig(false)],
                    [showGotoDialog, () => { setShowGotoDialog(false); setGotoNumber(''); }],
                    [showImportDialog, () => { setShowImportDialog(false); setImportCode(''); setImportError(''); }],
                    [showAdminDialog, () => setShowAdminDialog(false)],
                    [showJoinRoomDialog, () => { setShowJoinRoomDialog(false); setJoinRoomCode(''); }],
                    [addMode, () => setAddMode(false)],
                    [showHelp, () => setShowHelp(false)],
                ];
                const apen = dialoger.find(([er]) => er);
                if (!apen) return;
                const paaTast = (e) => {
                    if (e.key !== 'Escape') return;
                    e.preventDefault();
                    e.stopPropagation();
                    apen[1]();
                };
                window.addEventListener('keydown', paaTast, true);
                return () => window.removeEventListener('keydown', paaTast, true);
            }, [showLoginDialog, showControllerQR, showHusConfig, showGotoDialog, showImportDialog,
                showAdminDialog, showJoinRoomDialog, addMode, showHelp]);
            const [newSong, setNewSong] = useState({ nummer: '', tittel: '', toneart: '', tekst: '', tekstforfatter: '', copyright: '' });
            const [addSaving, setAddSaving] = useState(false);
            const [addError, setAddError] = useState('');

            // Sangplan-sesjonen (delt cookie med sangleder.bibeltroen.no) kan gi
            // sang-rettigheter via rolle — sjekkes ved klikk, ikke ved lasting,
            // så innlogging i en annen fane plukkes opp.
            const sjekkSangplanTilgang = async () => {
                try {
                    const r = await fetch('/api/sangplan/check-auth', { credentials: 'include' });
                    return await r.json() || {};
                } catch (err) {
                    return {};
                }
            };

            // Innloggingsstatus til ⋮-menyen («Logg inn» / «Logg ut (bruker)»).
            // Leses live over HTTP, så utlogging i en annen fane også synes.
            const oppdaterInnlogget = async () => {
                const t = await sjekkSangplanTilgang();
                setInnloggetBruker(t.authenticated ? (t.bruker_id || '') : null);
                try {
                    const r = await fetch('/api/auth/check', { credentials: 'include' });
                    const d = await r.json();
                    setIsLoggedIn(!!d.authenticated);
                } catch (e) {}
            };
            useEffect(() => { oppdaterInnlogget(); }, []);

            const apneInnlogging = () => {
                setPendingLoggInn(true);
                setLoginMode('konto');
                setLoginError('');
                setShowLoginDialog(true);
            };

            // Socket-økta ser innloggingen slik den var da socketen koblet til.
            // Etter innlogging i denne fanen: hent engangsbillett over HTTP
            // (fersk cookie) og løs den inn på socketen — uten omkobling.
            const oppdaterSocketOkt = async () => {
                // socketRef, ikke `socket`-staten: denne kalles fra
                // innloggings-handlere som kan holde en eldre render-closure
                // der staten ennå er null. Da ble billetten hentet og
                // forkastet i stillhet, og socket-økta ble aldri innlogget
                // (kanalene forble avvist med sess=None).
                oppdaterInnlogget();
                const s = socketRef.current || socket;
                if (!s) return;
                try {
                    const r = await fetch('/api/sangplan/socket-billett', {
                        method: 'POST', credentials: 'include'
                    });
                    const data = await r.json();
                    if (data.success) s.emit('los_inn_billett', { billett: data.billett });
                } catch (e) {}
            };

            // ── Administrasjon (⚙): kanalrom + brukere — flyttet fra editoren ──
            const harAdminRolle = (r) => (r || '').split(',').some(t => t === 'admin' || t === 'superadmin');
            // Redigering av sanger: redaktør, admin eller superadmin — speiler
            // serverens _har_redigeringstilgang (superadmin dekker alltid alt)
            const harRedigeringsRolle = (roller) => roller.some(t => t === 'redaktor' || t === 'admin' || t === 'superadmin');
            const harSuperRolle = (r) => (r || '').split(',').includes('superadmin');
            const KANAL_LABEL = { websocket: 'Websocket (vMix)' };

            const adminApi = async (sti, opts) => {
                try {
                    const r = await fetch('/api' + sti, Object.assign({ credentials: 'include' }, opts));
                    return await r.json();
                } catch (e) {
                    return { success: false, error: 'Kunne ikke koble til server' };
                }
            };
            const adminApiJson = (sti, body) => adminApi(sti, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            const apnAdminPanel = (rolle) => {
                setAdminRolle(rolle || '');
                setAdminStatus('');
                setAdminTab(harSuperRolle(rolle) ? 'kanalrom' : 'brukere');
                setShowAdminDialog(true);
            };

            // Logg ut av sangplan-kontoen (sesjonen deles over .bibeltroen.no,
            // så dette logger også ut editoren og sangleder-siden).
            const loggUtAdmin = async () => {
                await adminApi('/sangplan/logout', { method: 'POST' });
                await adminApi('/auth/logout', { method: 'POST' });   // delt passord
                setInnloggetBruker(null);
                setIsLoggedIn(false);
                // Socket-økta ser det frosne sesjonsbildet fra connect, så
                // utloggingen må meldes eksplisitt: serveren trekker
                // kanalretten og river vMix-broen (kanal_status paa:false).
                socket?.emit('logg_ut');
                setAdminRolle('');
                setShowAdminDialog(false);
            };

            // Tannhjulet: åpner panelet direkte hvis økta alt har admin-rolle,
            // ellers via konto-innloggingen (samme brukerbase som editoren).
            const startAdmin = async () => {
                const tilgang = await sjekkSangplanTilgang();
                if (harAdminRolle(tilgang.rolle)) { apnAdminPanel(tilgang.rolle); return; }
                setPendingAdmin(true);
                setLoginMode('konto');
                setLoginError(tilgang.authenticated
                    ? 'Kontoen din mangler admin-rolle — logg inn med en admin-konto'
                    : '');
                setShowLoginDialog(true);
            };

            const lastKanalrom = async () => {
                const data = await adminApi('/admin/faste-rom');
                if (data.success) setAdminKanalrom(data.rom);
                else setAdminStatus(data.error || 'Kunne ikke hente kanalrom');
            };
            const lastBrukere = async () => {
                const data = await adminApi('/editor/brukere');
                if (data.success) setAdminBrukere(data.brukere);
                else setAdminStatus(data.error || 'Kunne ikke hente brukere');
            };
            useEffect(() => {
                if (!showAdminDialog) return;
                if (adminTab === 'kanalrom') lastKanalrom();
                else lastBrukere();
            }, [showAdminDialog, adminTab]);

            const opprettKanalrom = async () => {
                const hus = nyttHus.trim().toUpperCase();
                if (!hus) { setAdminStatus('Skriv inn husnavn'); return; }
                const res = await adminApiJson('/admin/faste-rom', { hus });
                setAdminStatus(res.success
                    ? `Opprettet ${hus}: ${res.kanaler.map(k => KANAL_LABEL[k] || k).join(' + ')}`
                      + (res.sangbok ? ' + egen sangbok (med «Amazing Grace»)' : '')
                      + ' — kodene står i tabellen'
                    : res.error);
                if (res.success) { setNyttHus(''); lastKanalrom(); }
            };
            const roterKanalrom = async (r) => {
                const navn = `${r.hus} / ${KANAL_LABEL[r.kanal] || r.kanal}`;
                if (!confirm(`Ny kode for ${navn}?\nTilkoblet display kastes ut og trenger ny URL.`)) return;
                const res = await adminApi(`/admin/faste-rom/${r.id}/roter`, { method: 'POST' });
                setAdminStatus(res.success ? `Ny kode for ${navn}: ${res.kode}` : res.error);
                if (res.success) lastKanalrom();
            };
            const toggleKanalromAktiv = async (r) => {
                const navn = `${r.hus} / ${KANAL_LABEL[r.kanal] || r.kanal}`;
                const res = await adminApiJson(`/admin/faste-rom/${r.id}/aktiv`, { aktiv: !r.aktiv });
                setAdminStatus(res.success ? `${navn} er ${res.aktiv ? 'på' : 'av'}` : res.error);
                if (res.success) lastKanalrom();
            };
            const slettKanalrom = async (r) => {
                const navn = `${r.hus} / ${KANAL_LABEL[r.kanal] || r.kanal}`;
                if (!confirm(`Slette kanalrommet ${navn}?\nKoden slutter å virke umiddelbart.`)) return;
                const res = await adminApi(`/admin/faste-rom/${r.id}`, { method: 'DELETE' });
                setAdminStatus(res.success ? `${navn} er slettet` : res.error);
                if (res.success) lastKanalrom();
            };
            const kopierKanalUrl = async (r) => {
                try {
                    await navigator.clipboard.writeText(r.url);
                    setKopiertUrlId(r.id);
                    setTimeout(() => setKopiertUrlId(null), 1500);
                } catch (e) {
                    prompt('Kopier skjerm-URL-en:', r.url);
                }
            };

            // superadmin tildeles/fratas kun av superadmin (serveren håndhever)
            const rolleValgFor = (b) => {
                const valg = [
                    ['', 'Vanlig bruker'],
                    ['sangleder', 'Sangleder'],
                    ['operator', 'Operatør'],
                    ['operator,sangleder', 'Operatør + sangleder'],
                    ['redaktor', 'Redaktør'],
                    ['admin', 'Admin'],
                ];
                if (harSuperRolle(adminRolle)) valg.push(['superadmin', 'Superadmin']);
                // vis uansett brukerens faktiske rolle, selv om den ikke er blant valgene
                if (b.rolle && !valg.some(([v]) => v === b.rolle)) valg.push([b.rolle, b.rolle]);
                return valg;
            };
            const settBrukerRolle = async (b, rolle) => {
                const res = await adminApiJson('/editor/rolle', { brukernavn: b.brukernavn, rolle: rolle || null });
                setAdminStatus(res.message || res.error || '');
                lastBrukere(); // ved feil nullstilles nedtrekket til faktisk rolle
            };
            // Superadmin oppretter bruker uten passord — vedkommende setter sitt
            // eget via «Glemt passord» (koden på e-post beviser eierskapet).
            const opprettBruker = async () => {
                const brukernavn = nyBrukernavn.trim();
                const epost = nyEpost.trim();
                if (!brukernavn || !epost) { setAdminStatus('Fyll inn brukernavn og e-post'); return; }
                const res = await adminApiJson('/editor/ny-bruker', {
                    brukernavn, epost, rolle: nyBrukerRolle || null
                });
                setAdminStatus(res.message || res.error || '');
                if (res.success) {
                    setNyBrukernavn('');
                    setNyEpost('');
                    setNyBrukerRolle('');
                    lastBrukere();
                }
            };

            const startAddSong = async () => {
                if (!bokRedigerbar) {
                    alert('Denne boka er skrivebeskyttet. Bytt til en redigerbar bok for å legge til sanger.');
                    return;
                }
                if (!isLoggedIn) {
                    // Sangleder/operatør (og oppover) kan legge til uten delt passord
                    const tilgang = await sjekkSangplanTilgang();
                    if (!tilgang.kan_legge_til_sang) {
                        setShowLoginDialog(true);
                        return;
                    }
                }
                setNewSong({ nummer: '', tittel: '', toneart: '', tekst: '', tekstforfatter: '', copyright: '' });
                setAddError('');
                setAddMode(true);
            };

            const saveNewSong = async () => {
                if (!newSong.tittel.trim()) { setAddError('Tittel er påkrevd'); return; }
                if (!newSong.tekst.trim()) { setAddError('Tekst er påkrevd'); return; }
                setAddSaving(true);
                setAddError('');
                try {
                    const response = await fetch('/api/song/add', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ ...newSong, bok: bokId })
                    });
                    const data = await response.json();
                    if (data.success) {
                        const addedSong = { ...newSong, nummer: data.nummer };
                        const updatedSongs = [...songs, addedSong];
                        setSongs(updatedSongs);
                        localStorage.setItem(CACHE_KEY, JSON.stringify(updatedSongs));
                        localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());
                        setAddMode(false);
                        setSelectedSong(addedSong);
                        setVerses(parseVerses(addedSong.tekst));
                        if (data.venter_godkjenning) {
                            alert('Sangen er lagt til og kan brukes nå.\nEn redaktør godkjenner den i etterkant.');
                        }
                    } else {
                        setAddError(data.error || 'Kunne ikke legge til');
                    }
                } catch (err) {
                    console.error('Add error:', err);
                    setAddError('Nettverksfeil');
                } finally {
                    setAddSaving(false);
                }
            };

            // Edit song handlers
            const startEdit = async (song) => {
                if (!bokRedigerbar) {
                    alert('Denne boka er skrivebeskyttet og kan ikke redigeres.');
                    return;
                }
                if (!isLoggedIn) {
                    // Redigering krever redaktør/admin — sangleder/operatør er ikke nok
                    const tilgang = await sjekkSangplanTilgang();
                    const roller = (tilgang.rolle || '').split(',');
                    if (!harRedigeringsRolle(roller)) {
                        setPendingEditSong(song);
                        setShowLoginDialog(true);
                        return;
                    }
                }
                setEditMode(true);
                setEditSong({ ...song });
            };

            const [saving, setSaving] = useState(false);
            const [saveError, setSaveError] = useState('');

            const saveEdit = async () => {
                setSaving(true);
                setSaveError('');

                try {
                    const response = await fetch('/api/song/update', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ ...editSong, bok: bokId })
                    });

                    const data = await response.json();

                    if (data.success) {
                        // Update local state and cache
                        const updatedSongs = songs.map(s => s.nummer === editSong.nummer ? editSong : s);
                        setSongs(updatedSongs);
                        localStorage.setItem(CACHE_KEY, JSON.stringify(updatedSongs));
                        localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());

                        setEditMode(false);
                        setEditSong(null);

                        if (selectedSong?.nummer === editSong.nummer) {
                            setSelectedSong(editSong);
                            setVerses(parseVerses(editSong.tekst));
                        }
                    } else {
                        setSaveError(data.error || 'Kunne ikke lagre');
                    }
                } catch (err) {
                    console.error('Save error:', err);
                    setSaveError('Nettverksfeil - kunne ikke lagre');
                } finally {
                    setSaving(false);
                }
            };

            const cancelEdit = () => {
                setEditMode(false);
                setEditSong(null);
            };

            // Auto-scroll to current verse
            const verseRefs = useRef([]);
            const bibleVerseRefs = useRef([]);
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

            useEffect(() => {
                if (isTouchDevice) return; // Don't auto-scroll on touch devices
                if (verseRefs.current[currentVerse]) {
                    verseRefs.current[currentVerse].scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, [currentVerse]);

            // Reset bible verse refs when verses change
            useEffect(() => {
                bibleVerseRefs.current = [];
            }, [bibleVerses]);

            // Auto-scroll to current Bible verse (skip on touch devices to avoid scroll hijacking)
            useEffect(() => {
                if (isTouchDevice) return;
                setTimeout(() => {
                    if (bibleVerseRefs.current[currentBibleVerse]) {
                        bibleVerseRefs.current[currentBibleVerse].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }
                }, 10);
            }, [currentBibleVerse]);

            // Scroll to first song when search changes
            useEffect(() => {
                if (searchQuery.length >= 1 && filteredSongs.length > 0) {
                    // Small delay to let the list re-render
                    setTimeout(() => {
                        const firstSong = document.querySelector('[data-song-nummer]');
                        if (firstSong) {
                            firstSong.scrollIntoView({ block: 'start', behavior: 'smooth' });
                        }
                    }, 50);
                }
            }, [searchQuery, filteredSongs.length]);

            // Go to song by number
            const goToSongNumber = (nummer) => {
                const song = songs.find(s => s.nummer === nummer);
                if (song) {
                    selectSong(song);
                    setShowGotoDialog(false);
                    setGotoNumber('');
                    // Focus the song button
                    setTimeout(() => {
                        const songBtn = document.querySelector(`[data-song-nummer="${nummer}"]`);
                        if (songBtn) {
                            songBtn.focus();
                            songBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                        }
                    }, 50);
                }
            };

            // Navigate verse without sending to display
            const previewVerse = (index) => {
                setCurrentVerse(index);
            };

            // «(5:21)» foran et vers er det NORSKE versnummeret fra
            // 1930-nummereringen — filene våre er KJV-nummerert, så prefikset
            // forteller hvor verset står i en trykt 1930-bibel. Det fantes kun
            // i den norske teksten (1359 vers, 19 bøker; aldri i KJV), men ble
            // merket «KJV» — direkte misvisende, og enda tydeligere når begge
            // språk står side om side.
            // Tallet hører hjemme i REFERANSEN, ikke midt i verset: det leses ut
            // med versEtikett() og settes i «Salmene 3:2», mens teksten står ren.
            const cleanVerseText = useCallback((text) => {
                if (!text) return '';
                return text.replace(/^\(\d+:\d+\)\s*/, '');
            }, []);

            // Versnummeret i OVERSETTELSENS EGEN nummerering. 1930-fila er
            // KJV-nummerert, men bærer sitt eget nummer som «(3:2)» foran de
            // 1354 versene der de spriker (37 av dem skifter også kapittel, som
            // 3. Mos 6:2 = 5:21) — KJV-fila har aldri slikt prefiks, så samme
            // regel gir riktig etikett for begge språk.
            const versEtikett = useCallback((data, bokKode, kap, versNum) => {
                const raa = data?.bible?.[bokKode]?.[kap]?.[versNum] || '';
                const m = /^\s*\((\d+):(\d+)\)/.exec(raa);
                return m ? `${m[1]}:${m[2]}` : `${kap}:${versNum}`;
            }, []);

            // Kun for lokal forhåndsvisning. Escaper HTML før [..]→<i> slik at
            // teksten aldri kan smugle inn markup. Skjermen mottar råtekst over
            // socketen og gjør samme transformasjon selv (skjerm.js).
            const renderVerseHtml = useCallback((text) => {
                if (!text) return '';
                const escaped = cleanVerseText(text)
                    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                return escaped.replace(/\[([^\]]+)\]/g, '<i>$1</i>');
            }, [cleanVerseText]);

            // Felles payload-bygger for alle show_bible-utsendelser. Andrespråket
            // legges på som text2 — et TILLEGGSFELT, så en skjerm som ikke er
            // lastet på nytt bare ignorerer det og viser ett språk som før.
            // Paringen er gratis: begge JSON-filene bruker KJV-nummerering, så
            // vers N i den ene er vers N i den andre (ingen mapping-oppslag).
            const byggBibelLast = useCallback((bokKode, boknavn, kap, versNum, tekst, primaerData) => {
                const last = { book: boknavn, chapter: kap, verse: versNum, text: tekst };
                // book/chapter/verse beholdes for skjermer som ikke er lastet på
                // nytt (de bygger referansen selv). «ref» er den nye fasiten:
                // masterspråkets egen nummerering, som i en trykt bibel.
                const kilde = primaerData || bibleData;
                last.ref = `${boknavn} ${versEtikett(kilde, bokKode, kap, versNum)}`;
                // Salme-overskriften er ikke et vers (den ligger under «titler»),
                // og hører hjemme over vers 1 — ikke gjentatt på hvert vers.
                if (versNum === 1) {
                    const t = kilde?.titler?.[bokKode]?.[String(kap)];
                    if (t) last.tittel = t;
                }
                // Andrespråket brukes KUN når den lastede alt-fila er gjeldende
                // partner for aktiv versjon. Det utelukker både samme-språk-
                // dubletter og stale data etter bytte til en versjon uten
                // parallellstøtte (f.eks. tysk, som er nativt nummerert). Er den
                // ferske fila ennå ikke lastet, står andre blokk tom et øyeblikk.
                if (parallell && partner && altBibleData && altBibleData.lang === partner) {
                    const alt = altBibleData.data.bible?.[bokKode]?.[kap]?.[versNum];
                    if (alt) last.text2 = cleanVerseText(alt);
                    // Egen referanse per blokk: samme linje har ulikt nummer i de
                    // to nummereringene (norsk 3:2 ↔ KJV 3:1), så én felles ref
                    // ville vært feil for den ene blokka uansett hvilken vi valgte.
                    const altNavn = altBibleData.data.names?.[bokKode] || boknavn;
                    last.ref2 = `${altNavn} ${versEtikett(altBibleData.data, bokKode, kap, versNum)}`;
                    if (versNum === 1) {
                        // 54 salmer har engelsk overskrift uten norsk motstykke —
                        // da står tittellinja kun over den engelske blokka
                        const t2 = altBibleData.data.titler?.[bokKode]?.[String(kap)];
                        if (t2) last.tittel2 = t2;
                    }
                }
                return last;
            }, [parallell, altBibleData, bibleVersion, partner, bibleData, cleanVerseText, versEtikett]);
            // Ref settes under render (ikke i en effekt): versjonsbytte-effekten
            // er deklarert lenger opp og må kunne bruke byggeren når fetchen
            // hennes lander.
            const byggBibelLastRef = useRef(null);
            byggBibelLastRef.current = byggBibelLast;

            // Slås parallell av/på mens et vers står på veggen (grønt nivå),
            // sendes verset på nytt — ellers ville bryteren først fått effekt
            // ved neste vers. Kjører også når andrespråket er ferdig lastet.
            const parallellForrige = useRef(parallell);
            useEffect(() => {
                const endret = parallellForrige.current !== parallell;
                parallellForrige.current = parallell;
                if (!endret && !altBibleData) return;
                if (appMode !== 'bible' || bibleNavLevelRef.current !== 2) return;
                if (!socket || !bibleData || !bibleVerses.length) return;
                const bok = selectedBookRef.current;
                if (!bok) return;
                const idx = Math.min(currentBibleVerseRef.current, bibleVerses.length - 1);
                const last = byggBibelLast(bok, bibleData.names[bok], selectedChapterRef.current,
                                           bibleVerses[idx].num, cleanVerseText(bibleVerses[idx].text));
                socket.emit('show_bible', last);
                sisteVisning.current.bible = last;
            }, [parallell, altBibleData]);

            // Memoize cleaned bible verses for performance
            const cleanedBibleVerses = useMemo(() => {
                return bibleVerses.map(v => {
                    // «nr» er etiketten operatøren ser og velger etter — i
                    // oversettelsens egen nummerering. Full «kap:vers» når den
                    // spriker (3. Mos 6:2 heter 5:21 i 1930), ellers bare tallet.
                    const e = versEtikett(bibleData, selectedBook, selectedChapter, v.num);
                    return {
                        ...v,
                        nr: e === `${selectedChapter}:${v.num}` ? String(v.num) : e,
                        cleanText: cleanVerseText(v.text)
                    };
                });
            }, [bibleVerses, cleanVerseText, versEtikett, bibleData, selectedBook, selectedChapter]);

            // Send Bible verse to display
            const showBibleVerse = (index) => {
                if (!bibleVerses[index] || !socket || !bibleData) return;
                setCurrentBibleVerse(index);
                // Råtekst — skjermen escaper og gjør [..]→kursiv selv
                const bibelLast = byggBibelLast(
                    selectedBook, bibleData.names[selectedBook], selectedChapter,
                    bibleVerses[index].num, cleanVerseText(bibleVerses[index].text));
                socket.emit('show_bible', bibelLast);
                sisteVisning.current.bible = bibelLast;
                setQrVisible(false);
                announce(`${bibelLast.ref} vises på skjerm`);
            };

            // Refs for Bible navigation
            const bibleVersesRef = useRef(bibleVerses);
            const selectedBookRef = useRef(selectedBook);
            const selectedChapterRef = useRef(selectedChapter);
            const currentBibleVerseRef = useRef(currentBibleVerse);

            useEffect(() => { bibleVersesRef.current = bibleVerses; }, [bibleVerses]);
            useEffect(() => { selectedBookRef.current = selectedBook; }, [selectedBook]);
            useEffect(() => { selectedChapterRef.current = selectedChapter; }, [selectedChapter]);
            useEffect(() => { currentBibleVerseRef.current = currentBibleVerse; }, [currentBibleVerse]);

            // ===== Presentasjon (PowerPoint) =====
            // Hent gjeldende liste (uten innboks-skann) når fanen åpnes
            useEffect(() => {
                if (appMode !== 'presentasjon') { setPresVist(false); setPresLiveSlide(null); return; }
                fetch('/presentasjoner')
                    .then(res => res.json())
                    .then(data => setPresListe(data.presentasjoner || []))
                    .catch(() => {});
            }, [appMode]);

            // Fjernkontroll-kode (/presenter): lages straks rommet er oppe og
            // lever med økta — uavhengig av aktiv fane, så fjernkontrollen
            // virker selv om operatøren står i Sanger/Bibel. Idempotent på
            // serveren; nytt rom (reconnect) gir ny kode.
            useEffect(() => {
                if (!socket || !connected || !roomCode) return;
                socket.emit('sett_presenterkode', {});
            }, [socket, connected, roomCode]);

            // QR for /presenter?kode=… — vises kun her i Vis, aldri på storskjermen.
            // Liten i venstre boks; stor i høyreruta til en presentasjon velges.
            useEffect(() => {
                if (appMode !== 'presentasjon' || !presenterKode) return;
                const url = `${window.location.origin}/presenter?kode=${presenterKode}`;
                const farger = { dark: '#000000', light: '#ffffff' };
                const canvas = document.getElementById('presenter-qr-canvas');
                if (canvas) {
                    QRCode.toCanvas(canvas, url, { width: 88, margin: 1, color: farger },
                        (err) => { if (err) console.error('QR code error:', err); });
                }
                const stor = document.getElementById('presenter-qr-stor');
                if (stor) {
                    QRCode.toCanvas(stor, url, { width: 260, margin: 1, color: farger },
                        (err) => { if (err) console.error('QR code error:', err); });
                }
            }, [appMode, presenterKode, valgtPres]);

            // Talerens kontekst → speil i UI: posisjonen følger fjernkontrollen.
            // Nivå: gronn = grønn markør (salen følger), gul = frys (salen ser
            // skjerm-lysbildet, gul markør + «skjerm: N»), rod = ikke i salen.
            useEffect(() => {
                if (!presStatus) return;
                if (valgtPres && String(presStatus.pid) === String(valgtPres.id)) {
                    setPresSlide(presStatus.slide);
                    setPresVist(presStatus.nivaa === 'gronn');
                    setPresLiveSlide(presStatus.nivaa === 'gronn' ? presStatus.slide
                        : presStatus.nivaa === 'gul' ? presStatus.skjerm : null);
                }
            }, [presStatus]);

            // Send fjernkontroll-lenken til avsenderen av valgt presentasjon
            const sendPresenterLenke = () => {
                if (!socket || !valgtPres) return;
                setPresenterEpost({ ok: true, tekst: 'Sender …' });
                socket.emit('presenter_epost', { pid: valgtPres.id });
            };
            useEffect(() => { setPresenterEpost(null); }, [valgtPres]);

            // «Søk etter presentasjon»: skann innboksen på forespørsel
            const sokPresentasjoner = async () => {
                setPresSoker(true);
                setPresFeil('');
                try {
                    const res = await fetch('/presentasjoner/sok', { method: 'POST' });
                    const data = await res.json();
                    if (data.success) {
                        setPresListe(data.presentasjoner || []);
                        if (!(data.presentasjoner || []).length) setPresFeil('Ingen presentasjoner i innboksen.');
                    } else {
                        setPresFeil(data.error || 'Søket feilet');
                    }
                } catch {
                    setPresFeil('Kunne ikke nå serveren');
                } finally {
                    setPresSoker(false);
                }
            };

            // Velg presentasjon — konverter først om nødvendig
            const velgPresentasjon = async (p) => {
                setPresFeil('');
                let pres = p;
                if (p.status !== 'konvertert' || !p.lysbilder) {
                    setPresKonverterer(p.id);
                    try {
                        const res = await fetch(`/presentasjoner/${p.id}/konverter`, { method: 'POST' });
                        const data = await res.json();
                        if (!data.success) {
                            setPresFeil(data.error || 'Konvertering feilet');
                            return;
                        }
                        pres = { ...p, status: 'konvertert', lysbilder: data.lysbilder };
                        setPresListe(prev => prev.map(x => x.id === p.id ? pres : x));
                    } catch {
                        setPresFeil('Kunne ikke nå serveren');
                        return;
                    } finally {
                        setPresKonverterer(null);
                    }
                }
                setValgtPres(pres);
                setPresSlide(1);
                setPresVist(false);
                setPresLiveSlide(null);
                // Klargjør økta: talerens kontekst settes og ventende
                // token-telefoner kobles inn — skjermen røres ikke.
                socket?.emit('pres_klargjor', { pid: pres.id });
                if (window.innerWidth < 768) setMobileView('content');
            };

            const slettPresentasjon = async (p) => {
                if (!window.confirm(`Slette «${p.navn}»?`)) return;
                setPresFeil('');
                try {
                    const res = await fetch(`/presentasjoner/${p.id}`, { method: 'DELETE' });
                    const data = await res.json();
                    if (data.success) {
                        setPresListe(data.presentasjoner || []);
                        if (valgtPres?.id === p.id) { setValgtPres(null); setPresVist(false); }
                    } else {
                        // Sletting krever innlogging (eneste presentasjons-ruta
                        // som gjør det) — uten dette så et avslag ut som at
                        // ingenting skjedde.
                        setPresFeil(data.error || 'Kunne ikke slette');
                    }
                } catch {
                    setPresFeil('Kunne ikke nå serveren');
                }
            };

            // Vis et lysbilde på skjermen (1-basert, klemmes til gyldig område)
            const visLysbilde = (pres, n) => {
                if (!pres || !pres.lysbilder) return;
                const slide = Math.max(1, Math.min(n, pres.lysbilder));
                setPresSlide(slide);
                setPresVist(true);
                setPresLiveSlide(slide);
                if (socket) {
                    const bildeLast = {
                        pid: pres.id,
                        slide,
                        total: pres.lysbilder,
                        url: `/presentasjoner/${pres.id}/slide/${slide}`
                    };
                    socket.emit('show_image', bildeLast);
                    sisteVisning.current.presentasjon = bildeLast;
                    bibelIkkePaaSkjerm();
                }
                setQrVisible(false);
                announce(`Lysbilde ${slide} av ${pres.lysbilder} vises på skjerm`);
            };

            // Hold valgt miniatyr synlig i den vertikale lysbildelisten når
            // man blar. block:'center' klemmer mot kantene: markøren vandrer
            // nedover fra toppen til den når midten, står så fast der mens
            // listen glir forbi, og vandrer mot bunnen på de siste slidene.
            // behavior:'smooth' — rask tastegjentakelse re-targeter den
            // pågående animasjonen, så listen glir i stedet for å hoppe.
            useLayoutEffect(() => {
                if (appMode !== 'presentasjon' || !valgtPres) return;
                const valgt = document.querySelector('#pres-lysbildeliste [aria-selected="true"]');
                valgt?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
            }, [presSlide, valgtPres, appMode]);

            // Bla i gul (forhåndsvisning) uten å røre skjermen
            const blaPrivat = (retning) => {
                if (!valgtPres) return;
                setPresSlide(s => Math.max(1, Math.min(s + retning, valgtPres.lysbilder)));
            };

            // Bla utenfor grønn modus — avsløring skjer ALDRI som bieffekt:
            // · gul mens lysbildet står på skjermen: lokal privat titting
            // · svart skjerm / sang på skjermen: kontekst-bla via serveren,
            //   så talerens telefon og gul markør følger hverandre
            const blaIkkeGronn = (retning) => {
                if (!valgtPres) return;
                if (presLiveSlide) blaPrivat(retning);
                else socket?.emit('presenter_bla', { retning });
            };

            // Store ◀▶-knapper: i grønn styrer de skjermen; ellers blar de
            // privat (samme regel som fjernkontrollen — vis med ↓/Enter)
            const presNeste = () => {
                if (!valgtPres) return;
                if (presVist) visLysbilde(valgtPres, presSlide + 1);
                else blaIkkeGronn(1);
            };
            const presForrige = () => {
                if (!valgtPres) return;
                if (presVist) visLysbilde(valgtPres, presSlide - 1);
                else blaIkkeGronn(-1);
            };

            // Klikk på forhåndsvisningsbildet: toggle salens visning — som
            // talerens tap. Serveren avgjør (live → skjul; ellers vis, med
            // sperre hvis sang/bibel eier skjermen).
            const klikkPreviewBilde = () => {
                if (!valgtPres || !socket) return;
                socket.emit('presenter_blank', {});
            };
            // Sperre-tekst for modalen (kun når statusen gjelder valgt presentasjon)
            const presSperre = (presStatus && valgtPres
                && String(presStatus.pid) === String(valgtPres.id)) ? presStatus.sperre : null;

            // «/»-hopp til første/siste — samme grønn/gul/svart-regler
            const presHoppTil = (n) => {
                if (!valgtPres) return;
                if (presVist) visLysbilde(valgtPres, n);
                else if (presLiveSlide) setPresSlide(Math.max(1, Math.min(n, valgtPres.lysbilder)));
                else socket?.emit('presenter_bla', { til: n });
            };

            const tomPresentasjonSkjerm = () => {
                socket?.emit('clear');
                setPresVist(false);
                setPresLiveSlide(null);
            };

            // Keyboard navigation
            useEffect(() => {
                const handleKeyDown = (e) => {
                    // Nedtrekkslister (f.eks. sangbok-velgeren) beholder native
                    // piltast-oppførsel — ikke kapre ↑/↓ der
                    if (e.target.tagName === 'SELECT') return;
                    const isInInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';

                    // Help dialog toggle with "?" key
                    if ((e.key === '?' || (e.shiftKey && e.key === '/')) && !isInInput) {
                        e.preventDefault();
                        setShowHelp(prev => !prev);
                        return;
                    }

                    // Close help with Escape
                    if (e.key === 'Escape' && showHelp) {
                        e.preventDefault();
                        setShowHelp(false);
                        return;
                    }

                    // Edit mode shortcuts
                    if (editMode) {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEdit();
                            return;
                        }
                        if (e.key === 'Enter' && e.ctrlKey) {
                            e.preventDefault();
                            saveEdit();
                            return;
                        }
                        return;
                    }

                    // Flerlinjefelt (teksten i «Ny sang») beholder Enter og
                    // piltastene — den globale navigasjonen under gjelder
                    // søkefelt og lister, ikke fritekst.
                    if (e.target.tagName === 'TEXTAREA') return;

                    // Bible mode keyboard navigation
                    if (appMode === 'bible') {
                        // Fanelinjen håndterer selv sine taster (onKeyDown med
                        // stopPropagation) — ingen fane-håndtering her.

                        // Versjonsknappene (1930/KJV/?) håndterer selv sine taster
                        // i onKeyDown med stopPropagation — ingen håndtering her.

                        // "/" key focuses search input
                        if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
                            e.preventDefault();
                            bibleSearchInputRef.current?.focus();
                            return;
                        }

                        // Detect where focus is
                        const focusedBook = document.activeElement?.getAttribute('data-bible-book');
                        const focusedChapter = document.activeElement?.getAttribute('data-bible-chapter');
                        const isInSearchInput = document.activeElement === bibleSearchInputRef.current;

                        // Handle navigation from bible search input
                        if (isInSearchInput) {
                            if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                document.activeElement.blur();
                                document.getElementById('bible-tab')?.focus();
                                return;
                            }
                            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                                e.preventDefault();
                                document.activeElement.blur();
                                const firstBook = document.querySelector('[data-bible-book]');
                                if (firstBook) {
                                    firstBook.focus();
                                    firstBook.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                }
                                return;
                            }
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                // Go back to last focused book, or bible tab
                                const targetBook = lastFocusedBookRef.current;
                                setTimeout(() => {
                                    if (targetBook) {
                                        const bookBtn = document.querySelector(`[data-bible-book="${targetBook}"]`);
                                        if (bookBtn) {
                                            bookBtn.focus();
                                            bookBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                            return;
                                        }
                                    }
                                    document.getElementById('bible-tab')?.focus();
                                }, 10);
                                return;
                            }
                            return;
                        }

                        // STATE 1: Focus is on a book button
                        if (focusedBook) {
                            const allBooks = Array.from(document.querySelectorAll('[data-bible-book]'));
                            const idx = allBooks.findIndex(b => b.getAttribute('data-bible-book') === focusedBook);

                            if (e.key === 'ArrowDown' && idx < allBooks.length - 1) {
                                e.preventDefault();
                                const nextBook = allBooks[idx + 1];
                                const nextBookCode = nextBook.getAttribute('data-bible-book');
                                nextBook.focus();
                                nextBook.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                // Update selected book
                                setSelectedBook(nextBookCode);
                                setSelectedChapter(null);
                                setBibleVerses([]);
                                setCurrentBibleVerse(0);
                                bibleNavLevelRef.current = 0;
                                setBibleNavLevel(0);
                                return;
                            }
                            if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                if (idx > 0) {
                                    const prevBook = allBooks[idx - 1];
                                    const prevBookCode = prevBook.getAttribute('data-bible-book');
                                    prevBook.focus();
                                    prevBook.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                    // Update selected book
                                    setSelectedBook(prevBookCode);
                                    setSelectedChapter(null);
                                    setBibleVerses([]);
                                    setCurrentBibleVerse(0);
                                    bibleNavLevelRef.current = 0;
                                    setBibleNavLevel(0);
                                } else {
                                    // First book - go to search input
                                    bibleSearchInputRef.current?.focus();
                                }
                                return;
                            }
                            if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setTimeout(() => {
                                    const firstChapter = document.querySelector('[data-bible-chapter]');
                                    if (firstChapter) {
                                        firstChapter.focus();
                                        // Select first chapter
                                        const chapterNum = firstChapter.getAttribute('data-bible-chapter');
                                        setSelectedChapter(chapterNum);
                                        if (bibleData && selectedBookRef.current) {
                                            const verseData = bibleData.bible[selectedBookRef.current][chapterNum];
                                            if (verseData) {
                                                const verseArray = Object.keys(verseData).map(v => ({
                                                    num: parseInt(v),
                                                    text: verseData[v]
                                                })).sort((a, b) => a.num - b.num);
                                                setBibleVerses(verseArray);
                                                setCurrentBibleVerse(0);
                                                bibleNavLevelRef.current = 0;
                                                setBibleNavLevel(0);
                                            }
                                        }
                                    }
                                }, 50);
                                return;
                            }
                            // Arrow left - go to filter input
                            if (e.key === 'ArrowLeft') {
                                e.preventDefault();
                                lastFocusedBookRef.current = focusedBook;
                                bibleSearchInputRef.current?.focus();
                                return;
                            }
                            // Escape - go to bible tab
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                document.getElementById('bible-tab')?.focus();
                                return;
                            }
                            // Number keys - jump to chapter
                            if (/^[0-9]$/.test(e.key) && bibleData && selectedBookRef.current) {
                                e.preventDefault();
                                const newInput = bibleNumberInputRef.current + e.key;
                                bibleNumberInputRef.current = newInput;

                                if (bibleNumberTimeoutRef.current) {
                                    clearTimeout(bibleNumberTimeoutRef.current);
                                }

                                bibleNumberTimeoutRef.current = setTimeout(() => {
                                    const chapterNum = newInput;
                                    const bookData = bibleData.bible[selectedBookRef.current];
                                    if (bookData && bookData[chapterNum]) {
                                        setSelectedChapter(chapterNum);
                                        const verseData = bookData[chapterNum];
                                        const verseArray = Object.keys(verseData).map(v => ({
                                            num: parseInt(v),
                                            text: verseData[v]
                                        })).sort((a, b) => a.num - b.num);
                                        setBibleVerses(verseArray);
                                        setCurrentBibleVerse(0);
                                        bibleNavLevelRef.current = 0;
                                        setBibleNavLevel(0);
                                        setTimeout(() => {
                                            const chapterBtn = document.querySelector(`[data-bible-chapter="${chapterNum}"]`);
                                            if (chapterBtn) {
                                                chapterBtn.focus();
                                                chapterBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                            }
                                        }, 50);
                                    }
                                    bibleNumberInputRef.current = '';
                                }, 800);
                                return;
                            }
                            return;
                        }

                        // STATE 2: Focus is on a chapter button
                        if (focusedChapter) {
                            const allChapters = Array.from(document.querySelectorAll('[data-bible-chapter]'));
                            const idx = allChapters.findIndex(c => c.getAttribute('data-bible-chapter') === focusedChapter);

                            // Helper to select chapter and load verses
                            const selectChapter = (ch) => {
                                const chapterNum = ch.getAttribute('data-bible-chapter');
                                setSelectedChapter(chapterNum);
                                if (bibleData && selectedBookRef.current) {
                                    const verseData = bibleData.bible[selectedBookRef.current][chapterNum];
                                    if (verseData) {
                                        const verseArray = Object.keys(verseData).map(v => ({
                                            num: parseInt(v),
                                            text: verseData[v]
                                        })).sort((a, b) => a.num - b.num);
                                        setBibleVerses(verseArray);
                                        setCurrentBibleVerse(0);
                                        bibleNavLevelRef.current = 0;
                                        setBibleNavLevel(0);
                                        // Scroll verse list to top
                                        setTimeout(() => {
                                            const verseList = document.querySelector('.bible-verse-list');
                                            if (verseList) verseList.scrollTop = 0;
                                        }, 50);
                                    }
                                }
                            };

                            if (e.key === 'ArrowDown' && idx < allChapters.length - 1) {
                                e.preventDefault();
                                const nextChapter = allChapters[idx + 1];
                                nextChapter.focus();
                                nextChapter.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                selectChapter(nextChapter);
                                return;
                            }
                            if (e.key === 'ArrowUp' && idx > 0) {
                                e.preventDefault();
                                const prevChapter = allChapters[idx - 1];
                                prevChapter.focus();
                                prevChapter.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                selectChapter(prevChapter);
                                return;
                            }
                            if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                // Blur so verse navigation takes over, and set to level 1 (yellow)
                                document.activeElement.blur();
                                bibleNavLevelRef.current = 1;
                                setBibleNavLevel(1);
                                setCurrentBibleVerse(0);
                                return;
                            }
                            if (e.key === 'ArrowLeft' || e.key === 'Escape') {
                                e.preventDefault();
                                setSelectedChapter(null);
                                setBibleVerses([]);
                                setCurrentBibleVerse(0);
                                bibleNavLevelRef.current = 0;
                                setBibleNavLevel(0);
                                bibleNumberInputRef.current = '';
                                const bookBtn = document.querySelector(`[data-bible-book="${selectedBookRef.current}"]`);
                                if (bookBtn) {
                                    bookBtn.focus();
                                    bookBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                }
                                return;
                            }
                            // Number keys - jump to verse (when chapter is selected and has verses)
                            if (/^[0-9]$/.test(e.key)) {
                                e.preventDefault();
                                const newInput = bibleNumberInputRef.current + e.key;
                                bibleNumberInputRef.current = newInput;

                                if (bibleNumberTimeoutRef.current) {
                                    clearTimeout(bibleNumberTimeoutRef.current);
                                }

                                bibleNumberTimeoutRef.current = setTimeout(() => {
                                    const verseNum = parseInt(newInput);
                                    // Find verse index by verse number
                                    const verseIdx = bibleVersesRef.current.findIndex(v => v.num === verseNum);
                                    if (verseIdx >= 0) {
                                        // Blur chapter button to switch to verse navigation
                                        document.activeElement.blur();
                                        setCurrentBibleVerse(verseIdx);
                                        bibleNavLevelRef.current = 1;
                                        setBibleNavLevel(1);
                                    }
                                    bibleNumberInputRef.current = '';
                                }, 800);
                                return;
                            }
                            return;
                        }

                        // STATE 3: No left-panel focus - handle verse navigation
                        if (bibleVersesRef.current.length > 0) {
                            // Arrow up/down - navigate verses
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                if (currentBibleVerseRef.current < bibleVersesRef.current.length - 1) {
                                    const newIdx = currentBibleVerseRef.current + 1;
                                    setCurrentBibleVerse(newIdx);
                                    if (bibleNavLevelRef.current === 2) {
                                        showBibleVerse(newIdx);
                                    }
                                }
                                return;
                            }
                            if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                if (currentBibleVerseRef.current > 0) {
                                    const newIdx = currentBibleVerseRef.current - 1;
                                    setCurrentBibleVerse(newIdx);
                                    if (bibleNavLevelRef.current === 2) {
                                        showBibleVerse(newIdx);
                                    }
                                }
                                return;
                            }
                            // Arrow right - activate display (go to green)
                            if (e.key === 'ArrowRight') {
                                e.preventDefault();
                                if (bibleNavLevelRef.current < 2) {
                                    bibleNavLevelRef.current = 2;
                                    setBibleNavLevel(2);
                                    showBibleVerse(currentBibleVerseRef.current);
                                }
                                return;
                            }
                            // Enter/Space - toggle between yellow (preview) and green (active)
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (bibleNavLevelRef.current === 2) {
                                    // Green -> Yellow (back to preview)
                                    bibleNavLevelRef.current = 1;
                                    setBibleNavLevel(1);
                                } else {
                                    // Yellow -> Green (activate display)
                                    bibleNavLevelRef.current = 2;
                                    setBibleNavLevel(2);
                                    showBibleVerse(currentBibleVerseRef.current);
                                }
                                return;
                            }
                            // Arrow left - go back levels
                            if (e.key === 'ArrowLeft') {
                                e.preventDefault();
                                if (bibleNavLevelRef.current === 2) {
                                    bibleNavLevelRef.current = 1;
                                    setBibleNavLevel(1);
                                } else {
                                    // Go back to chapter list
                                    bibleNavLevelRef.current = 0;
                                    setBibleNavLevel(0);
                                    if (socketRef.current) socketRef.current.emit('clear');
                                    const chapterBtn = document.querySelector(`[data-bible-chapter="${selectedChapterRef.current}"]`);
                                    if (chapterBtn) {
                                        chapterBtn.focus();
                                        chapterBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                    }
                                }
                                return;
                            }
                            // Escape - go back
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                if (bibleNavLevelRef.current > 0) {
                                    bibleNavLevelRef.current = 0;
                                    setBibleNavLevel(0);
                                    if (socketRef.current) socketRef.current.emit('clear');
                                    const chapterBtn = document.querySelector(`[data-bible-chapter="${selectedChapterRef.current}"]`);
                                    if (chapterBtn) {
                                        chapterBtn.focus();
                                        chapterBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                    }
                                } else {
                                    // Clear chapter, go to book
                                    setSelectedChapter(null);
                                    setBibleVerses([]);
                                    const bookBtn = document.querySelector(`[data-bible-book="${selectedBookRef.current}"]`);
                                    if (bookBtn) {
                                        bookBtn.focus();
                                        bookBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                    }
                                }
                                return;
                            }
                            // Home/End
                            if (e.key === 'Home') {
                                e.preventDefault();
                                setCurrentBibleVerse(0);
                                if (bibleNavLevelRef.current === 2) showBibleVerse(0);
                                return;
                            }
                            if (e.key === 'End') {
                                e.preventDefault();
                                const last = bibleVersesRef.current.length - 1;
                                setCurrentBibleVerse(last);
                                if (bibleNavLevelRef.current === 2) showBibleVerse(last);
                                return;
                            }
                            // Number keys - jump to verse
                            if (/^[0-9]$/.test(e.key)) {
                                e.preventDefault();
                                const newInput = bibleNumberInputRef.current + e.key;
                                bibleNumberInputRef.current = newInput;

                                // Clear previous timeout
                                if (bibleNumberTimeoutRef.current) {
                                    clearTimeout(bibleNumberTimeoutRef.current);
                                }

                                // Jump to verse after short delay
                                bibleNumberTimeoutRef.current = setTimeout(() => {
                                    const verseNum = parseInt(newInput);
                                    // Find verse index by verse number
                                    const verseIdx = bibleVersesRef.current.findIndex(v => v.num === verseNum);
                                    if (verseIdx >= 0) {
                                        setCurrentBibleVerse(verseIdx);
                                        if (bibleNavLevelRef.current === 0) {
                                            bibleNavLevelRef.current = 1;
                                            setBibleNavLevel(1);
                                        }
                                        if (bibleNavLevelRef.current === 2) {
                                            showBibleVerse(verseIdx);
                                        }
                                    }
                                    bibleNumberInputRef.current = '';
                                }, 800);
                                return;
                            }
                        }

                        // Blanke ark uten fokus (f.eks. etter klikk på fanen,
                        // som blurres av fanetrumf-effekten): ↓ går inn i
                        // boklisten — valgt bok om den finnes, ellers 1. Mosebok
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const bok = (selectedBookRef.current
                                && document.querySelector(`[data-bible-book="${selectedBookRef.current}"]`))
                                || document.querySelector('[data-bible-book]');
                            if (bok) {
                                bok.focus();
                                bok.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                const code = bok.getAttribute('data-bible-book');
                                if (code && !selectedBookRef.current) setSelectedBook(code);
                            }
                            return;
                        }

                        return; // Don't process song mode shortcuts in Bible mode
                    }

                    // Presentasjonsmodus — samme akser som Bibel-fanen: ↑/↓ blar
                    // lysbilder (i grønn følger skjermen, ellers privat/kontekst),
                    // →/Enter = vis (grønn), Enter i grønn = tilbake til gul.
                    // ←/Esc = ett nivå ned i samme stige som fjernkontrollen:
                    // grønn → gul (frys) → svart skjerm → presentasjonslisten.
                    // Space/PgUp/PgDn blar alltid (presentasjonsklikkere).
                    if (appMode === 'presentasjon') {
                        // Enter/mellomrom på en fokusert knapp skal klikke knappen, ikke kapres
                        const paKnapp = document.activeElement?.tagName === 'BUTTON';

                        // Fra søk-knappen: ↓ går inn i listen, ↑ til PowerPoint-fanen
                        if (document.activeElement?.id === 'pres-sok-btn') {
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                const forste = document.querySelector('[data-pres-id]');
                                if (forste) { forste.focus(); forste.scrollIntoView({ block: 'nearest' }); }
                                return;
                            }
                            if (e.key === 'ArrowUp' || e.key === 'Escape') {
                                e.preventDefault();
                                document.getElementById('presentasjon-tab')?.focus();
                                return;
                            }
                        }

                        // Fokus i presentasjonslisten: ↑/↓ blar, Enter/→ velger
                        const fokusertPresId = document.activeElement?.getAttribute?.('data-pres-id');
                        if (fokusertPresId) {
                            const rader = Array.from(document.querySelectorAll('[data-pres-id]'));
                            const idx = rader.findIndex(el => el.getAttribute('data-pres-id') === fokusertPresId);
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                const neste = rader[idx + 1];
                                if (neste) { neste.focus(); neste.scrollIntoView({ block: 'nearest' }); }
                            } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                const forrige = rader[idx - 1];
                                if (forrige) { forrige.focus(); forrige.scrollIntoView({ block: 'nearest' }); }
                                else document.getElementById('pres-sok-btn')?.focus();
                            } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
                                e.preventDefault();
                                const p = presListe.find(x => String(x.id) === fokusertPresId);
                                if (p) {
                                    velgPresentasjon(p);
                                    // Slipp fokus så piltastene styrer lysbildene med en gang
                                    document.activeElement.blur();
                                }
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                document.getElementById('pres-sok-btn')?.focus();
                            }
                            return;
                        }

                        if (!isInInput && valgtPres) {
                            if (e.key === 'ArrowDown' || e.key === 'PageDown' || (e.key === ' ' && !paKnapp)) {
                                e.preventDefault();
                                if (presVist) visLysbilde(valgtPres, presSlide + 1);
                                else blaIkkeGronn(1);
                            } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                                e.preventDefault();
                                if (presVist) visLysbilde(valgtPres, presSlide - 1);
                                else if (presSlide > 1) blaIkkeGronn(-1);
                            } else if (e.key === 'ArrowRight' || (e.key === 'Enter' && !paKnapp)) {
                                e.preventDefault();
                                if (presVist && e.key === 'Enter') {
                                    setPresVist(false); // Enter i grønn: tilbake til gul (skjermen beholder siste)
                                } else {
                                    visLysbilde(valgtPres, presSlide); // gul → grønn
                                }
                            } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
                                e.preventDefault();
                                if (presVist) {
                                    setPresVist(false); // grønn → gul: skjermen beholder siste viste
                                } else if (presLiveSlide) {
                                    // Gul med lysbilde i salen: → svart skjerm
                                    // (konteksten består — → viser igjen)
                                    tomPresentasjonSkjerm();
                                    announce('Svart skjerm');
                                } else {
                                    // Svart: ut i presentasjonslisten (skjermen røres ikke)
                                    const rad = document.querySelector(`[data-pres-id="${valgtPres.id}"]`) || document.querySelector('[data-pres-id]');
                                    if (rad) { rad.focus(); rad.scrollIntoView({ block: 'nearest' }); }
                                }
                            } else if (e.key === 'Home') {
                                e.preventDefault();
                                presHoppTil(1);
                            } else if (e.key === 'End') {
                                e.preventDefault();
                                presHoppTil(valgtPres.lysbilder);
                            }
                        }
                        return; // Ingen sang-snarveier i presentasjonsmodus
                    }

                    const isInGotoField = e.target.id === 'goto-nummer';
                    const focusedElement = document.activeElement;

                    // Handle goto dialog
                    if (showGotoDialog) {
                        if (e.key === 'Escape') {
                            setShowGotoDialog(false);
                            setGotoNumber('');
                            return;
                        }
                        if (e.key === 'Enter' && gotoNumber) {
                            goToSongNumber(gotoNumber);
                            return;
                        }
                        return;
                    }

                    // ↑ fra søkefeltet: opp i panel-headeren (oppdater-knappen),
                    // som selv går videre opp til Sanger-fanen
                    if (e.key === 'ArrowUp' && isInInput && !isInGotoField) {
                        e.preventDefault();
                        e.target.blur();
                        document.getElementById('refresh-btn')?.focus();
                        return;
                    }

                    // Arrow down or Enter from search field - jump to sort buttons or first song
                    if ((e.key === 'ArrowDown' || e.key === 'Enter') && isInInput && !isInGotoField) {
                        e.preventDefault();
                        e.target.blur();
                        const sortButtons = document.querySelectorAll('.sort-button');
                        if (sortButtons.length > 0) {
                            sortButtons[0].focus();
                        } else {
                            const firstSong = document.querySelector('[data-song-nummer]');
                            if (firstSong) {
                                firstSong.focus();
                                firstSong.scrollIntoView({ block: 'center', behavior: 'smooth' });
                            }
                        }
                        return;
                    }

                    // "/" - focus search
                    if (e.key === '/' && navLevel === 0) {
                        if (!isInInput) {
                            e.preventDefault();
                            searchInputRef.current?.focus();
                            return;
                        }
                    }

                    // "g" - open goto dialog
                    if (e.key === 'g' && !isInInput && navLevel === 0) {
                        e.preventDefault();
                        setShowGotoDialog(true);
                        setTimeout(() => gotoInputRef.current?.focus(), 100);
                        return;
                    }

                    // "s" - focus sort buttons
                    if (e.key === 's' && !isInInput && navLevel === 0) {
                        e.preventDefault();
                        const sortButtons = document.querySelectorAll('.sort-button');
                        if (sortButtons.length > 0) {
                            sortButtons[0].focus();
                        }
                        return;
                    }

                    // "t" - jump to top of list
                    if (e.key === 't' && !isInInput && navLevel === 0) {
                        e.preventDefault();
                        const firstSong = document.querySelector('[data-song-nummer]');
                        if (firstSong) {
                            firstSong.focus();
                            firstSong.scrollIntoView({ block: 'center', behavior: 'smooth' });
                            const songNummer = firstSong.getAttribute('data-song-nummer');
                            const song = filteredSongs.find(s => s.nummer === songNummer);
                            if (song) selectSong(song);
                        }
                        return;
                    }

                    // "b" - jump to bottom of list
                    if (e.key === 'b' && !isInInput && navLevel === 0) {
                        e.preventDefault();
                        const allSongs = document.querySelectorAll('[data-song-nummer]');
                        const lastSong = allSongs[allSongs.length - 1];
                        if (lastSong) {
                            lastSong.focus();
                            lastSong.scrollIntoView({ block: 'center', behavior: 'smooth' });
                            const songNummer = lastSong.getAttribute('data-song-nummer');
                            const song = filteredSongs.find(s => s.nummer === songNummer);
                            if (song) selectSong(song);
                        }
                        return;
                    }

                    // "e" - edit selected song
                    if (e.key === 'e' && !isInInput && selectedSongRef.current) {
                        e.preventDefault();
                        startEdit(selectedSongRef.current);
                        return;
                    }

                    // "r" - refresh songs from server
                    if (e.key === 'r' && !isInInput) {
                        e.preventDefault();
                        loadSongs(true);
                        return;
                    }

                    // Escape - clear search or go back
                    if (e.key === 'Escape') {
                        if (isInInput) {
                            setSearchQuery('');
                            e.target.blur();
                            const firstSong = document.querySelector('[data-song-nummer]');
                            if (firstSong) {
                                firstSong.focus();
                                firstSong.scrollIntoView({ block: 'center', behavior: 'smooth' });
                            }
                            return;
                        }
                        // If search is active, clear it first
                        if (searchQuery && navLevel === 0) {
                            setSearchQuery('');
                            return;
                        }
                        // If in verse mode, go back
                        if (navLevel === 2) {
                            setNavLevel(1);
                            return;
                        }
                        if (navLevel === 1) {
                            setNavLevel(0);
                            if (socketRef.current) {
                                socketRef.current.emit('clear');
                            }
                            const currentSongButton = document.querySelector(`[data-song-nummer="${selectedSongRef.current?.nummer}"]`);
                            if (currentSongButton) {
                                currentSongButton.focus();
                            }
                            return;
                        }
                    }

                    // Don't handle other keys in input
                    if (isInInput) return;

                    // Fanelinjen håndterer selv piler/Enter/mellomrom i sine
                    // onKeyDown-handlere (med stopPropagation) — ingen fane-
                    // håndtering her.

                    // Arrow up/down from sort buttons
                    if (focusedElement?.classList.contains('sort-button')) {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const firstSong = document.querySelector('[data-song-nummer]');
                            if (firstSong) {
                                firstSong.focus();
                                firstSong.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                // Select the first song
                                const songNummer = firstSong.getAttribute('data-song-nummer');
                                const song = filteredSongs.find(s => s.nummer === songNummer);
                                if (song) {
                                    selectSong(song);
                                }
                            }
                            return;
                        }
                        if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            searchInputRef.current?.focus();
                            return;
                        }
                        // Left/right between sort buttons
                        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                            e.preventDefault();
                            const sortButtons = Array.from(document.querySelectorAll('.sort-button'));
                            const currentIndex = sortButtons.indexOf(focusedElement);
                            let nextIndex;
                            if (e.key === 'ArrowRight') {
                                nextIndex = Math.min(currentIndex + 1, sortButtons.length - 1);
                            } else {
                                nextIndex = Math.max(currentIndex - 1, 0);
                            }
                            sortButtons[nextIndex]?.focus();
                            return;
                        }
                    }

                    // Level 2: Display active - up/down navigates and sends to display
                    if (navLevel === 2 && selectedSongRef.current && verses.length > 0) {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (currentVerse < verses.length - 1) {
                                showVerse(currentVerse + 1);
                            }
                            return;
                        }
                        if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (currentVerse > 0) {
                                showVerse(currentVerse - 1);
                            }
                            return;
                        }
                        // Left arrow or Enter - back to preview mode (level 1), keep display showing
                        if (e.key === 'ArrowLeft' || e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setNavLevel(1);
                            // Display stays on with current verse
                            return;
                        }
                        // Home/End
                        if (e.key === 'Home') {
                            e.preventDefault();
                            showVerse(0);
                            return;
                        }
                        if (e.key === 'End') {
                            e.preventDefault();
                            showVerse(verses.length - 1);
                            return;
                        }
                    }

                    // Level 1: Song preview - up/down navigates verses without display
                    if (navLevel === 1 && selectedSongRef.current && verses.length > 0) {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (currentVerse < verses.length - 1) {
                                previewVerse(currentVerse + 1);
                            }
                            return;
                        }
                        if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (currentVerse > 0) {
                                previewVerse(currentVerse - 1);
                            }
                            return;
                        }
                        // Right arrow or Enter - activate display (level 2)
                        if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setNavLevel(2);
                            showVerse(currentVerse);
                            return;
                        }
                        // Left arrow - back to song list (level 0), clear display
                        if (e.key === 'ArrowLeft') {
                            e.preventDefault();
                            setNavLevel(0);
                            // Clear display when going back to song list
                            if (socketRef.current) {
                                socketRef.current.emit('clear');
                            }
                            const currentSongButton = document.querySelector(`[data-song-nummer="${selectedSongRef.current?.nummer}"]`);
                            if (currentSongButton) {
                                currentSongButton.focus();
                                currentSongButton.scrollIntoView({ block: 'center', behavior: 'smooth' });
                            }
                            return;
                        }
                        // Home/End
                        if (e.key === 'Home') {
                            e.preventDefault();
                            previewVerse(0);
                            return;
                        }
                        if (e.key === 'End') {
                            e.preventDefault();
                            previewVerse(verses.length - 1);
                            return;
                        }
                    }

                    // Arrow left from song list - go to search and deselect song
                    if (e.key === 'ArrowLeft' && navLevel === 0) {
                        const songButton = focusedElement?.hasAttribute('data-song-nummer')
                            ? focusedElement
                            : focusedElement?.closest('[data-song-nummer]');
                        if (songButton) {
                            e.preventDefault();
                            songButton.blur();
                            setSelectedSong(null);
                            searchInputRef.current?.focus();
                            return;
                        }
                    }

                    // Level 0: Song list navigation
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        // Let tab buttons handle their own navigation
                        if (focusedElement?.id === 'songs-tab' || focusedElement?.id === 'bible-tab' || focusedElement?.id === 'presentasjon-tab') {
                            return;
                        }

                        const songButton = focusedElement?.hasAttribute('data-song-nummer')
                            ? focusedElement
                            : focusedElement?.closest('[data-song-nummer]');

                        if (songButton) {
                            e.preventDefault();
                            const allSongs = Array.from(document.querySelectorAll('[data-song-nummer]'));
                            const currentIndex = allSongs.indexOf(songButton);

                            if (currentIndex === -1) return;

                            const step = e.shiftKey ? 10 : 1;
                            let nextIndex;

                            if (e.key === 'ArrowDown') {
                                nextIndex = Math.min(currentIndex + step, allSongs.length - 1);
                            } else {
                                // Arrow up from first song - go to sort buttons
                                if (currentIndex === 0) {
                                    const sortButtons = document.querySelectorAll('.sort-button');
                                    if (sortButtons.length > 0) {
                                        songButton.blur();
                                        setSelectedSong(null);
                                        sortButtons[0].focus();
                                        return;
                                    }
                                }
                                nextIndex = Math.max(currentIndex - step, 0);
                            }

                            if (allSongs[nextIndex]) {
                                allSongs[nextIndex].focus();
                                allSongs[nextIndex].scrollIntoView({ block: 'center', behavior: 'smooth' });
                                const songNummer = allSongs[nextIndex].getAttribute('data-song-nummer');
                                const song = filteredSongs.find(s => s.nummer === songNummer);
                                if (song) {
                                    selectSong(song);
                                }
                            }
                            return;
                        }

                        // Fallback: focus first song (but not if in toolbar or tabs)
                        const isInToolbar = focusedElement?.closest('.toolbar-buttons') ||
                                           focusedElement?.id === 'songs-tab' ||
                                           focusedElement?.id === 'bible-tab' ||
                                           focusedElement?.id === 'presentasjon-tab' ||
                                           focusedElement?.id === 'refresh-btn';
                        if (!isInToolbar) {
                            e.preventDefault();
                            const firstSong = document.querySelector('[data-song-nummer]');
                            if (firstSong) {
                                firstSong.focus();
                                firstSong.scrollIntoView({ block: 'center', behavior: 'smooth' });
                            }
                        }
                        return;
                    }

                    // Arrow right, Enter or Space from level 0 - enter song preview (level 1)
                    if ((e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') && navLevel === 0) {
                        // Only if a song button is focused
                        const songButton = focusedElement?.hasAttribute('data-song-nummer')
                            ? focusedElement
                            : focusedElement?.closest('[data-song-nummer]');
                        if (songButton && selectedSongRef.current) {
                            // Check if song has verses (use ref to get latest)
                            const songVerses = parseVerses(selectedSongRef.current.tekst);
                            if (songVerses.length > 0) {
                                e.preventDefault();
                                setNavLevel(1);
                                previewVerse(0);
                            }
                        }
                        return;
                    }
                };

                window.addEventListener('keydown', handleKeyDown);
                return () => window.removeEventListener('keydown', handleKeyDown);
            }, [currentVerse, verses, editMode, filteredSongs, navLevel, showGotoDialog, gotoNumber, appMode, bibleNavLevel, currentBibleVerse, bibleVerses, selectedBook, selectedChapter, showHelp, valgtPres, presSlide, presVist, presLiveSlide, presListe]);

            // Mobile view state - show list or content
            const [mobileView, setMobileView] = useState('list'); // 'list' or 'content'

            // Switch to content view on mobile when song/verse selected
            useEffect(() => {
                if (window.innerWidth < 768 && (selectedSong || bibleVerses.length > 0)) {
                    setMobileView('content');
                }
            }, [selectedSong, bibleVerses]);

            return (
                <div className="flex flex-col md:flex-row h-screen overflow-hidden" role="application" aria-label="Sangbok og Bibel presentasjonssystem">
                    {/* Left sidebar - full width on mobile, half on desktop */}
                    <nav className={`w-full md:w-1/2 bg-gray-800 flex flex-col border-r border-gray-700 min-h-0 overflow-hidden ${mobileView === 'content' ? 'hidden md:flex' : 'flex'}`} aria-label="Navigasjon">
                        {/* Mode switcher */}
                        <div className="h-12 flex items-center px-2 border-b border-gray-700">
                            <div className="flex-1 flex bg-gray-900 rounded-lg p-1" role="tablist" aria-label="Velg modus">
                                <button
                                    id="songs-tab"
                                    tabIndex={0}
                                    onClick={() => {
                                        setAppMode('songs');
                                        setQrVisible(false);
                                        navLevelRef.current = 1;
                                        setNavLevel(1);
                                        bibleNavLevelRef.current = 0;
                                        setBibleNavLevel(0);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowRight') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById('bible-tab')?.focus();
                                        } else if (e.key === 'ArrowLeft') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Wrap rundt til siste fane
                                            document.getElementById('presentasjon-tab')?.focus();
                                        } else if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Aktiver fanen (som klikk) og gå til første sang
                                            setAppMode('songs');
                                            setQrVisible(false);
                                            navLevelRef.current = 1;
                                            setNavLevel(1);
                                            bibleNavLevelRef.current = 0;
                                            setBibleNavLevel(0);
                                            setTimeout(() => {
                                                const firstSong = document.querySelector('[data-song-nummer]');
                                                if (firstSong) {
                                                    firstSong.focus();
                                                    firstSong.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                                    const songNummer = firstSong.getAttribute('data-song-nummer');
                                                    const song = filteredSongs.find(s => s.nummer === songNummer);
                                                    if (song) {
                                                        selectSong(song);
                                                    }
                                                }
                                            }, 50);
                                        }
                                    }}
                                    role="tab"
                                    aria-selected={appMode === 'songs'}
                                    aria-controls="songs-panel"
                                    className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition focus:ring-2 focus:ring-gull-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${appMode === 'songs' ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}
                                    style={{ outline: 'none' }}
                                >
                                    Sanger
                                </button>
                                <button
                                    id="bible-tab"
                                    tabIndex={0}
                                    onClick={() => {
                                        setAppMode('bible');
                                        setQrVisible(false);
                                        navLevelRef.current = 0;
                                        setNavLevel(0);
                                        // Blanke ark hver gang fanen aktiveres — ingen gjenopptak
                                        setSelectedBook(null);
                                        setSelectedChapter(null);
                                        setBibleVerses([]);
                                        setCurrentBibleVerse(0);
                                        bibleNavLevelRef.current = 0;
                                        setBibleNavLevel(0);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowLeft') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById('songs-tab')?.focus();
                                        } else if (e.key === 'ArrowRight') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById('presentasjon-tab')?.focus();
                                        } else if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Switch to bible mode
                                            setAppMode('bible');
                                            setQrVisible(false);
                                            // Blanke ark hver gang fanen aktiveres — ingen gjenopptak
                                            setSelectedBook(null);
                                            setSelectedChapter(null);
                                            setBibleVerses([]);
                                            setCurrentBibleVerse(0);
                                            bibleNavLevelRef.current = 0;
                                            setBibleNavLevel(0);
                                            // ↓ lander på 1. Mosebok. Bibel-JSON-en (4 MB) lastes
                                            // async ved første besøk — prøv til boklisten finnes i
                                            // DOM-en, ellers blir fokus stående på fanen og ↓ ser død ut
                                            const gaaInn = (forsok) => {
                                                if (!document.getElementById('bible-panel')) return; // forlot fanen
                                                const firstBook = document.querySelector('[data-bible-book]');
                                                if (firstBook) {
                                                    firstBook.focus();
                                                    firstBook.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                                    const bookCode = firstBook.getAttribute('data-bible-book');
                                                    if (bookCode) setSelectedBook(bookCode);
                                                    return;
                                                }
                                                if (forsok < 40) setTimeout(() => gaaInn(forsok + 1), 150);
                                            };
                                            setTimeout(() => gaaInn(0), 50);
                                        }
                                    }}
                                    role="tab"
                                    aria-selected={appMode === 'bible'}
                                    aria-controls="bible-panel"
                                    className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition focus:ring-2 focus:ring-gull-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${appMode === 'bible' ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}
                                    style={{ outline: 'none' }}
                                >
                                    Bibel
                                </button>
                                <button
                                    id="presentasjon-tab"
                                    tabIndex={0}
                                    onClick={() => {
                                        setAppMode('presentasjon');
                                        setQrVisible(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowLeft') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById('bible-tab')?.focus();
                                        } else if (e.key === 'ArrowRight') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Wrap rundt til første fane
                                            document.getElementById('songs-tab')?.focus();
                                        } else if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setAppMode('presentasjon');
                                            setQrVisible(false);
                                            // Panelet finnes alt hvis fanen er aktiv — fokuser da uten forsinkelse
                                            const sokBtn = document.getElementById('pres-sok-btn');
                                            if (sokBtn) sokBtn.focus();
                                            else setTimeout(() => document.getElementById('pres-sok-btn')?.focus(), 0);
                                        }
                                    }}
                                    role="tab"
                                    aria-selected={appMode === 'presentasjon'}
                                    aria-controls="presentasjon-panel"
                                    className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition focus:ring-2 focus:ring-gull-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${appMode === 'presentasjon' ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}
                                    style={{ outline: 'none' }}
                                >
                                    Presentasjon
                                </button>
                            </div>
                        </div>

                        {/* Songs mode */}
                        {appMode === 'songs' && (
                        <div id="songs-panel" role="tabpanel" aria-labelledby="songs-tab" className="flex flex-col flex-1 overflow-hidden">
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-gray-700">
                            {/* Bokvelger — kun aktivt hus' bøker (bøker deles aldri på tvers av hus) */}
                            {husBoker.length > 1 && (
                                <div className="mb-2 flex items-center gap-2">
                                    <label htmlFor="bok-velger" className="text-xs text-gray-400 flex-shrink-0">Sangbok:</label>
                                    <select
                                        id="bok-velger"
                                        value={bokId}
                                        onChange={(e) => setBokId(e.target.value)}
                                        className={`flex-1 min-w-0 px-2 py-1.5 rounded-lg border text-sm bg-gray-700 focus:outline-none focus:border-gull-500 ${iHovedbok ? 'border-gray-600 text-white' : 'border-gull-600 text-gull-300'}`}
                                    >
                                        {husBoker.map(b => (
                                            <option key={b.id} value={b.id}>
                                                {b.navn}{b.redigerbar ? '' : ' (skrivebeskyttet)'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Search + oppdater på samme rad */}
                            <div className="flex items-center gap-2">
                                <input
                                    id="sang-sok-input"
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Søk (nummer, tittel, tekst)... [/]"
                                    aria-label="Søk i sanger"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="flex-1 min-w-0 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                                />
                                {/* Sangplan: hent/velg plan — plassert ved søket der sangene velges */}
                                <button
                                    onClick={() => setShowImportDialog(true)}
                                    className={`relative flex-shrink-0 p-2 rounded transition focus:ring-2 focus:ring-gull-500 ${importedPlan ? 'text-green-400 hover:text-green-300' : 'text-gray-400 hover:text-white'}`}
                                    title={romPlaner.length > 0 ? `Velg sangplan (${romPlaner.length} delt til møtet)` : 'Velg sangplan'}
                                    aria-label="Velg sangplan"
                                >
                                    {romPlaner.length > 0 && !importedPlan && (
                                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-gull-400 rounded-full" aria-hidden="true"></span>
                                    )}
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                </button>
                                <button
                                    id="refresh-btn"
                                    onClick={() => loadSongs(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            searchInputRef.current?.focus();
                                        } else if (e.key === 'ArrowUp' || e.key === 'Escape') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById('songs-tab')?.focus();
                                        }
                                    }}
                                    className="flex-shrink-0 text-gray-400 hover:text-white p-2 rounded transition focus:ring-2 focus:ring-gull-500"
                                    title="Oppdater fra server (r)"
                                    aria-label="Oppdater sangliste"
                                >
                                    <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </div>

                            {/* Tabs / Sort buttons */}
                            <div className="flex gap-1 mt-3" role="group" aria-label="Sorteringsvalg">
                                {['nummer', 'alfa', 'toneart'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => {
                                            if (activeTab === tab) {
                                                setSortAsc(!sortAsc);
                                            } else {
                                                setActiveTab(tab);
                                                setSortAsc(true);
                                            }
                                        }}
                                        aria-pressed={activeTab === tab}
                                        aria-label={`Sorter etter ${tab === 'nummer' ? 'nummer' : tab === 'alfa' ? 'alfabet' : 'toneart'}${activeTab === tab ? (sortAsc ? ', stigende' : ', synkende') : ''}`}
                                        className={`sort-button flex-1 px-2 py-1 text-sm rounded transition focus:outline-none focus:ring-2 focus:ring-gull-500 ${activeTab === tab ? 'tab-btn active' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                                    >
                                        <span aria-hidden="true">{tab === 'nummer' ? '#' : tab === 'alfa' ? 'A-Å' : '♪'}</span>
                                        {activeTab === tab && (
                                            <span className="ml-1" aria-hidden="true">{sortAsc ? '↑' : '↓'}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Imported plan section */}
                        {importedPlan && importedPlan.sanger.length > 0 && (
                            <div className="border-b border-gray-700 p-2 bg-gray-800/50">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-xs text-green-400 font-semibold uppercase">Sangplan</span>
                                        <span className="text-xs text-gray-400 truncate">{importedPlan.navn}</span>
                                        <span className="text-xs text-gray-500 flex-shrink-0">
                                            {(importedPlan.sunget || []).length}/{importedPlan.sanger.filter(s => s.kind !== 'note').length} sunget
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={planForrige}
                                            title="Forrige sang i planen"
                                            aria-label="Forrige sang i planen"
                                            className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 transition"
                                        >
                                            ◀
                                        </button>
                                        <button
                                            onClick={planNeste}
                                            title="Neste sang i planen"
                                            className="text-xs px-2 py-1 bg-green-700 hover:bg-green-600 rounded text-white font-medium transition"
                                        >
                                            Neste ▶
                                        </button>
                                        <button
                                            onClick={planFjern}
                                            className="text-xs text-gray-500 hover:text-red-400 ml-1"
                                        >
                                            Fjern
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {importedPlan.sanger.map((item, idx) => {
                                        if (item.kind === 'note') {
                                            return (
                                                <span key={`note-${idx}`} className="text-xs px-2 py-1 bg-gull-900/30 border border-gull-700/50 rounded text-gull-400" title={item.text || 'Notat'}>
                                                    📝
                                                </span>
                                            );
                                        }
                                        const fullSong = songs.find(s => s.nummer === item.nummer) || (item.tekst ? item : null);
                                        const erAktiv = importedPlan.aktiv === idx;
                                        const erSunget = (importedPlan.sunget || []).includes(idx);
                                        return (
                                            <button
                                                key={`${item.nummer}-${idx}`}
                                                onClick={() => velgPlanElement(idx)}
                                                className={`text-xs px-2 py-1 rounded transition ${
                                                    erAktiv
                                                        ? 'bg-green-700/60 border border-green-500 text-white ring-2 ring-green-400'
                                                        : erSunget
                                                            ? 'bg-gray-700/60 border border-gray-600 text-gray-500'
                                                            : fullSong
                                                                ? 'bg-green-900/30 border border-green-700/50 text-green-400 hover:bg-green-800/50'
                                                                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                                }`}
                                                disabled={!fullSong}
                                                title={item.tittel}
                                            >
                                                {erSunget && !erAktiv ? '✓ ' : ''}{item.nummer}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Song list */}
                        <div className="flex-1 overflow-y-auto p-2 song-list" role="listbox" aria-label="Sangliste">
                            {loading ? (
                                <div className="text-center py-8 text-gray-400" role="status" aria-live="polite">Laster sanger...</div>
                            ) : filteredSongs.length === 0 && searchQuery ? (
                                <div className="text-center py-8 px-4 text-gray-400" role="status" aria-live="polite">
                                    <div className="text-base mb-1">Ingen treff for «{searchQuery}»</div>
                                    <div className="text-xs text-gray-500">Prøv andre ord, færre ord eller bare sangnummer</div>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {filteredSongs.map(song => {
                                        const qWordsForHighlight = searchQuery.length >= 2
                                            ? _splitWords(_normalize(searchQuery))
                                            : [];
                                        const titlePhrase = qWordsForHighlight.length > 0
                                            ? findPhrasePosition(song.tittel || '', qWordsForHighlight)
                                            : null;
                                        // Show extract below only when title doesn't already match
                                        const searchContext = !titlePhrase ? getSearchContext(song.tekst, searchQuery) : null;
                                        return (
                                            <button
                                                key={song.nummer}
                                                role="option"
                                                aria-selected={selectedSong?.nummer === song.nummer}
                                                aria-label={`${song.nummer}: ${song.tittel}${song.toneart ? `, ${song.toneart}` : ''}${searchContext ? `. ${searchContext.verseLabel}: ${searchContext.before}${searchContext.match}${searchContext.after}` : ''}`}
                                                data-song-nummer={song.nummer}
                                                onClick={() => selectSong(song)}
                                                className={`song-item w-full text-left p-2 rounded-lg border-2 cursor-pointer transition outline-none ring-0 focus:outline-none focus:ring-0 ${
                                                    selectedSong?.nummer === song.nummer
                                                        ? `selected level-${navLevel}`
                                                        : 'border-transparent'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2" aria-hidden="true">
                                                    <span className={`font-mono text-sm w-8 ${
                                                        selectedSong?.nummer === song.nummer
                                                            ? navLevel === 2
                                                                ? 'text-green-400'
                                                                : navLevel === 1
                                                                    ? 'text-gull-400'
                                                                    : 'text-gray-300'
                                                            : 'text-gull-400'
                                                    }`}>{song.nummer}</span>
                                                    <span className="flex-1 truncate">
                                                        {titlePhrase ? (
                                                            <>
                                                                {song.tittel.slice(0, titlePhrase.phraseStart)}
                                                                <mark className="bg-gull-500 text-gull-950 px-0.5 rounded">{song.tittel.slice(titlePhrase.phraseStart, titlePhrase.phraseEnd)}</mark>
                                                                {song.tittel.slice(titlePhrase.phraseEnd)}
                                                            </>
                                                        ) : song.tittel}
                                                    </span>
                                                    {song.toneart && (
                                                        <span className="text-xs text-gray-500 bg-gray-700 px-1.5 py-0.5 rounded">{song.toneart}</span>
                                                    )}
                                                </div>
                                                {searchContext && (
                                                    <div className="ml-10 mt-1 text-xs text-gray-400 truncate" aria-hidden="true">
                                                        <span className="text-gray-500">[{searchContext.verseLabel}]</span> {searchContext.before}<mark className="bg-gull-500 text-gull-950 px-0.5 rounded">{searchContext.match}</mark>{searchContext.after}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Ny sang — bannerknapp nederst i kolonnen */}
                        <div className="flex-shrink-0 p-2 border-t border-gray-700">
                            <button
                                id="add-song-btn"
                                onClick={startAddSong}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        searchInputRef.current?.focus();
                                    } else if (e.key === 'Escape') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        document.getElementById('songs-tab')?.focus();
                                    }
                                }}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gull-400 hover:text-gull-300 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-gull-500"
                                title="Legg til ny sang"
                                aria-label="Legg til ny sang"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Ny sang
                            </button>
                        </div>
                        </div>
                        )}

                        {/* Bible mode - two column layout in left panel */}
                        {appMode === 'bible' && (
                        <div id="bible-panel" role="tabpanel" aria-labelledby="bible-tab" className="flex flex-col flex-1 min-h-0">
                        <div className="px-4 py-3 border-b border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    {aktiveBibler.map((b, bIdx) => (
                                    <button
                                        key={b.id}
                                        id={`bible-version-${b.id}`}
                                        onClick={() => setBibleVersion(b.id)}
                                        title={b.navn}
                                        onKeyDown={(e) => {
                                            if (e.key === 'ArrowRight') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const neste = bIdx < aktiveBibler.length - 1
                                                    ? `bible-version-${aktiveBibler[bIdx + 1].id}` : 'bible-help-btn';
                                                document.getElementById(neste)?.focus();
                                            } else if (e.key === 'ArrowLeft') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // Første knapp wrapper rundt til siste i raden
                                                const forrige = bIdx > 0
                                                    ? `bible-version-${aktiveBibler[bIdx - 1].id}` : 'bible-help-btn';
                                                document.getElementById(forrige)?.focus();
                                            } else if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setTimeout(() => {
                                                    const firstBook = document.querySelector('[data-bible-book]');
                                                    if (firstBook) {
                                                        firstBook.focus();
                                                        firstBook.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                                        const bookCode = firstBook.getAttribute('data-bible-book');
                                                        if (bookCode) {
                                                            setSelectedBook(bookCode);
                                                            setSelectedChapter(null);
                                                            setBibleVerses([]);
                                                            setCurrentBibleVerse(0);
                                                            bibleNavLevelRef.current = 0;
                                                            setBibleNavLevel(0);
                                                        }
                                                    }
                                                }, 0);
                                            } else if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                document.getElementById('bible-tab')?.focus();
                                            }
                                        }}
                                        className={`px-3 py-1 text-sm font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-gull-500 ${bibleVersion === b.id ? 'bg-gull-500 text-gull-950' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}
                                    >
                                        {b.kort || b.navn}
                                    </button>
                                    ))}
                                    {partner && <button
                                        id="bible-parallell"
                                        onClick={() => setParallell(p => !p)}
                                        title="Vis begge språk under hverandre på storskjermen (kanalen beholder ett språk)"
                                        aria-pressed={parallell}
                                        className={`px-3 py-1 text-sm font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-gull-500 ${parallell ? 'bg-gull-500 text-gull-950' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}
                                    >
                                        2×
                                    </button>}
                                    <button
                                        id="bible-help-btn"
                                        onClick={() => setShowHelp(true)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'ArrowRight') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // Wrap rundt til første knapp i raden (1930)
                                                document.getElementById('bible-version-no')?.focus();
                                            } else if (e.key === 'ArrowLeft') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                document.getElementById('bible-version-en')?.focus();
                                            } else if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                bibleSearchInputRef.current?.focus();
                                            } else if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                document.getElementById('bible-tab')?.focus();
                                            }
                                        }}
                                        className="text-gray-400 hover:text-white p-1 rounded transition focus:ring-2 focus:ring-gull-500"
                                        style={{ outline: 'none' }}
                                        title="Tastaturhjelp (?)"
                                        aria-label="Vis tastaturhjelp"
                                    >
                                        <span className="w-5 h-5 bg-white text-gray-800 rounded-full flex items-center justify-center text-sm font-bold" aria-hidden="true">?</span>
                                    </button>
                                </div>
                            </div>
                            <input
                                id="bibel-sok-input"
                                ref={bibleSearchInputRef}
                                type="text"
                                value={bibleSearchQuery}
                                onChange={(e) => setBibleSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // Select first matching book
                                        if (bibleData && bibleSearchQuery.trim()) {
                                            const match = bibleData.books.find(code =>
                                                bibleData.names[code].toLowerCase().includes(bibleSearchQuery.trim().toLowerCase())
                                            );
                                            if (match) {
                                                setSelectedBook(match);
                                                setSelectedChapter(null);
                                                setBibleVerses([]);
                                                setBibleSearchQuery('');
                                                // Clear display when selecting a book from search
                                                socket?.emit('clear');
                                                setQrVisible(false);
                                                e.target.blur();
                                                setTimeout(() => {
                                                    const bookBtn = document.querySelector(`[data-bible-book="${match}"]`);
                                                    if (bookBtn) bookBtn.focus();
                                                }, 50);
                                            }
                                        }
                                    }
                                    if (e.key === 'Escape' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setBibleSearchQuery('');
                                        // Go back to last focused book, or bible tab
                                        const targetBook = lastFocusedBookRef.current;
                                        setTimeout(() => {
                                            if (targetBook) {
                                                const bookBtn = document.querySelector(`[data-bible-book="${targetBook}"]`);
                                                if (bookBtn) {
                                                    bookBtn.focus();
                                                    bookBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                                    return;
                                                }
                                            }
                                            document.getElementById('bible-tab')?.focus();
                                        }, 10);
                                    }
                                    if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        document.getElementById('bible-version-no')?.focus();
                                    }
                                }}
                                placeholder="Filter bøker..."
                                aria-label="Filtrer bibelbok"
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                            />
                        </div>

                        {/* Mobile: Show books OR chapters. Desktop: Show both side by side */}
                        <div className="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden">
                            {/* Book list - full width on mobile when no book selected, left column on desktop */}
                            <div className={`${selectedBook ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-1/2 overflow-y-auto border-b md:border-b-0 md:border-r border-gray-700 p-2 flex-1`} role="listbox" aria-label="Bibelbøker">
                                {bibleLoading ? (
                                    <div className="text-center py-8 text-gray-400" role="status" aria-live="polite">Laster...</div>
                                ) : bibleData && (
                                    <div className="space-y-0.5">
                                        {bibleData.books
                                            .filter(bookCode =>
                                                !bibleSearchQuery.trim() ||
                                                bibleData.names[bookCode].toLowerCase().includes(bibleSearchQuery.trim().toLowerCase())
                                            )
                                            .map(bookCode => (
                                            <button
                                                key={bookCode}
                                                data-bible-book={bookCode}
                                                role="option"
                                                aria-selected={selectedBook === bookCode}
                                                onClick={() => {
                                                    setSelectedBook(bookCode);
                                                    setSelectedChapter(null);
                                                    setBibleVerses([]);
                                                    setCurrentBibleVerse(0);
                                                    bibleNavLevelRef.current = 0;
                                                    setBibleNavLevel(0);
                                                    setBibleSearchQuery('');
                                                    // Clear display when selecting a book
                                                    socket?.emit('clear');
                                                    setQrVisible(false);
                                                }}
                                                className={`bible-item w-full text-left px-2 py-1.5 rounded text-sm cursor-pointer transition outline-none ${
                                                    selectedBook === bookCode
                                                        ? `level-${bibleNavLevel} text-white`
                                                        : 'text-gray-300 hover:bg-gray-700'
                                                }`}
                                            >
                                                {bibleData.names[bookCode]}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Chapter list - full width on mobile when book selected, right column on desktop */}
                            <div className={`${selectedBook ? 'block' : 'hidden md:block'} w-full md:w-1/2 overflow-y-auto p-2 flex-1`} role="listbox" aria-label="Kapitler">
                                {/* Mobile back to books button */}
                                {selectedBook && (
                                    <button
                                        onClick={() => {
                                            setSelectedBook(null);
                                            setSelectedChapter(null);
                                            setBibleVerses([]);
                                        }}
                                        className="md:hidden flex items-center gap-2 w-full p-2 mb-2 text-left text-gull-400 hover:text-gull-300 border-b border-gray-700"
                                        aria-label="Tilbake til bibelbøker"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        <span className="font-medium">{bibleData?.names[selectedBook]}</span>
                                    </button>
                                )}
                                {selectedBook && bibleData ? (
                                    <div className="grid grid-cols-4 md:grid-cols-1 gap-1 md:space-y-0.5">
                                        {Object.keys(bibleData.bible[selectedBook] || {}).sort((a,b) => parseInt(a) - parseInt(b)).map(ch => (
                                            <button
                                                key={ch}
                                                data-bible-chapter={ch}
                                                role="option"
                                                aria-selected={selectedChapter === ch}
                                                onClick={() => {
                                                    setSelectedChapter(ch);
                                                    const verseData = bibleData.bible[selectedBook][ch];
                                                    const verseArray = Object.keys(verseData).map(v => ({
                                                        num: parseInt(v),
                                                        text: verseData[v]
                                                    })).sort((a, b) => a.num - b.num);
                                                    setBibleVerses(verseArray);
                                                    setCurrentBibleVerse(0);
                                                    bibleNavLevelRef.current = 0;
                                                    setBibleNavLevel(0);
                                                    // Clear display when selecting a chapter
                                                    socket?.emit('clear');
                                                    setQrVisible(false);
                                                    // On mobile, switch to content view
                                                    if (window.innerWidth < 768) {
                                                        setMobileView('content');
                                                    }
                                                }}
                                                className={`bible-item w-full text-center md:text-left px-2 py-2 md:py-1.5 rounded text-sm cursor-pointer transition outline-none ${
                                                    selectedChapter === ch
                                                        ? `level-${bibleNavLevel} text-white`
                                                        : 'text-gray-300 hover:bg-gray-700'
                                                }`}
                                            >
                                                <span className="md:hidden">{ch}</span>
                                                <span className="hidden md:inline">Kapittel {ch}</span>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="hidden md:block text-center py-8 text-gray-500 text-sm">
                                        Velg en bok
                                    </div>
                                )}
                            </div>
                        </div>
                        </div>
                        )}

                        {/* Presentation mode */}
                        {appMode === 'presentasjon' && (
                        <div id="presentasjon-panel" role="tabpanel" aria-labelledby="presentasjon-tab" className="flex flex-col flex-1 min-h-0">
                            <div className="px-4 py-3 border-b border-gray-700">
                                <div className="flex items-center justify-between mb-3">
                                    <button
                                        id="pres-sok-btn"
                                        onClick={sokPresentasjoner}
                                        disabled={presSoker}
                                        className="flex items-center gap-2 px-3 py-2 bg-gull-500 hover:bg-gull-400 text-gull-950 disabled:bg-gray-600 disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-gull-500"
                                    >
                                        <svg className={`w-4 h-4 ${presSoker ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            {presSoker ? (
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            ) : (
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            )}
                                        </svg>
                                        {presSoker ? 'Søker …' : 'Søk etter presentasjon'}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-400">
                                    Send PowerPoint eller PDF som vedlegg til <span className="text-gray-300 font-medium">opstadtab@gmail.com</span>, og trykk «Søk etter presentasjon».
                                </p>
                                {presFeil && (
                                    <p className="mt-2 text-sm text-red-400" role="alert">{presFeil}</p>
                                )}
                                {presenterKode && (
                                    <div className="mt-3 flex items-center gap-3 p-3 bg-gray-800 border border-gray-700 rounded-lg">
                                        <canvas id="presenter-qr-canvas" className="rounded bg-white flex-shrink-0" width="88" height="88" aria-hidden="true"></canvas>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-gray-200">Fjernkontroll for taleren</div>
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                Skann QR-en, eller åpne <span className="text-gray-300">vis.bibeltroen.no/presenter</span> og tast
                                            </div>
                                            <div
                                                className="text-xl font-mono font-bold text-gull-400 tracking-widest cursor-pointer hover:text-gull-300 mt-0.5"
                                                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/presenter?kode=${presenterKode}`)}
                                                title="Klikk for å kopiere lenken"
                                            >
                                                {presenterKode.slice(0, 3)} {presenterKode.slice(3)}
                                            </div>
                                            {presenterAntall > 0 && (
                                                <div className="text-xs text-green-400 mt-0.5 flex items-center gap-1.5" role="status">
                                                    <span className="w-2 h-2 rounded-full bg-green-400 inline-block" aria-hidden="true"></span>
                                                    Koblet til fjernkontroll{presenterAntall > 1 ? ` (${presenterAntall})` : ''}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Mobil: liste ELLER lysbilder. Desktop: begge side om side (som Bibel-fanen) */}
                            <div className="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden">
                            {/* Presentasjonsliste — venstre kolonne (mobil: skjules når en er valgt) */}
                            <div className={`${valgtPres ? 'hidden md:block' : 'block'} w-full md:w-1/2 md:border-r border-gray-700 overflow-y-auto p-2 flex-1`} role="listbox" aria-label="Presentasjoner">
                                {presListe.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 text-sm">
                                        Ingen presentasjoner ennå
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {presListe.map(p => (
                                            <div
                                                key={p.id}
                                                role="option"
                                                aria-selected={valgtPres?.id === p.id}
                                                tabIndex={0}
                                                data-pres-id={p.id}
                                                onClick={() => velgPresentasjon(p)}
                                                className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg cursor-pointer transition focus:outline-none focus:ring-2 focus:ring-gull-500 ${valgtPres?.id === p.id ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium truncate">{p.navn}</div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {p.avsender} · {(p.dato || '').substring(0, 10)}
                                                        {p.status === 'konvertert' ? ` · ${p.lysbilder} lysbilder` : ''}
                                                    </div>
                                                </div>
                                                {presKonverterer === p.id ? (
                                                    <span className="text-xs text-gull-400 flex-shrink-0">Konverterer …</span>
                                                ) : p.status !== 'konvertert' ? (
                                                    <span className="text-xs text-gray-500 flex-shrink-0">Ny</span>
                                                ) : null}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); slettPresentasjon(p); }}
                                                    className="p-1.5 text-gray-500 hover:text-red-400 flex-shrink-0"
                                                    aria-label={`Slett ${p.navn}`}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Lysbildeliste — høyre kolonne, vertikal (som kapitler i Bibel-fanen).
                                Alltid synlig på desktop; plassholder når ingen er valgt */}
                            <div id="pres-lysbildeliste" className={`${valgtPres ? 'block' : 'hidden md:block'} w-full md:w-1/2 overflow-y-auto p-2 flex-1`} role="listbox" aria-label="Lysbilder">
                                {valgtPres ? (
                                <>
                                    {/* Mobil: tilbake til presentasjonene */}
                                    <button
                                        onClick={() => { setValgtPres(null); setPresVist(false); setPresLiveSlide(null); }}
                                        className="md:hidden flex items-center gap-2 w-full p-2 mb-2 text-left text-gull-400 hover:text-gull-300 border-b border-gray-700"
                                        aria-label="Tilbake til presentasjonene"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        <span className="font-medium truncate">{valgtPres.navn}</span>
                                    </button>
                                    <div className="space-y-2">
                                        {Array.from({ length: valgtPres.lysbilder }, (_, i) => i + 1).map(n => (
                                            <button
                                                key={n}
                                                role="option"
                                                aria-selected={presSlide === n}
                                                onClick={(e) => {
                                                    // Slipp fokus så piltastene styrer lysbildene etterpå
                                                    e.currentTarget.blur();
                                                    if (presSlide === n) {
                                                        // Klikk på valgt: gul → vis (grønn), grønn → gul
                                                        if (presVist) setPresVist(false);
                                                        else visLysbilde(valgtPres, n);
                                                    } else if (presVist) {
                                                        visLysbilde(valgtPres, n); // grønn: bytt direkte
                                                    } else {
                                                        setPresSlide(n); // gul: bare velg
                                                    }
                                                }}
                                                className={`relative block w-4/5 mx-auto rounded overflow-hidden border-2 transition ${presSlide === n ? (presVist ? 'border-green-500 ring-2 ring-green-500' : 'border-gull-500 ring-2 ring-gull-500') : (!presVist && presLiveSlide === n ? 'border-green-700' : 'border-gray-600 hover:border-gray-400')}`}
                                                aria-label={presSlide === n ? (presVist ? `Lysbilde ${n} vises — klikk for forhåndsvisning` : `Vis lysbilde ${n} på skjerm`) : `Velg lysbilde ${n}`}
                                            >
                                                <img
                                                    src={`/presentasjoner/${valgtPres.id}/mini/${n}`}
                                                    alt=""
                                                    loading="lazy"
                                                    className="w-full h-auto"
                                                />
                                                <span className="absolute bottom-0 right-0 px-1 text-xs bg-black/70 text-white">{n}</span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                                ) : (
                                    <div className="hidden md:block text-center py-8 text-gray-500 text-sm">
                                        Velg en presentasjon
                                    </div>
                                )}
                            </div>
                            </div>
                        </div>
                        )}

                    </nav>

                    {/* Main content - full width on mobile, half on desktop */}
                    <main id="main-content" className={`w-full md:w-1/2 flex-1 flex flex-col min-h-0 ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`} aria-label="Forhåndsvisning">
                        {statusLinjeUI}
                        {/* Songs mode - right panel */}
                        {appMode === 'songs' && (
                            selectedSong ? (
                            <>
                                {/* Song header */}
                                <header className="px-4 py-3 border-b border-gray-700 bg-gray-800">
                                    <div className="flex items-center justify-between gap-2">
                                        {/* Mobile back button */}
                                        <button
                                            onClick={() => setMobileView('list')}
                                            className="md:hidden p-2 -ml-2 text-gray-400 hover:text-white"
                                            aria-label="Tilbake til sanglisten"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <h1 className="text-lg md:text-2xl font-bold truncate">{selectedSong.nummer}. {selectedSong.tittel}</h1>
                                            <div className="flex gap-4 mt-1 text-sm text-gray-400">
                                                {selectedSong.toneart && <span>Toneart: {selectedSong.toneart}</span>}
                                                {selectedSong.tekstforfatter && <span>Tekst: {selectedSong.tekstforfatter}</span>}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => startEdit(selectedSong)}
                                            className="hidden md:flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition flex-shrink-0"
                                            aria-label={`Rediger sang ${selectedSong.nummer}: ${selectedSong.tittel}`}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            <span className="text-sm">Rediger</span>
                                        </button>
                                        {/* Skjermjustering: utvid/skjul kontrollboksen under overskriften */}
                                        <button
                                            id="skjermstil-btn"
                                            onClick={() => setShowSkjermStil(v => !v)}
                                            className={`flex items-center gap-1 px-3 py-2 rounded-lg transition flex-shrink-0 ${(showSkjermStil || skjermStilAvviker) ? 'text-gull-400 bg-gray-700/60' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                                            title="Juster størrelse og plassering av sangteksten på storskjermen"
                                            aria-label="Skjermjustering"
                                            aria-expanded={showSkjermStil}
                                            aria-controls="skjermstil-boks"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4M9 12h6" />
                                            </svg>
                                            <span className="text-sm">{skjermStilAvviker ? `${Math.round(skjermStil.zoom * 100)} %` : 'Skjerm'}</span>
                                            <svg className={`w-3 h-3 transition-transform ${showSkjermStil ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                    </div>
                                    {showSkjermStil && (
                                        <div id="skjermstil-boks" className="mt-3 pt-3 border-t border-gray-700 flex flex-wrap items-center gap-x-6 gap-y-2" role="group" aria-label="Skjermjustering">
                                            <span className="flex items-center gap-1">
                                                <span className="text-xs text-gray-400 mr-1">Størrelse</span>
                                                <button onClick={() => justerSkjermStil({ zoom: -0.1 })} className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 text-base font-bold focus:outline-none focus:ring-2 focus:ring-gull-500" title="Mindre tekst" aria-label="Mindre tekst">−</button>
                                                <span className="w-12 text-center font-mono text-xs text-gull-400">{Math.round(skjermStil.zoom * 100)} %</span>
                                                <button onClick={() => justerSkjermStil({ zoom: 0.1 })} className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 text-base font-bold focus:outline-none focus:ring-2 focus:ring-gull-500" title="Større tekst" aria-label="Større tekst">+</button>
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <span className="text-xs text-gray-400 mr-1">x</span>
                                                <button onClick={() => justerSkjermStil({ dx: -2 })} className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 text-base font-bold focus:outline-none focus:ring-2 focus:ring-gull-500" title="Flytt til venstre" aria-label="Flytt til venstre">←</button>
                                                <span className="w-12 text-center font-mono text-xs text-gull-400">{skjermStil.dx > 0 ? '+' : ''}{skjermStil.dx}</span>
                                                <button onClick={() => justerSkjermStil({ dx: 2 })} className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 text-base font-bold focus:outline-none focus:ring-2 focus:ring-gull-500" title="Flytt til høyre" aria-label="Flytt til høyre">→</button>
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <span className="text-xs text-gray-400 mr-1">y</span>
                                                <button onClick={() => justerSkjermStil({ dy: -2 })} className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 text-base font-bold focus:outline-none focus:ring-2 focus:ring-gull-500" title="Flytt opp" aria-label="Flytt opp">↑</button>
                                                <span className="w-12 text-center font-mono text-xs text-gull-400">{skjermStil.dy > 0 ? '+' : ''}{skjermStil.dy}</span>
                                                <button onClick={() => justerSkjermStil({ dy: 2 })} className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 text-base font-bold focus:outline-none focus:ring-2 focus:ring-gull-500" title="Flytt ned" aria-label="Flytt ned">↓</button>
                                            </span>
                                            <button
                                                onClick={() => sendSkjermStil(SKJERM_STIL_STANDARD)}
                                                disabled={!skjermStilAvviker}
                                                className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-xs text-gray-300 focus:outline-none focus:ring-2 focus:ring-gull-500"
                                            >
                                                Nullstill
                                            </button>
                                        </div>
                                    )}
                                </header>

                                {/* Vertical verse list */}
                                <section className="flex-1 overflow-y-auto p-2 md:p-4" role="region" aria-label="Versliste">
                                    <div className="max-w-3xl mx-auto space-y-2" role="listbox" aria-label={`Vers i ${selectedSong.tittel}`}>
                                        {verses.map((verse, index) => {
                                            const verseLabel = verse.isChorus ? 'Kor' : `Vers ${verses.slice(0, index + 1).filter(v => !v.isChorus).length}`;
                                            return (
                                            <div
                                                key={index}
                                                ref={el => verseRefs.current[index] = el}
                                                role="option"
                                                aria-selected={currentVerse === index}
                                                aria-label={`${verseLabel}: ${cleanVerseText(verse.text).substring(0, 80)}${cleanVerseText(verse.text).length > 80 ? '...' : ''}`}
                                                tabIndex={currentVerse === index ? 0 : -1}
                                                onClick={() => {
                                                    const level = navLevelRef.current;
                                                    if (level === 0) {
                                                        navLevelRef.current = 1;
                                                        setNavLevel(1);
                                                        previewVerse(index);
                                                    } else if (level === 1) {
                                                        if (currentVerse === index) {
                                                            navLevelRef.current = 2;
                                                            setNavLevel(2);
                                                            showVerse(index);
                                                        } else {
                                                            previewVerse(index);
                                                        }
                                                    } else {
                                                        if (currentVerse === index) {
                                                            navLevelRef.current = 1;
                                                            setNavLevel(1);
                                                        } else {
                                                            showVerse(index);
                                                        }
                                                    }
                                                }}
                                                className={`verse-item p-4 rounded-lg border cursor-pointer transition ${currentVerse === index ? (navLevel === 2 ? 'verse-level-2' : navLevel === 1 ? 'verse-level-1' : 'bg-gray-800 border-gray-700') : 'bg-gray-800 border-gray-700 hover:bg-gray-700'}`}
                                            >
                                                <div className="flex gap-4" aria-hidden="true">
                                                    <div className={`flex-shrink-0 w-12 text-sm font-medium ${currentVerse === index && navLevel === 2 ? 'text-green-400' : currentVerse === index && navLevel === 1 ? 'text-gull-400' : 'text-gray-500'}`}>
                                                        {verseLabel}
                                                    </div>
                                                    <div className={`preview-text text-sm leading-relaxed ${currentVerse === index && navLevel >= 1 ? 'text-white' : 'text-gray-400'}`}>
                                                        {cleanVerseText(verse.text)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                        })}
                                    </div>
                                    <p className="text-center text-gray-500 mt-4 text-sm" role="status" aria-live="polite">
                                        {currentVerse + 1} / {verses.length} — Bruk piltaster for navigering
                                    </p>
                                </section>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-gray-500" role="status" aria-label="Ingen sang valgt">
                                <div className="text-center">
                                    <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                    </svg>
                                    <p className="text-lg">Velg en sang fra listen</p>
                                </div>
                            </div>
                        ))}

                        {/* Bible mode - right panel */}
                        {appMode === 'bible' && (
                            bibleVerses.length > 0 ? (
                            <>
                                {/* Bible header */}
                                <header className="px-4 py-3 border-b border-gray-700 bg-gray-800 flex items-center gap-2">
                                    {/* Mobile back button */}
                                    <button
                                        onClick={() => setMobileView('list')}
                                        className="md:hidden p-2 -ml-2 text-gray-400 hover:text-white"
                                        aria-label="Tilbake til bibelbøker"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <h2 className="text-lg md:text-2xl font-bold" aria-live="polite">
                                        {bibleData?.names[selectedBook]} {selectedChapter}
                                    </h2>
                                </header>

                                {/* Bible verse list */}
                                <div className="flex-1 overflow-y-auto p-2 md:p-4 bible-verse-list" role="listbox" aria-label={`Vers i ${bibleData?.names[selectedBook]} kapittel ${selectedChapter}`}>
                                    <div className="max-w-3xl mx-auto space-y-2">
                                        {cleanedBibleVerses.map((verse, index) => (
                                            <div
                                                key={verse.num}
                                                ref={el => bibleVerseRefs.current[index] = el}
                                                role="option"
                                                aria-selected={currentBibleVerse === index}
                                                aria-label={`Vers ${verse.nr}: ${verse.cleanText.substring(0, 50)}${verse.cleanText.length > 50 ? '...' : ''}`}
                                                onClick={() => {
                                                    const level = bibleNavLevelRef.current;
                                                    if (level === 0) {
                                                        bibleNavLevelRef.current = 1;
                                                        setBibleNavLevel(1);
                                                        setCurrentBibleVerse(index);
                                                    } else if (level === 1) {
                                                        if (currentBibleVerse === index) {
                                                            bibleNavLevelRef.current = 2;
                                                            setBibleNavLevel(2);
                                                            if (socket && bibleData) {
                                                                {
                                                                    const last = byggBibelLast(
                                                                        selectedBook, bibleData.names[selectedBook],
                                                                        selectedChapter, verse.num, verse.cleanText);
                                                                    socket.emit('show_bible', last);
                                                                    sisteVisning.current.bible = last;
                                                                }
                                                            }
                                                        } else {
                                                            setCurrentBibleVerse(index);
                                                        }
                                                    } else {
                                                        if (currentBibleVerse === index) {
                                                            bibleNavLevelRef.current = 1;
                                                            setBibleNavLevel(1);
                                                        } else {
                                                            setCurrentBibleVerse(index);
                                                            if (socket && bibleData) {
                                                                {
                                                                    const last = byggBibelLast(
                                                                        selectedBook, bibleData.names[selectedBook],
                                                                        selectedChapter, verse.num, verse.cleanText);
                                                                    socket.emit('show_bible', last);
                                                                    sisteVisning.current.bible = last;
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                                className={`verse-item p-4 rounded-lg border cursor-pointer transition ${currentBibleVerse === index ? (bibleNavLevel === 2 ? 'verse-level-2' : bibleNavLevel === 1 ? 'verse-level-1' : 'bg-gray-800 border-gray-700') : 'bg-gray-800 border-gray-700 hover:bg-gray-700'}`}
                                            >
                                                <div className="flex gap-4" aria-hidden="true">
                                                    <div className={`flex-shrink-0 w-12 text-sm font-medium tabular-nums ${currentBibleVerse === index && bibleNavLevel === 2 ? 'text-green-400' : currentBibleVerse === index && bibleNavLevel === 1 ? 'text-gull-400' : 'text-gray-500'}`}>
                                                        {verse.nr}
                                                    </div>
                                                    <div className={`preview-text text-sm leading-relaxed ${currentBibleVerse === index && bibleNavLevel >= 1 ? 'text-white' : 'text-gray-400'}`}
                                                        dangerouslySetInnerHTML={{ __html: renderVerseHtml(verse.text) }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-center text-gray-500 mt-4 text-sm" role="status" aria-live="polite" aria-atomic="true">
                                        {currentBibleVerse + 1} / {bibleVerses.length} — Bruk piltaster for navigering
                                    </p>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-gray-500" role="status" aria-label="Ingen vers valgt">
                                <div className="text-center">
                                    <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                    <p className="text-lg">Velg bok og kapittel</p>
                                </div>
                            </div>
                        ))}

                        {/* Presentation mode - right panel */}
                        {appMode === 'presentasjon' && (
                            valgtPres ? (
                            <>
                                <header className="px-4 py-3 border-b border-gray-700 bg-gray-800">
                                    <div className="flex items-center justify-between gap-2">
                                        <button
                                            onClick={() => setMobileView('list')}
                                            className="md:hidden p-2 -ml-2 text-gray-400 hover:text-white"
                                            aria-label="Tilbake til presentasjonslisten"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <h1 className="text-lg md:text-2xl font-bold truncate">{valgtPres.navn}</h1>
                                            <p className="text-sm text-gray-400">{valgtPres.lysbilder} lysbilder</p>
                                        </div>
                                        <button
                                            onClick={sendPresenterLenke}
                                            disabled={!presenterKode || !connected}
                                            className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition flex-shrink-0"
                                            title={`Send fjernkontroll-lenken på e-post til ${valgtPres.avsender || 'avsenderen'}`}
                                        >
                                            Send fjernkontroll
                                        </button>
                                        <button
                                            onClick={tomPresentasjonSkjerm}
                                            disabled={!presVist && !presLiveSlide && !presSperre}
                                            className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition flex-shrink-0"
                                        >
                                            Tøm skjerm
                                        </button>
                                    </div>
                                    {presenterEpost && (
                                        <p className={`text-xs mt-1 ${presenterEpost.ok ? 'text-green-400' : 'text-red-400'}`} role="status">
                                            {presenterEpost.tekst}
                                        </p>
                                    )}
                                </header>

                                {/* Current slide preview — klikk toggler salens visning */}
                                <div className="flex-1 min-h-0 grid grid-rows-[100%] grid-cols-[100%] place-items-center p-2 md:p-4 bg-gray-900 relative">
                                    <KryssfadeBilde
                                        src={`/presentasjoner/${valgtPres.id}/slide/${presSlide}`}
                                        alt={`Lysbilde ${presSlide} av ${valgtPres.lysbilder}`}
                                        onClick={klikkPreviewBilde}
                                        title="Klikk: vis på skjerm / svart skjerm"
                                        className={`max-w-full max-h-full min-h-0 object-contain rounded border-2 cursor-pointer transition-colors ${presVist ? 'border-green-500 ring-2 ring-green-500' : 'border-gull-500 ring-2 ring-gull-500'}`}
                                    />
                                    {!presVist && (
                                        <div className="absolute inset-0 flex items-end justify-center pb-8 pointer-events-none">
                                            {presLiveSlide ? (
                                                <div className="bg-black/80 border-2 border-gull-500 text-gull-300 text-sm font-semibold px-4 py-2 rounded-full max-w-[90%] truncate">
                                                    Salen ser lysbilde {presLiveSlide} — du blar privat. Klikk på bildet for svart skjerm
                                                </div>
                                            ) : (
                                                <div className="bg-black/80 border-2 border-red-500 text-red-300 text-sm font-semibold px-4 py-2 rounded-full max-w-[90%] truncate">
                                                    {presSperre === 'sang' ? 'Salen ser sangen — trykk «Tøm skjerm» for å kunne vise'
                                                        : presSperre === 'bibel' ? 'Salen ser bibelverset — trykk «Tøm skjerm» for å kunne vise'
                                                        : 'Svart skjerm — klikk på bildet for å vise'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Store «◀▶»: styrer skjermen i grønn, blar privat ellers */}
                                <div className="flex items-center gap-3 px-3 py-2 border-t border-gray-700 bg-gray-800">
                                    <button
                                        onClick={() => presHoppTil(1)}
                                        disabled={presSlide <= 1}
                                        className="flex-shrink-0 w-14 py-4 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition flex items-center justify-center text-xl font-bold"
                                        aria-label="Første lysbilde"
                                        title="Første lysbilde"
                                    >
                                        «
                                    </button>
                                    <button
                                        onClick={presForrige}
                                        disabled={presSlide <= 1}
                                        className="flex-1 py-4 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition flex items-center justify-center"
                                        aria-label="Forrige lysbilde"
                                    >
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <div className="text-center flex-shrink-0 w-20" role="status" aria-live="polite">
                                        <div className={`text-lg font-bold ${presVist ? 'text-green-400' : 'text-gull-400'}`}>
                                            {presSlide} / {valgtPres.lysbilder}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {presVist ? 'på skjerm' : presLiveSlide ? `skjerm: ${presLiveSlide}` : 'ikke vist'}
                                        </div>
                                    </div>
                                    <button
                                        onClick={presNeste}
                                        disabled={presSlide >= valgtPres.lysbilder}
                                        className="flex-1 py-4 bg-gull-500 hover:bg-gull-400 text-gull-950 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition flex items-center justify-center"
                                        aria-label="Neste lysbilde"
                                    >
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => presHoppTil(valgtPres.lysbilder)}
                                        disabled={presSlide >= valgtPres.lysbilder}
                                        className="flex-shrink-0 w-14 py-4 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition flex items-center justify-center text-xl font-bold"
                                        aria-label="Siste lysbilde"
                                        title="Siste lysbilde"
                                    >
                                        »
                                    </button>
                                </div>

                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center overflow-y-auto" role="status" aria-label="Ingen presentasjon valgt">
                                <div className="text-center px-6 py-4">
                                    {presenterKode ? (
                                        <>
                                            <canvas id="presenter-qr-stor" className="mx-auto rounded-xl bg-white p-1" width="260" height="260" aria-hidden="true"></canvas>
                                            <p className="mt-4 text-3xl font-mono font-bold tracking-widest text-gull-400">
                                                {presenterKode.slice(0, 3)} {presenterKode.slice(3)}
                                            </p>
                                            <p className="mt-2 text-gray-400 max-w-xs mx-auto">
                                                Fjernkontroll for taleren — skann QR-en, eller åpne{' '}
                                                <span className="text-gray-300">vis.bibeltroen.no/presenter</span> og tast koden
                                            </p>
                                            {presenterAntall > 0 ? (
                                                <p className="mt-4 text-green-400 font-medium flex items-center justify-center gap-2" role="status">
                                                    <span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" aria-hidden="true"></span>
                                                    Koblet til fjernkontroll{presenterAntall > 1 ? ` (${presenterAntall})` : ''}
                                                </p>
                                            ) : (
                                                <p className="mt-4 text-gray-500">Venter på at fjernkontrollen kobler til …</p>
                                            )}
                                            <p className="mt-6 text-gray-500 text-sm">Velg en presentasjon fra listen for å komme i gang</p>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                                            </svg>
                                            <p className="text-lg text-gray-500">Velg en presentasjon fra listen</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </main>

                    {/* Controller QR dialog */}
                    {showControllerQR && roomCode && (
                        <div
                            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                            role="dialog"
                            aria-modal="true"
                            onClick={() => setShowControllerQR(false)}
                            onKeyDown={(e) => e.key === 'Escape' && setShowControllerQR(false)}
                            tabIndex={-1}
                            ref={(el) => el && el.focus()}
                        >
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
                                <h3 className="text-xl font-bold mb-2">Koble til iPad</h3>
                                <p className="text-gray-400 text-sm mb-4">Skann QR-koden med iPad for å styre presentasjonen</p>
                                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                                    <canvas id="controller-qr-canvas" width="200" height="200"></canvas>
                                </div>
                                <p className="text-2xl font-mono text-gull-400 mb-4">{roomCode}</p>
                                <p className="text-gray-500 text-xs mb-4">Trykk Esc eller klikk utenfor for å lukke</p>
                                <button
                                    onClick={() => setShowControllerQR(false)}
                                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition"
                                >
                                    Lukk
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Hus-innstillinger: config-innlogging + aktive bibler */}
                    {showHusConfig && husAlias && (
                        <div
                            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                            role="dialog"
                            aria-modal="true"
                            onClick={() => setShowHusConfig(false)}
                            onKeyDown={(e) => e.key === 'Escape' && setShowHusConfig(false)}
                            tabIndex={-1}
                        >
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                                <h3 className="text-xl font-bold mb-1">Hus-innstillinger</h3>
                                <p className="text-gull-400 font-mono text-sm mb-4">{husAlias}</p>
                                {!husCfgInnlogget ? (
                                    <>
                                        <p className="text-gray-400 text-sm mb-2">Tast husets config-passord for å endre innstillinger.</p>
                                        <input
                                            type="password"
                                            value={husCfgPassord}
                                            onChange={(e) => { setHusCfgPassord(e.target.value); setHusCfgFeil(''); }}
                                            onKeyDown={(e) => e.key === 'Enter' && husCfgLoggInn()}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg mb-3 focus:outline-none focus:border-gull-500"
                                            autoFocus
                                        />
                                        {husCfgFeil && <p className="text-red-400 text-sm mb-3" role="alert">{husCfgFeil}</p>}
                                        <div className="flex gap-2 justify-end">
                                            <button onClick={() => setShowHusConfig(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition">Avbryt</button>
                                            <button onClick={husCfgLoggInn} className="px-4 py-2 bg-gull-500 hover:bg-gull-400 text-gull-950 rounded-lg transition">Logg inn</button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-gray-400 text-sm mb-2">Aktive bibler for huset:</p>
                                        <div className="mb-4 space-y-1.5">
                                            {bibler.map(b => (
                                                <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={husCfgValg.includes(b.id)}
                                                        onChange={(e) => setHusCfgValg(v =>
                                                            e.target.checked ? [...v, b.id] : v.filter(x => x !== b.id))}
                                                    />
                                                    <span>{b.navn}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {husCfgFeil && <p className="text-red-400 text-sm mb-3" role="alert">{husCfgFeil}</p>}
                                        <div className="flex gap-2 justify-end">
                                            <button onClick={() => setShowHusConfig(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition">Avbryt</button>
                                            <button onClick={husCfgLagre} disabled={husCfgLagrer} className="px-4 py-2 bg-gull-500 hover:bg-gull-400 text-gull-950 rounded-lg transition disabled:opacity-50">
                                                {husCfgLagrer ? 'Lagrer …' : 'Lagre'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Join room dialog */}
                    {showJoinRoomDialog && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" onClick={() => setShowJoinRoomDialog(false)}>
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                                <h3 className="text-xl font-bold mb-4">Koble til rom</h3>
                                <p className="text-gray-400 text-sm mb-4">Skriv inn romkoden for å koble til et eksisterende rom.</p>
                                <input
                                    id="join-rom-input"
                                    type="text"
                                    value={joinRoomCode}
                                    onChange={(e) => setJoinRoomCode(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => e.key === 'Enter' && handleJoinRoom()}
                                    placeholder="F.eks. ABC123"
                                    maxLength={6}
                                    className="w-full px-4 py-3 bg-gray-700 rounded-lg text-center text-2xl font-mono tracking-widest mb-4 focus:outline-none focus:ring-2 focus:ring-gull-500"
                                    autoFocus
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setShowJoinRoomDialog(false)}
                                        className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition"
                                    >
                                        Avbryt
                                    </button>
                                    <button
                                        onClick={handleJoinRoom}
                                        disabled={joinRoomCode.length < 6}
                                        className="flex-1 px-4 py-2 bg-gull-500 hover:bg-gull-400 text-gull-950 disabled:bg-gray-600 disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg transition"
                                    >
                                        Koble til
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Help dialog */}
                    {showHelp && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="help-dialog-title" onClick={() => setShowHelp(false)}>
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 id="help-dialog-title" className="text-xl font-bold">Tastaturhjelp</h3>
                                    <button
                                        onClick={() => setShowHelp(false)}
                                        className="text-gray-400 hover:text-white p-1"
                                        aria-label="Lukk hjelp"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="space-y-4 text-sm">
                                    <div>
                                        <h4 className="font-bold text-gull-400 mb-2">Faner</h4>
                                        <div className="grid grid-cols-2 gap-1 text-gray-300">
                                            <span className="text-gray-500">← / →</span><span>Bytt mellom Sanger/Bibel/Presentasjon</span>
                                            <span className="text-gray-500">↓ / Enter</span><span>Gå inn i fanen</span>
                                        </div>
                                    </div>

                                    {appMode === 'presentasjon' ? (
                                        <div>
                                            <h4 className="font-bold text-gull-400 mb-2">Presentasjon - Navigering</h4>
                                            <div className="grid grid-cols-2 gap-1 text-gray-300">
                                                <span className="text-gray-500">↑ / ↓</span><span>Bla lysbilder (grønn: skjermen følger)</span>
                                                <span className="text-gray-500">→ / Enter</span><span>Vis lysbilde på skjerm (grønn)</span>
                                                <span className="text-gray-500">Enter</span><span>I grønn: tilbake til gul — skjermen beholder siste</span>
                                                <span className="text-gray-500">← / Escape</span><span>Ett nivå ned: gul → svart skjerm → listen</span>
                                                <span className="text-gray-500">Space / PgUp / PgDn</span><span>Bla (presentasjonsklikker)</span>
                                                <span className="text-gray-500">Home / End</span><span>Første / siste lysbilde</span>
                                            </div>
                                        </div>
                                    ) : appMode === 'songs' ? (
                                        <>
                                            <div>
                                                <h4 className="font-bold text-gull-400 mb-2">Sanger - Navigering</h4>
                                                <div className="grid grid-cols-2 gap-1 text-gray-300">
                                                    <span className="text-gray-500">↑ / ↓</span><span>Velg sang</span>
                                                    <span className="text-gray-500">← / →</span><span>Forrige/neste vers</span>
                                                    <span className="text-gray-500">Enter</span><span>Vis vers på skjerm</span>
                                                    <span className="text-gray-500">Escape</span><span>Tøm skjerm / tilbake</span>
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gull-400 mb-2">Hurtigtaster</h4>
                                                <div className="grid grid-cols-2 gap-1 text-gray-300">
                                                    <span className="text-gray-500">/</span><span>Fokus søkefelt</span>
                                                    <span className="text-gray-500">g</span><span>Gå til sangnummer</span>
                                                    <span className="text-gray-500">r</span><span>Oppdater sangliste</span>
                                                    <span className="text-gray-500">0-9</span><span>Tast sangnummer direkte</span>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <h4 className="font-bold text-gull-400 mb-2">Bibel - Navigering</h4>
                                                <div className="grid grid-cols-2 gap-1 text-gray-300">
                                                    <span className="text-gray-500">← / →</span><span>Bytt 1930/KJV</span>
                                                    <span className="text-gray-500">↑ / ↓</span><span>Velg bok/kapittel/vers</span>
                                                    <span className="text-gray-500">→ / Enter</span><span>Velg / vis vers</span>
                                                    <span className="text-gray-500">←</span><span>Tilbake</span>
                                                    <span className="text-gray-500">Escape</span><span>Tøm skjerm</span>
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gull-400 mb-2">Hurtigtaster</h4>
                                                <div className="grid grid-cols-2 gap-1 text-gray-300">
                                                    <span className="text-gray-500">/</span><span>Fokus søkefelt</span>
                                                    <span className="text-gray-500">0-9</span><span>Tast kapittel/vers direkte</span>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div>
                                        <h4 className="font-bold text-gull-400 mb-2">Nivåer</h4>
                                        <div className="space-y-1 text-gray-300">
                                            <div><span className="inline-block w-3 h-3 rounded bg-gray-500 mr-2" aria-hidden="true"></span>Grå: Valgt</div>
                                            <div><span className="inline-block w-3 h-3 rounded bg-gull-500 mr-2" aria-hidden="true"></span>Gul: Forhåndsvisning</div>
                                            <div><span className="inline-block w-3 h-3 rounded bg-green-500 mr-2" aria-hidden="true"></span>Grønn: Sendt til skjerm</div>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="font-bold text-gull-400 mb-2">Chrome-utvidelse: Sangbok Skjerm</h4>
                                        <p className="text-gray-300 mb-2">
                                            Åpner displayet automatisk i ekte fullskjerm på den sekundære skjermen — uten ekstra klikk.
                                        </p>
                                        <ol className="list-decimal list-inside text-gray-300 space-y-1">
                                            <li>
                                                <a href="/sangbok-skjerm-dev.zip" download className="text-gull-400 underline hover:text-gull-300">
                                                    Last ned utvidelsen
                                                </a>
                                                {' '}og pakk ut zip-filen
                                            </li>
                                            <li>Åpne <code className="text-gray-400">chrome://extensions</code> og slå på «Utviklermodus»</li>
                                            <li>Trykk «Last inn upakket» og velg den utpakkede mappen</li>
                                        </ol>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Last deretter denne siden på nytt. «Ny»-knappen åpner da displayet rett i fullskjerm på skjerm 2.
                                        </p>
                                    </div>
                                </div>

                                <p className="text-xs text-gray-500 mt-4 text-center">Trykk Escape eller klikk utenfor for å lukke</p>
                            </div>
                        </div>
                    )}

                    {/* Goto dialog */}
                    {showGotoDialog && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="goto-dialog-title">
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm">
                                <h3 id="goto-dialog-title" className="text-xl font-bold mb-4">Gå til sangnummer</h3>
                                <label htmlFor="goto-nummer" className="sr-only">Sangnummer</label>
                                <input
                                    ref={gotoInputRef}
                                    id="goto-nummer"
                                    type="text"
                                    value={gotoNumber}
                                    onChange={(e) => setGotoNumber(e.target.value)}
                                    placeholder="Skriv sangnummer..."
                                    aria-describedby="goto-hint"
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                                />
                                <div className="flex gap-2 mt-4">
                                    <button
                                        onClick={() => { setShowGotoDialog(false); setGotoNumber(''); }}
                                        className="flex-1 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                    >
                                        Avbryt
                                    </button>
                                    <button
                                        onClick={() => goToSongNumber(gotoNumber)}
                                        className="flex-1 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition font-medium"
                                    >
                                        Gå til
                                    </button>
                                </div>
                                <p id="goto-hint" className="text-xs text-gray-500 mt-3 text-center">Trykk Enter for å bekrefte, Escape for å avbryte</p>
                            </div>
                        </div>
                    )}

                    {/* Login modal */}
                    {showLoginDialog && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="login-dialog-title">
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm">
                                <h3 id="login-dialog-title" className="text-xl font-bold mb-4">
                                    {showCodeInput ? 'Skriv inn kode'
                                        : loginMode === 'konto' && kontoSteg === 'glemt' ? 'Glemt passord'
                                        : loginMode === 'konto' && kontoSteg === 'nytt' ? 'Nytt passord'
                                        : pendingLoggInn ? 'Logg inn'
                                        : pendingAdmin ? 'Logg inn som administrator'
                                        : pendingKanal ? 'Logg inn for å styre utganger'
                                        : pendingEditSong ? 'Logg inn for å redigere'
                                        : 'Logg inn for å legge til sang'}
                                </h3>

                                {!showCodeInput ? (
                                    loginMode === 'konto' ? (
                                    kontoSteg === 'glemt' ? (
                                    <>
                                        <p className="text-sm text-gray-300 mb-3">Vi sender en 6-sifret kode til e-posten som er registrert på kontoen.</p>
                                        <label htmlFor="glemt-bruker" className="sr-only">E-post eller brukernavn</label>
                                        <input
                                            id="glemt-bruker"
                                            type="text"
                                            value={kontoBruker}
                                            onChange={(e) => { setKontoBruker(e.target.value); setLoginError(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && kontoBruker.trim()) handleGlemtKonto(); }}
                                            placeholder="E-post eller brukernavn..."
                                            autoFocus
                                            autoComplete="username"
                                            spellCheck={false}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                                        />
                                        {loginError && (
                                            <p id="login-error" className="text-red-400 text-sm mt-2" role="alert">{loginError}</p>
                                        )}
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                onClick={() => { setKontoSteg('logg'); setLoginError(''); }}
                                                className="flex-1 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                                disabled={loginLoading}
                                            >
                                                Tilbake
                                            </button>
                                            <button
                                                onClick={handleGlemtKonto}
                                                className="flex-1 py-2 bg-gull-500 hover:bg-gull-600 text-gull-950 rounded-lg transition font-medium disabled:opacity-50"
                                                disabled={loginLoading || !kontoBruker.trim()}
                                            >
                                                {loginLoading ? 'Sender...' : 'Send kode'}
                                            </button>
                                        </div>
                                    </>
                                    ) : kontoSteg === 'nytt' ? (
                                    <>
                                        {glemtInfo && <p className="text-sm text-gray-300 mb-3" role="status">{glemtInfo}</p>}
                                        <label htmlFor="glemt-kode" className="sr-only">Kode fra e-post</label>
                                        <input
                                            id="glemt-kode"
                                            type="text"
                                            inputMode="numeric"
                                            value={glemtKode}
                                            onChange={(e) => { setGlemtKode(e.target.value); setLoginError(''); }}
                                            placeholder="6-sifret kode fra e-posten..."
                                            autoFocus
                                            autoComplete="one-time-code"
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                                        />
                                        <label htmlFor="glemt-nytt" className="sr-only">Nytt passord</label>
                                        <input
                                            id="glemt-nytt"
                                            type="password"
                                            value={glemtNyttPassord}
                                            onChange={(e) => { setGlemtNyttPassord(e.target.value); setLoginError(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleTilbakestillKonto(); }}
                                            placeholder="Nytt passord (minst 8 tegn)..."
                                            autoComplete="new-password"
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500 mt-2"
                                        />
                                        {loginError && (
                                            <p id="login-error" className="text-red-400 text-sm mt-2" role="alert">{loginError}</p>
                                        )}
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                onClick={() => { setKontoSteg('glemt'); setLoginError(''); }}
                                                className="flex-1 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                                disabled={loginLoading}
                                            >
                                                Tilbake
                                            </button>
                                            <button
                                                onClick={handleTilbakestillKonto}
                                                className="flex-1 py-2 bg-gull-500 hover:bg-gull-600 text-gull-950 rounded-lg transition font-medium disabled:opacity-50"
                                                disabled={loginLoading || glemtKode.trim().length < 6 || glemtNyttPassord.length < 8}
                                            >
                                                {loginLoading ? 'Lagrer...' : 'Sett nytt passord'}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-3 text-center">Koden er gyldig i 15 minutter</p>
                                    </>
                                    ) : (
                                    <>
                                        <label htmlFor="konto-bruker" className="sr-only">E-post eller brukernavn</label>
                                        <input
                                            id="konto-bruker"
                                            type="text"
                                            value={kontoBruker}
                                            onChange={(e) => { setKontoBruker(e.target.value); setLoginError(''); }}
                                            placeholder="E-post eller brukernavn..."
                                            autoFocus
                                            autoComplete="username"
                                            spellCheck={false}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                                        />
                                        <label htmlFor="konto-passord" className="sr-only">Passord</label>
                                        <input
                                            id="konto-passord"
                                            type="password"
                                            value={kontoPassord}
                                            onChange={(e) => { setKontoPassord(e.target.value); setLoginError(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleKontoLogin(); }}
                                            placeholder="Passord..."
                                            autoComplete="current-password"
                                            aria-describedby={loginError ? 'login-error' : undefined}
                                            className="w-full px-3 py-2 mt-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                                        />
                                        {loginError && (
                                            <p id="login-error" className="text-red-400 text-sm mt-2" role="alert">{loginError}</p>
                                        )}
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                onClick={() => { setShowLoginDialog(false); setKontoPassord(''); setLoginError(''); setPendingEditSong(null); setPendingAdmin(false); setPendingKanal(null); pendingKanalRef.current = null; }}
                                                className="flex-1 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                                disabled={loginLoading}
                                            >
                                                Avbryt
                                            </button>
                                            <button
                                                onClick={handleKontoLogin}
                                                className="flex-1 py-2 bg-gull-500 hover:bg-gull-600 text-gull-950 rounded-lg transition font-medium disabled:opacity-50"
                                                disabled={loginLoading || !kontoBruker.trim() || !kontoPassord}
                                            >
                                                {loginLoading ? 'Logger inn...' : 'Logg inn'}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-3 text-center">
                                            Samme konto som på sangleder.bibeltroen.no
                                        </p>
                                        <button
                                            onClick={() => { setKontoSteg('glemt'); setLoginError(''); setGlemtInfo(''); }}
                                            className="w-full mt-2 text-sm text-gray-400 hover:text-white transition"
                                            disabled={loginLoading}
                                        >
                                            Glemt passord?
                                        </button>
                                        {!pendingAdmin && (
                                        <button
                                            onClick={() => { setLoginMode('delt'); setLoginError(''); }}
                                            className="w-full mt-2 text-sm text-gray-400 hover:text-white transition"
                                            disabled={loginLoading}
                                        >
                                            Bruk delt redigeringspassord i stedet
                                        </button>
                                        )}
                                    </>
                                    )) : (
                                    <>
                                        <label htmlFor="login-password" className="sr-only">Delt redigeringspassord</label>
                                        <input
                                            id="login-password"
                                            type="password"
                                            value={loginPassword}
                                            onChange={(e) => { setLoginPassword(e.target.value); setLoginError(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                                            placeholder="Delt redigeringspassord..."
                                            autoFocus
                                            aria-describedby={loginError ? 'login-error' : undefined}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500"
                                        />
                                        {loginError && (
                                            <p id="login-error" className="text-red-400 text-sm mt-2" role="alert">{loginError}</p>
                                        )}
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                onClick={() => { setShowLoginDialog(false); setLoginPassword(''); setLoginError(''); setPendingEditSong(null); setPendingAdmin(false); setPendingKanal(null); pendingKanalRef.current = null; }}
                                                className="flex-1 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                                disabled={loginLoading}
                                            >
                                                Avbryt
                                            </button>
                                            <button
                                                onClick={handleLogin}
                                                className="flex-1 py-2 bg-gull-500 hover:bg-gull-600 text-gull-950 rounded-lg transition font-medium disabled:opacity-50"
                                                disabled={loginLoading}
                                            >
                                                {loginLoading ? 'Logger inn...' : 'Logg inn'}
                                            </button>
                                        </div>
                                        <button
                                            onClick={handleForgotPassword}
                                            className="w-full mt-3 text-sm text-gray-400 hover:text-white transition"
                                            disabled={loginLoading}
                                        >
                                            {loginLoading ? 'Sender...' : 'Glemt passord?'}
                                        </button>
                                        <button
                                            onClick={() => { setLoginMode('konto'); setLoginError(''); }}
                                            className="w-full mt-2 text-sm text-gray-400 hover:text-white transition"
                                            disabled={loginLoading}
                                        >
                                            Logg inn med konto i stedet
                                        </button>
                                    </>
                                    )
                                ) : (
                                    <>
                                        {codeSent && (
                                            <p className="text-green-400 text-sm mb-3">Kode sendt til din e-post!</p>
                                        )}
                                        <input
                                            id="reset-kode-input"
                                            type="text"
                                            value={resetCode}
                                            onChange={(e) => { setResetCode(e.target.value); setLoginError(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyCode(); }}
                                            placeholder="6-sifret kode..."
                                            autoFocus
                                            maxLength={6}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gull-500 text-center text-2xl tracking-widest"
                                        />
                                        {loginError && (
                                            <p className="text-red-400 text-sm mt-2">{loginError}</p>
                                        )}
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                onClick={() => { setShowCodeInput(false); setResetCode(''); setLoginError(''); setCodeSent(false); }}
                                                className="flex-1 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                                disabled={loginLoading}
                                            >
                                                Tilbake
                                            </button>
                                            <button
                                                onClick={handleVerifyCode}
                                                className="flex-1 py-2 bg-gull-500 hover:bg-gull-600 text-gull-950 rounded-lg transition font-medium disabled:opacity-50"
                                                disabled={loginLoading || resetCode.length < 6}
                                            >
                                                {loginLoading ? 'Verifiserer...' : 'Bekreft'}
                                            </button>
                                        </div>
                                        <button
                                            onClick={handleForgotPassword}
                                            className="w-full mt-3 text-sm text-gray-400 hover:text-white transition"
                                            disabled={loginLoading}
                                        >
                                            Send ny kode
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Administrasjon: kanalrom (superadmin) + brukere (admin+) */}
                    {showAdminDialog && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="admin-dialog-title">
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 id="admin-dialog-title" className="text-xl font-bold">Administrasjon</h3>
                                    <div className="flex items-center gap-2">
                                    <button
                                        onClick={loggUtAdmin}
                                        className="px-3 py-1.5 rounded-lg text-sm bg-gray-700 text-gray-300 hover:bg-gray-600 transition"
                                        title="Logg ut av kontoen (gjelder også editoren og sangleder-siden)"
                                    >
                                        Logg ut
                                    </button>
                                    <button
                                        onClick={() => setShowAdminDialog(false)}
                                        className="text-gray-400 hover:text-white p-1"
                                        aria-label="Lukk administrasjon"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                    </div>
                                </div>
                                <div className="flex gap-2 mb-4" role="tablist" aria-label="Administrasjonsfaner">
                                    {harSuperRolle(adminRolle) && (
                                        <button
                                            role="tab"
                                            aria-selected={adminTab === 'kanalrom'}
                                            onClick={() => { setAdminTab('kanalrom'); setAdminStatus(''); }}
                                            className={`px-3 py-1.5 rounded-lg text-sm transition ${adminTab === 'kanalrom' ? 'bg-gull-500 text-gull-950 font-medium' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                                        >
                                            Kanalrom
                                        </button>
                                    )}
                                    <button
                                        role="tab"
                                        aria-selected={adminTab === 'brukere'}
                                        onClick={() => { setAdminTab('brukere'); setAdminStatus(''); }}
                                        className={`px-3 py-1.5 rounded-lg text-sm transition ${adminTab === 'brukere' ? 'bg-gull-500 text-gull-950 font-medium' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                                    >
                                        Brukere
                                    </button>
                                </div>

                                {adminTab === 'kanalrom' ? (
                                    <>
                                        <p className="text-xs text-gray-400 mb-3">
                                            Romkodene er passord for døgnåpne visninger (OBS-kilden, vMix sin browser-input).
                                            Kopier skjerm-URL-en inn i kilden — roter koden hvis den er på avveie.
                                        </p>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-left text-gray-400 text-xs">
                                                        <th scope="col" className="py-1 pr-3 font-medium">Hus</th>
                                                        <th scope="col" className="py-1 pr-3 font-medium">Kanal</th>
                                                        <th scope="col" className="py-1 pr-3 font-medium">Kode</th>
                                                        <th scope="col" className="py-1 pr-3 font-medium">Status</th>
                                                        <th scope="col" className="py-1 font-medium">Handling</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {adminKanalrom.map((r) => (
                                                        <tr key={r.id} className={`border-t border-gray-700 ${r.aktiv ? '' : 'opacity-50'}`}>
                                                            <td className="py-2 pr-3 font-mono">{r.hus}</td>
                                                            <td className="py-2 pr-3">{KANAL_LABEL[r.kanal] || r.kanal}</td>
                                                            <td className="py-2 pr-3">
                                                                <div className="whitespace-nowrap">
                                                                    <code className="text-gull-400">{r.kode}</code>
                                                                    <button
                                                                        onClick={() => kopierKanalUrl(r)}
                                                                        className="ml-2 text-xs px-2 py-0.5 bg-gray-700 hover:bg-gray-600 rounded transition"
                                                                    >
                                                                        {kopiertUrlId === r.id ? 'Kopiert!' : 'Kopier URL'}
                                                                    </button>
                                                                </div>
                                                                {/* URL-en sto før kun i knappens title= — usynlig på nettbrett,
                                                                    som ikke har hover. select-all gjør at ett trykk merker hele
                                                                    strengen, så den kan kopieres uten clipboard-API-et. */}
                                                                <div className="mt-1 max-w-xs font-mono text-[11px] leading-snug text-gray-400 break-all select-all">
                                                                    {r.url}
                                                                </div>
                                                            </td>
                                                            <td className="py-2 pr-3 text-xs">
                                                                {!r.aktiv ? 'Avslått'
                                                                    : r.tilkoblet ? <span className="text-green-400">{r.tilkoblet} kilde(r) tilkoblet</span>
                                                                    : <span className="text-gray-500">Ingen kilde</span>}
                                                            </td>
                                                            <td className="py-2 whitespace-nowrap text-xs">
                                                                <button onClick={() => roterKanalrom(r)} className="px-2 py-0.5 bg-gray-700 hover:bg-gray-600 rounded transition">Roter kode</button>
                                                                <button onClick={() => toggleKanalromAktiv(r)} className="ml-1.5 px-2 py-0.5 bg-gray-700 hover:bg-gray-600 rounded transition">{r.aktiv ? 'Slå av' : 'Slå på'}</button>
                                                                <button onClick={() => slettKanalrom(r)} className="ml-1.5 px-2 py-0.5 bg-red-800 hover:bg-red-700 rounded transition">Slett</button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {adminKanalrom.length === 0 && (
                                                        <tr><td colSpan={5} className="py-3 text-gray-500 text-sm">Ingen kanalrom ennå</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-gray-700">
                                            <h4 className="text-sm font-medium mb-2">Nytt hus — vMix-kanal + egen sangbok opprettes samtidig</h4>
                                            <div className="flex flex-wrap items-end gap-2">
                                                <label className="text-xs text-gray-400">
                                                    Hus (møtekode, 2–10 store bokstaver/tall)
                                                    <input
                                                        value={nyttHus}
                                                        onChange={(e) => setNyttHus(e.target.value.toUpperCase())}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') opprettKanalrom(); }}
                                                        placeholder="OPSTAD"
                                                        spellCheck={false}
                                                        className="block mt-1 w-40 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gull-500"
                                                    />
                                                </label>
                                                <button
                                                    onClick={opprettKanalrom}
                                                    className="px-4 py-2 bg-gull-500 hover:bg-gull-400 text-gull-950 rounded-lg font-medium transition"
                                                >
                                                    Opprett
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-2">
                                                Har huset alt en av kanalene, opprettes bare den som mangler.
                                                Nye hus får egen sangbok med én startsang; OPSTAD eier hovedboka
                                                (den kan aldri slettes).
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-gray-400 text-xs">
                                                    <th scope="col" className="py-1 pr-3 font-medium">Bruker</th>
                                                    <th scope="col" className="py-1 pr-3 font-medium">E-post</th>
                                                    <th scope="col" className="py-1 font-medium">Rolle</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {adminBrukere.map((b) => (
                                                    <tr key={b.brukernavn} className="border-t border-gray-700">
                                                        <td className="py-2 pr-3">{b.brukernavn}{b.verifisert ? '' : ' (ubekreftet)'}</td>
                                                        <td className="py-2 pr-3 text-gray-400">{b.epost || '—'}</td>
                                                        <td className="py-2">
                                                            <select
                                                                value={b.rolle || ''}
                                                                onChange={(e) => settBrukerRolle(b, e.target.value)}
                                                                disabled={(b.rolle || '').includes('superadmin') && !harSuperRolle(adminRolle)}
                                                                aria-label={`Rolle for ${b.brukernavn}`}
                                                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-gull-500 disabled:opacity-50"
                                                            >
                                                                {rolleValgFor(b).map(([verdi, navn]) => (
                                                                    <option key={verdi} value={verdi}>{navn}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {adminBrukere.length === 0 && (
                                                    <tr><td colSpan={3} className="py-3 text-gray-500 text-sm">Ingen brukere</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                        {harSuperRolle(adminRolle) && (
                                            <div className="mt-4 pt-4 border-t border-gray-700">
                                                <h4 className="text-sm font-medium mb-2">Ny bruker</h4>
                                                <div className="flex flex-wrap items-end gap-2">
                                                    <label className="text-xs text-gray-400">
                                                        Brukernavn
                                                        <input
                                                            value={nyBrukernavn}
                                                            onChange={(e) => setNyBrukernavn(e.target.value)}
                                                            placeholder="Navn Navnesen"
                                                            spellCheck={false}
                                                            className="block mt-1 w-40 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gull-500"
                                                        />
                                                    </label>
                                                    <label className="text-xs text-gray-400">
                                                        E-post
                                                        <input
                                                            value={nyEpost}
                                                            onChange={(e) => setNyEpost(e.target.value)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') opprettBruker(); }}
                                                            placeholder="navn@epost.no"
                                                            type="email"
                                                            spellCheck={false}
                                                            className="block mt-1 w-48 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gull-500"
                                                        />
                                                    </label>
                                                    <label className="text-xs text-gray-400">
                                                        Rolle
                                                        <select
                                                            value={nyBrukerRolle}
                                                            onChange={(e) => setNyBrukerRolle(e.target.value)}
                                                            className="block mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-gull-500"
                                                        >
                                                            {rolleValgFor({ rolle: '' }).map(([verdi, navn]) => (
                                                                <option key={verdi} value={verdi}>{navn}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    <button
                                                        onClick={opprettBruker}
                                                        className="px-4 py-2 bg-gull-500 hover:bg-gull-400 text-gull-950 rounded-lg font-medium transition"
                                                    >
                                                        Opprett
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    Det settes ikke passord her: brukeren velger sitt eget via
                                                    «Glemt passord» med brukernavnet sitt — koden går til e-posten.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {adminStatus && (
                                    <p className="text-sm text-gull-300 mt-3" role="status">{adminStatus}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Import sangplan modal */}
                    {showImportDialog && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="import-dialog-title">
                            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[80vh] flex flex-col">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 id="import-dialog-title" className="text-xl font-bold">Hent sangplan</h3>
                                    <button
                                        onClick={() => { setShowImportDialog(false); setImportCode(''); setImportError(''); }}
                                        className="text-gray-400 hover:text-white p-1"
                                        aria-label="Lukk"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Aktiv plan */}
                                {importedPlan && (
                                    <div className="mb-4 p-3 bg-green-900/30 border border-green-700 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="text-xs text-green-400 font-semibold uppercase">Aktiv plan</span>
                                                <p className="text-sm text-white font-medium">{importedPlan.navn}</p>
                                                <p className="text-xs text-gray-400">fra {importedPlan.bruker} • {importedPlan.sanger.filter(s => s.kind !== 'note').length} sanger</p>
                                            </div>
                                            <button
                                                onClick={planFjern}
                                                className="text-xs text-red-400 hover:text-red-300 px-2 py-1"
                                            >
                                                Fjern
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Innboks: planer delt til det faste rommet */}
                                {romPlaner.length > 0 && (
                                    <div className="mb-4">
                                        <span className="block text-xs text-gray-400 mb-1">Delt til dette møtet</span>
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {romPlaner.map((p, i) => (
                                                <div key={`${p.delt_av}-${p.navn}-${i}`} className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
                                                    <div>
                                                        <p className="text-sm text-white font-medium">{p.navn}</p>
                                                        <p className="text-xs text-gray-400">fra {p.delt_av} • {(p.sangplan || []).filter(s => s.kind !== 'note').length} sanger</p>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            planSett(p.navn, p.sangplan || [], p.delt_av);
                                                            setShowImportDialog(false);
                                                        }}
                                                        className="text-xs bg-gull-500 hover:bg-gull-400 text-gull-950 px-3 py-1.5 rounded-lg flex-shrink-0"
                                                    >
                                                        Last inn
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Kode fra Sangleder («Send til Vis») */}
                                <div className="mb-4">
                                    <label htmlFor="import-kode" className="block text-xs text-gray-400 mb-1">
                                        Kode fra Sangleder
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            id="import-kode"
                                            type="text"
                                            value={importCode}
                                            onChange={(e) => { setImportCode(e.target.value); setImportError(''); }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleImportPlan()}
                                            placeholder="F.eks. KYNG"
                                            maxLength={4}
                                            autoComplete="off"
                                            autoCapitalize="characters"
                                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono tracking-widest placeholder-gray-500 focus:outline-none focus:border-gull-500"
                                        />
                                        <button
                                            onClick={handleImportPlan}
                                            disabled={importLoading || !importCode.trim()}
                                            className="px-4 py-2 bg-gull-500 hover:bg-gull-400 text-gull-950 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg text-sm font-medium transition"
                                        >
                                            Hent
                                        </button>
                                    </div>
                                    {importError && (
                                        <p className="text-red-400 text-sm mt-1" role="alert">{importError}</p>
                                    )}
                                </div>

                                <p className="text-sm text-gray-400 text-center py-4">
                                    Koden vises i Sangleder under «Send til Vis» eller «Del».
                                    Planene er private — kun koden gir tilgang.
                                </p>

                                <div className="mt-4 pt-4 border-t border-gray-700">
                                    <p className="text-xs text-gray-500 text-center">
                                        <a href="https://sangleder.bibeltroen.no" target="_blank" className="text-gull-400 hover:underline">
                                            Åpne Sangleder for å opprette nye planer →
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Add song modal */}
                    {addMode && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="add-dialog-title">
                            <div className="bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                                    <h3 id="add-dialog-title" className="text-xl font-bold">Legg til ny sang</h3>
                                    <button onClick={() => setAddMode(false)} className="text-gray-400 hover:text-white" aria-label="Lukk">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="add-nummer" className="block text-sm text-gray-400 mb-1">Nummer (valgfritt, auto-tildeles)</label>
                                            <input
                                                id="add-nummer"
                                                type="text"
                                                value={newSong.nummer}
                                                onChange={(e) => setNewSong({...newSong, nummer: e.target.value})}
                                                placeholder="Auto"
                                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="add-toneart" className="block text-sm text-gray-400 mb-1">Toneart</label>
                                            <input
                                                id="add-toneart"
                                                type="text"
                                                value={newSong.toneart}
                                                onChange={(e) => setNewSong({...newSong, toneart: e.target.value})}
                                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="add-tittel" className="block text-sm text-gray-400 mb-1">Tittel *</label>
                                        <input
                                            id="add-tittel"
                                            type="text"
                                            value={newSong.tittel}
                                            onChange={(e) => setNewSong({...newSong, tittel: e.target.value})}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="add-tekstforfatter" className="block text-sm text-gray-400 mb-1">Tekstforfatter</label>
                                        <input
                                            id="add-tekstforfatter"
                                            type="text"
                                            value={newSong.tekstforfatter}
                                            onChange={(e) => setNewSong({...newSong, tekstforfatter: e.target.value})}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="add-tekst" className="block text-sm text-gray-400 mb-1">Tekst * (bruk blank linje mellom vers)</label>
                                        <textarea
                                            id="add-tekst"
                                            value={newSong.tekst}
                                            onChange={(e) => setNewSong({...newSong, tekst: e.target.value})}
                                            rows={15}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg font-mono text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="p-4 border-t border-gray-700 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {addError && (
                                            <span className="text-sm text-red-400" role="alert">{addError}</span>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setAddMode(false)}
                                            className="px-4 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                            disabled={addSaving}
                                        >
                                            Avbryt
                                        </button>
                                        <button
                                            onClick={saveNewSong}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition font-medium disabled:opacity-50"
                                            disabled={addSaving}
                                        >
                                            {addSaving ? 'Lagrer...' : 'Legg til sang'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Edit modal */}
                    {editMode && editSong && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="edit-dialog-title">
                            <div className="bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                                    <h3 id="edit-dialog-title" className="text-xl font-bold">Rediger sang {editSong.nummer}</h3>
                                    <button onClick={cancelEdit} className="text-gray-400 hover:text-white" aria-label="Lukk redigering">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="edit-nummer" className="block text-sm text-gray-400 mb-1">Nummer</label>
                                            <input
                                                id="edit-nummer"
                                                type="text"
                                                value={editSong.nummer}
                                                onChange={(e) => setEditSong({...editSong, nummer: e.target.value})}
                                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="edit-toneart" className="block text-sm text-gray-400 mb-1">Toneart</label>
                                            <input
                                                id="edit-toneart"
                                                type="text"
                                                value={editSong.toneart}
                                                onChange={(e) => setEditSong({...editSong, toneart: e.target.value})}
                                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="edit-tittel" className="block text-sm text-gray-400 mb-1">Tittel</label>
                                        <input
                                            id="edit-tittel"
                                            type="text"
                                            value={editSong.tittel}
                                            onChange={(e) => setEditSong({...editSong, tittel: e.target.value})}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="edit-tekstforfatter" className="block text-sm text-gray-400 mb-1">Tekstforfatter</label>
                                        <input
                                            id="edit-tekstforfatter"
                                            type="text"
                                            value={editSong.tekstforfatter}
                                            onChange={(e) => setEditSong({...editSong, tekstforfatter: e.target.value})}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="edit-tekst" className="block text-sm text-gray-400 mb-1">Tekst (bruk blank linje mellom vers)</label>
                                        <textarea
                                            id="edit-tekst"
                                            value={editSong.tekst}
                                            onChange={(e) => setEditSong({...editSong, tekst: e.target.value})}
                                            rows={15}
                                            aria-describedby="edit-tekst-hint"
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg font-mono text-sm"
                                        />
                                        <span id="edit-tekst-hint" className="sr-only">Bruk blank linje mellom vers. Skriv "chorus" på egen linje før refrenget.</span>
                                    </div>
                                </div>

                                <div className="p-4 border-t border-gray-700 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-gray-500" aria-hidden="true">Ctrl+Enter = Lagre | Escape = Avbryt</span>
                                        {saveError && (
                                            <span className="text-sm text-red-400" role="alert">{saveError}</span>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={cancelEdit}
                                            className="px-4 py-2 bg-gray-600 hover:bg-gray-550 rounded-lg transition"
                                            disabled={saving}
                                        >
                                            Avbryt
                                        </button>
                                        <button
                                            onClick={saveEdit}
                                            className="px-4 py-2 bg-gull-500 hover:bg-gull-600 text-gull-950 rounded-lg transition font-medium disabled:opacity-50"
                                            disabled={saving}
                                        >
                                            {saving ? 'Lagrer...' : 'Lagre'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Render
        const root = ReactDOM.createRoot(document.getElementById('app'));
        root.render(<App />);
